import type { Dictionary } from './types';

export const ptBR: Dictionary = {
  common: {
    appName: 'Builder',
    save: 'Salvar',
    saving: 'Salvando…',
    saved: 'Salvo',
    cancel: 'Cancelar',
    close: 'Fechar',
    delete: 'Excluir',
    edit: 'Editar',
    back: 'Voltar',
    loading: 'Carregando…',
    retry: 'Tentar de novo',
    search: 'Buscar',
    seeAll: 'Ver tudo',
    error: 'Erro',
    success: 'Sucesso',
    yes: 'Sim',
    no: 'Não',
    and: 'e',
    or: 'ou',
    notFound: 'Não encontrado',
    somethingWentWrong: 'Algo deu errado.',
  },
  undoToast: {
    action: 'Desfazer',
    dismiss: 'Fechar aviso',
    submitting: 'Desfazendo…',
    labelWithCounter: (label, secs) => `${label} · ${secs}s`,
    deletedLabel: {
      rdo: 'RDO excluído',
      budgetItem: 'Item do orçamento excluído',
      fvs: 'Vistoria excluída',
    },
  },
  inbox: {
    tabs: {
      approvals: 'Aprovações',
      notifications: 'Avisos',
    },
    filters: {
      projectsAria: 'Filtrar por obra',
      kindsAria: 'Filtrar por tipo',
      allProjects: 'Todas obras',
      allKinds: 'Todos tipos',
      clearAll: 'Limpar filtros',
      noMatch: 'Nenhum item bate com o filtro',
      noMatchHint: 'Ajuste obra ou tipo, ou clique em "Limpar filtros".',
      noProject: 'Sem obra',
      kindLabel: {
        proposal_pending: 'Sugestões',
        client_error_open: 'Erros',
        self_heal_failed: 'Variação anormal',
        anomaly_pending: 'Pontos de atenção',
        po_pending: 'Pedido pendente',
        rnc_unassigned: 'Não conformidade',
        fvs_overdue: 'Vistoria',
        doc_expiring: 'Documentos',
        anomaly_high: 'Pontos de atenção',
        rdo_missing: 'RDO',
        medicao_draft: 'Medição',
      },
    },
  },
  auth: {
    loginTitle: 'Entrar no Builder',
    loginSubtitle: 'Gerencie suas obras com transparência.',
    registerTitle: 'Criar sua conta',
    registerSubtitle: 'Comece a organizar suas obras em minutos.',
    email: 'E-mail',
    password: 'Senha',
    fullName: 'Nome completo',
    phone: 'Telefone',
    phoneOptional: 'Telefone (opcional)',
    loginCta: 'Entrar',
    registerCta: 'Criar conta',
    needAccount: 'Novo por aqui? Criar conta',
    haveAccount: 'Já tem conta? Entrar',
    forgotPassword: 'Esqueci a senha',
    logout: 'Sair',
    badCredentials: 'E-mail ou senha incorretos.',
    iAmA: 'Eu sou',
    passwordHint: 'Ao menos 8 caracteres, com letras e números.',
    couldNotCreate: 'Não foi possível criar a conta.',
    continueWithGoogle: 'Continuar com o Google',
    orContinueWithEmail: 'ou entre com e-mail e senha',
    googleSignInFailed: 'Não foi possível entrar com o Google.',
  },
  nav: {
    projects: 'Obras',
    myProjects: 'Minhas obras',
    portfolio: 'Carteira',
    // Wave 351-C-fix · `Inbox` is the unified surface aggregating
    // proposal_pending + client_error_open + self_heal_failed (see
    // /v1/inbox + apps/web/src/pages/InboxPage.tsx). Sits at the top
    // of the Pendências section so the daily "what's on me?" question
    // points here first, with the older single-source surfaces
    // (Alertas/Anomalias/Aprovações) kept below for direct deep links.
    inbox: 'Inbox',
    alertas: 'Alertas',
    anomalias: 'Pontos de atenção',
    approvals: 'Aprovações',
    activity: 'Atividade',
    trena: 'Trena',
    trenaDescription: 'Orçamento inteligente por m²',
    hojeDescription: 'O que fazer agora — uma decisão por vez', // W148 · 2026-05-28
    inboxDescription: 'Tudo o que está pendente — escolha o que importa', // W148 · 2026-05-28
    alertasDescription: 'Avisos automáticos sobre risco no cronograma', // W148 · 2026-05-28
    anomaliasDescription: 'Pontos fora do normal detectados pelo sistema', // W148 · 2026-05-28
    userApprovalsDescription: 'Pedidos esperando seu OK', // W148 · 2026-05-28
    executivo: 'Executivo',
    ownerPortal: 'Portal do Cliente',
    userApprovals: 'Aprovar cadastros',
    ajuda: 'Central de ajuda',
    settings: 'Configurações',
    account: 'Conta',
    theme: 'Tema',
    language: 'Idioma',
    themeLight: 'Claro',
    themeDark: 'Escuro',
    themeSystem: 'Sistema',
    sectionOverview: 'Visão geral',
    sectionPending: 'Pendências',
    sectionAccount: 'Conta',
    // Wave 352-B · NAV_REGISTRY-driven SideNav.
    relatorios: 'Relatórios',
    fornecedores: 'Fornecedores',
    procurementCockpit: 'Recomendador',
    procurementFrameworks: 'Contratos',
    procurementDashboard: 'Painel compras',
    adminAuctions: 'Leilões reversos',
    broker: 'Leilões para Fornecedores',
    // Wave UUUU · cross-project quality hub nav entry.
    qualidadeHub: 'Hub de Qualidade',
    // Wave 353 · legacy desktop project list label.
    projectsList: 'Lista de obras',
    adminHome: 'Painel Admin',
    adminCommandControl: 'Command & Control',
    adminBrain: 'Brain',
    adminSaude: 'Saúde',
    adminDeploys: 'Deploys',
    adminAutonomia: 'Autonomia',
    adminConectores: 'Conectores',
    adminMetaTemplates: 'Templates Meta',
    adminLabsGenerative: 'Labs · Engenharia Generativa',
    adminLabsGenerativeHistory: 'Histórico Lab',
    adminUsuarios: 'Usuários',
    // Wave 390-395 · autonomy sub-routes
    adminAutonomyIncidents: 'Incidentes',
    adminAutonomyPMLoop: 'PM Loop',
    adminAutonomyDataSelfHeal: 'Auto-cura de dados',
    adminAutonomyWorkflowSelfHeal: 'Auto-cura de fluxo',
    adminAutonomyProcurementAutopilot: 'Autopilot de compras',
    adminAutonomyResourceOptimizer: 'Otimizador de recursos',
    adminAutonomyRuleAutopilot: 'Autopilot de regras',
    adminAutonomyQualityAutopilot: 'Autopilot de qualidade',
    adminAutonomyClaimsDetection: 'Detecção de claims',
    adminAutonomyOwnerTrustPlus: 'Confiança do dono+',
    adminAutonomyAgentGovernor: 'Governança do agente',
    adminAutonomySelfTuning: 'Auto-ajuste',
    adminAutonomyDailyBrief: 'Resumo diário',
    adminAutonomySLA: 'Monitor SLA',
    adminAutonomyRadarCritic: 'Crítico do Radar',
    adminAutonomyChaos: 'Cron de caos',
    adminAutonomyExceptionsOnly: 'Só exceções',
    // Financial & Compliance
    adminMonthlyClose: 'Fechamento mensal',
    adminFinancialRisk: 'Risco financeiro',
    adminAuditPack: 'Pacote de auditoria',
    // Observability
    adminMemoryGraph: 'Grafo de memória',
    adminBenchmarking: 'Benchmarking',
    adminSOP: 'SOPs',
    // Tooling
    adminPolicy: 'Policy as Code',
    adminSimulation: 'Simulação',
    // W383 R8/R9 · product analytics + decision graph + Housy unknowns
    adminProductAnalytics: 'Métricas de produto',
    adminDecisionGraph: 'Grafo de decisão',
    adminHousyUnknowns: 'Lacunas do Housy',
    // Tier F · radar deep scoring + house report
    adminRadarDeepScoring: 'Radar · pontuação',
    adminRadarHouseReport: 'Radar · relatório',
    // Tier F · platform admin
    adminTenants: 'Organizações',
    adminBulkOps: 'Operações em massa',
    adminAuditLog: 'Histórico de auditoria',
    adminObservability: 'Observabilidade',
    adminBackups: 'Cópias de segurança',
    // Wave SYSTEM-TASKS · 2026-05-20 · scheduler com self-healing
    adminSystemTasks: 'Tarefas do sistema',
    // Wave 393A · override audit
    adminTrenaOverrides: 'Overrides de orçamento',
    // Wave 392 · storage stats
    adminTrenaStorage: 'Armazenamento Trena',
    // Wave 393B · compositions compat audit
    adminTrenaCompositions: 'Composições · Compat',
    // Wave QQQ · follow-ups telemetry
    adminFollowUps: 'Follow-ups · Telemetria',
    // B7 · regional overrides
    adminTrenaRegional: 'Composições · Regional',
    // B9 · assumption chain
    adminTrenaAssumptions: 'Premissas · Tenant',
    // Settings sub-routes
    settingsNotifications: 'Preferências de notificações',
    // Wave pm-uom · 2026-05-17 · Unidades de medida settings.
    settingsUom: 'Unidades de medida',
    // W123.4 · 2026-05-26 · 6 grupos PM + Configurações + Admin segregada.
    hoje: 'Hoje',
    plano: 'Plano',
    compras: 'Compras',
    obra: 'Obra',
    dinheiro: 'Gastos da obra',
    entrega: 'Entrega',
    checklist: 'Conferências',
    config: 'Configurações',
    comprasGeral: 'Compras (todas as obras)', // W149 · 2026-05-28
    comprasGeralDescription: 'Pedidos, cotações e fornecedores de todos os projetos', // W149
    dinheiroGeral: 'Dinheiro (todas as obras)', // W149 · 2026-05-28
    dinheiroGeralDescription: 'Gastos, pagamentos e fluxo financeiro de todos os projetos', // W149
    admin: {
      comando: 'Comando',
      painelAdmin: 'Painel Admin',
      saude: 'Saúde',
      brain: 'Brain',
      autonomia: 'Autonomia',
      deploys: 'Deploys',
      usuarios: 'Usuários',
      backups: 'Cópias de segurança',
      auditoria: 'Histórico de auditoria',
      featureFlags: 'Bandeiras de funcionalidade',
      labsGenerative: 'Labs · Generativa',
      emailCf: 'E-mail (CF)',
      cron: 'Tarefas do sistema',
      sinapi: 'SINAPI · Insumos',
      observabilidade: 'Observabilidade',
    },
    modes: {
      campo: 'Campo',
      inbox: 'Inbox',
      gestao: 'Gestão',
      financeiro: 'Financeiro',
      qualidade: 'Qualidade',
      sistema: 'Sistema',
      tudo: 'Tudo',
    },
    showAll: 'Mostrar tudo',
    showAllOutros: 'Outros',
    qualidadeEmpty:
      'Abra uma obra para acessar vistorias, não conformidades e Entrega.',
  },
  // Wave I · Sidebar hierárquica por intenção.
  waveI: {
    nav: {
      sections: {
        tocar:     { label: 'Tocar a obra',   description: 'Dia a dia da execução: RDOs, pranchas, fotos, vistorias' },
        comprar:   { label: 'Comprar',         description: 'Orçamento, compras, cotações, fornecedores' },
        medir:     { label: 'Medir e orçar',   description: 'Estimativa, cronograma, medições' },
        qualidade: { label: 'Qualidade',        description: 'Não-conformidades, auditoria, qualidade cross-obras' },
        financeiro:{ label: 'Financeiro',       description: 'Verba, estoque, relatórios' },
        admin:     { label: 'Admin',            description: 'Configurações da plataforma (só admin)' },
      },
      items: {
        hoje:              'Hoje',
        rdos:              'RDOs',
        pranchas:          'Pranchas',
        galeria:           'Galeria',
        fvs:               'Vistorias',
        atividade:         'Atividade',
        semana:            'Semana',
        orcamento:         'Orçamento',
        compras:           'Compras',
        cotacoes:          'Cotações',
        fornecedores:      'Fornecedores',
        leiloes:           'Leilões reversos',
        trena:             'Trena',
        cronograma:        'Cronograma',
        medicoes:          'Medições',
        hubQualidade:      'Hub Qualidade',
        rnc:               'Não conformidades',
        rfi:               'RFI',
        verba:             'Verba',
        estoque:           'Estoque',
        relatorios:        'Relatórios',
        painelCompras:     'Painel compras',
        adminHome:         'Painel Admin',
        adminCommandControl: 'Command & Control',
        adminSaude:        'Saúde',
        adminBrain:        'Brain',
        adminAutonomia:    'Autonomia',
        adminUsuarios:     'Usuários',
        adminComando:      'Comando',
        adminConfiguracoes: 'Configurações',
        adminHistoricos:   'Históricos',
        adminSystemTasks:  'Tarefas do sistema',
        adminMetaTemplates: 'Templates Meta',
        adminLabsGenerative: 'Labs · Engenharia Generativa',
        adminLabsGenerativeHistory: 'Histórico Lab',
      },
      noProjectSelected: 'Selecione uma obra',
    },
    mobileBottomNav: {
      ariaLabel: 'Navegação principal',
      hoje:    'Hoje',
      rdo:     'RDO+',
      galeria: 'Galeria',
      inbox:   'Inbox',
      mais:    'Mais',
      fechar:  'Fechar menu',
    },
  },
  campo: {
    title: 'Campo',
    subtitle: 'O que precisa de você na obra hoje.',
    greeting: (firstName) => `Bom dia, ${firstName}.`,
    workingOn: 'Você está em',
    projectPickerAria: 'Selecionar obra',
    noProjectsTitle: 'Nenhuma obra atribuída.',
    noProjectsDescription:
      'Quando o gestor te incluir em uma obra, ela aparece aqui.',
    rdoCard: 'Diário de hoje',
    rdoCardAria: (projectName) => `Abrir diário de hoje · ${projectName}`,
    rdoMissing: 'Ainda não enviado',
    rdoDraft: 'Rascunho salvo · não assinado',
    rdoSigned: (hourMinute) => `Enviado às ${hourMinute}`,
    fvsCard: 'Vistorias pendentes hoje',
    fvsCardAria: (projectName, n) =>
      `${n} vistorias pendentes em ${projectName} · abrir`,
    fvsPendingSubtitle: (n) =>
      n === 1 ? '1 item aberto' : `${n} itens abertos`,
    photoFab: 'Foto',
    photoFabAria: (projectName) => `Tirar foto da obra · ${projectName}`,
    photoFabDisabled: 'Selecione uma obra para tirar foto',
    photoSaved: (projectName) => `Foto salva em ${projectName}.`,
    hojePill: 'Hoje',
    hojePillAria: (n) =>
      n === 0 ? 'Sua fila de hoje está vazia.' : `Hoje · ${n} pendentes`,
    allQuietTitle: 'Tudo em dia. Bom trabalho.',
    allQuietDescription:
      'Diário enviado, vistorias sem pendências. Volte quando algo aparecer.',
    // Inline 3-tap phase
    tabRdo: 'Diário',
    tabFotos: 'Fotos',
    tabVoz: 'Voz',
    tabFvs: 'Vistoria',
    autoSaved: 'Rascunho salvo automaticamente',
    sendTodayBtn: (n) => n === 0 ? 'Enviar hoje' : `Enviar hoje (${n} item(s))`,
    publishedOk: 'Diário publicado com sucesso!',
    partialSend: (sent, total) => `${sent} de ${total} enviados. O restante permanece no rascunho.`,
    sendFailed: 'Erro ao enviar. Tente novamente.',
    openFullEditor: 'Editor completo →',
    fvsSentOk: (n) => n === 1 ? '1 resposta de vistoria enviada.' : `${n} respostas de vistoria enviadas.`,
    fvsSentPartial: (sent, total) => `${sent} de ${total} respostas de vistoria enviadas. O restante permanece no rascunho.`,
  },
  semana: {
    title: 'Compromissos da semana',
    subtitle: (range) => `Tudo que cai entre ${range}.`,
    weekRangeSeparator: '—',
    thisWeek: 'Esta semana',
    prevWeek: 'Semana anterior',
    nextWeek: 'Próxima semana',
    dayPicker: 'Escolher dia',
    dayShort: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'],
    emptyTitle: 'Semana tranquila',
    emptyBody:
      'Nada agendado pra essa semana: sem etapa começando, sem medição, sem vistoria marcada, sem pedido esperado. Navegue ← → pra outra semana.',
    dayEmpty: 'Nada nesse dia.',
    noProject: 'Selecione uma obra para ver os compromissos da semana.',
    kindLabel: {
      stage: 'Etapa',
      measurement: 'Medição',
      fvs: 'Vistoria',
      po: 'Entrega de pedido',
    },
    itemLabel: {
      stage: (name) => `Início · ${name}`,
      measurement: (day) => `Medição começa dia ${day}`,
      fvs: (templateName) => templateName,
      po: (supplierName) => `Recebimento · ${supplierName}`,
    },
    itemFallback: {
      fvs: 'Vistoria',
      po: 'fornecedor',
    },
  },
  hoje: {
    title: 'Hoje',
    subtitle: 'O que está pendente para você agora.',
    loadError: 'Não consegui carregar a fila de hoje.',
    emptyTitle: 'Sua fila de hoje está limpa.',
    emptyDescription:
      'Sem decisões, vistorias, diários ou pontos de atenção pendentes. Volte quando algo aparecer.',
    section: {
      aprovacoes: 'Aprovações esperando você',
      diariosAtrasados: 'Diários atrasados',
      fvsPendentes: 'Vistorias pendentes',
      rncs: 'Correções (não conformidades) atribuídas a você',
      // Wave G: 'Anomalias para resolver' → human PT-BR
      anomalias: 'Pontos de atenção para resolver',
      outros: 'Outros',
    },
  },
  projects: {
    title: 'Obras',
    newProject: 'Nova obra',
    empty: 'Nenhuma obra ainda.',
    emptyHint: 'Crie sua primeira obra para começar.',
    filterAll: 'Todas',
    filterActive: 'Em andamento',
    filterOnHold: 'Paradas',
    filterCompleted: 'Concluídas',
    stats: {
      inProgress: 'Em andamento',
      onHold: 'Paradas',
      completed: 'Concluídas',
      overdue: 'Atrasadas',
    },
    status: {
      planning: 'Planejamento',
      in_progress: 'Em andamento',
      on_hold: 'Parada',
      completed: 'Concluída',
      archived: 'Arquivada',
    },
    client: 'Cliente',
    clientName: 'Nome do cliente',
    clientEmail: 'E-mail do cliente',
    clientPhone: 'Telefone do cliente',
    address: 'Endereço',
    progress: 'Progresso',
    dueDate: 'Prazo',
    startDate: 'Início',
    completedAt: 'Concluída em',
    budget: 'Orçamento',
    notes: 'Notas',
    createProject: 'Criar obra',
    backToProjects: 'Todas as obras',
    overallProgress: 'progresso geral',
    notStarted: 'Não iniciada',
    couldNotLoad: 'Não foi possível carregar a obra.',
    ownerReportBtn: 'Relatório do mês',
    ownerReportPickMonth: 'Escolha o mês do relatório',
    ownerReportDownloading: 'Gerando relatório do mês…',
    ownerReportError: 'Não foi possível gerar o relatório. Tente novamente.',
    ownerReportEmptyHint: 'Este mês não tem atividade ainda. Os primeiros dados aparecem aqui assim que você criar RDOs e pedidos de compra.',
    ownerReportRetry: 'Tentar novamente',
  },
  stages: {
    title: 'Etapas',
    addStage: 'Adicionar etapa',
    addFirst: 'Adicionar primeira etapa',
    empty: 'Nenhuma etapa cadastrada.',
    emptyHint:
      'Divida a obra em fases: demolição, estrutura, elétrica, acabamento, e assim por diante.',
    name: 'Nome da etapa',
    description: 'Descrição',
    status: 'Status',
    percent: '% concluído',
    reorderHint: 'Arraste para reordenar',
    plannedStart: 'Início previsto',
    plannedEnd: 'Fim previsto',
    target: 'Meta',
    start: 'Iniciar',
    done: 'Concluir',
    reopen: 'Reabrir',
    couldNotLoad: 'Não foi possível carregar as etapas.',
    couldNotAdd: 'Não foi possível adicionar a etapa.',
    statuses: {
      not_started: 'Não iniciada',
      in_progress: 'Em andamento',
      blocked: 'Bloqueada',
      completed: 'Concluída',
    },
  },
  dailyLogs: {
    title: 'Diário de obra',
    addLog: 'Novo registro',
    logToday: 'Registrar hoje',
    saveLog: 'Salvar registro',
    draftRestored: (when) => `Rascunho restaurado (${when})`,
    discardDraft: 'Descartar rascunho',
    createFirst: 'Criar primeiro registro',
    empty: 'Nenhum registro ainda.',
    emptyHint: 'Capture clima, equipe e o que foi feito em cada dia de obra.',
    date: 'Data',
    workPerformed: 'O que foi feito',
    issues: 'Problemas',
    issuesOptional: 'Problemas (opcional)',
    issuesLabel: 'Problemas',
    note: 'Observações',
    notePlaceholder: 'O que aconteceu na obra hoje?',
    weather: 'Clima',
    temperatureC: 'Temperatura (°C)',
    tempShort: 'Temp. °C',
    crewCount: 'Equipe (pessoas)',
    crewShort: 'Equipe',
    hoursWorked: 'Horas trabalhadas',
    hoursShort: 'Horas',
    postedBy: (name) => `por ${name}`,
    couldNotSave: 'Não foi possível salvar o registro.',
    weatherOptions: {
      clear: 'Sol',
      cloudy: 'Nublado',
      rain: 'Chuva',
      storm: 'Tempestade',
      snow: 'Neve',
      fog: 'Neblina',
      wind: 'Vento',
      hot: 'Calor',
      cold: 'Frio',
    },
    rdo: {
      statusDraft: 'Rascunho',
      statusSigned: 'Assinado',
      statusCorrected: 'Corrigido',
      signedBy: (name, when) => `Assinado por ${name} em ${when}`,
      correctsOriginal: 'Corrige RDO anterior',
      sign: 'Assinar RDO',
      signing: 'Assinando…',
      confirmSignTitle: 'Assinar este RDO?',
      confirmSignBody:
        'Ao assinar, o registro fica imutável. Correções precisam ser feitas em um novo RDO vinculado a este.',
      confirmSignCta: 'Assinar agora',
      correct: 'Corrigir',
      correctTitle: 'Corrigir RDO',
      correctSubtitle:
        'O registro original permanecerá arquivado. Explique o motivo da correção.',
      correctionNote: 'Motivo da correção',
      correctionNotePlaceholder:
        'Ex.: equipe informada estava incorreta, a contagem certa é…',
      saveCorrection: 'Salvar correção',
      couldNotSign: 'Não foi possível assinar o RDO.',
      couldNotCorrect: 'Não foi possível registrar a correção.',
      kpi: {
        title: 'Resumo dos RDOs',
        totalLogs: 'Total de registros',
        signedLogs: 'RDOs assinados',
        signedThisMonth: 'Assinados este mês',
        lastLog: 'Último registro',
        lastSigned: 'Última assinatura',
        never: 'Nenhum',
      },
      audioNotes: {
        title: 'Notas de áudio',
        record: 'Gravar nota de áudio',
        recording: 'Gravando…',
        stopRecording: 'Parar gravação',
        uploading: 'Enviando…',
        uploadError: 'Falha ao enviar áudio. Tente novamente.',
        noAudios: 'Nenhuma nota de áudio neste RDO.',
        play: 'Reproduzir',
        duration: (seconds: number) => `${seconds}s`,
        holdToRecord: 'Pressione e segure para gravar',
        unsupportedBrowser: 'Gravação de áudio não suportada neste navegador.',
        delete: 'Apagar nota',
        deleteConfirm: 'Apagar esta nota de áudio? Essa ação não pode ser desfeita.',
        deleteError: 'Não foi possível apagar a nota. Tente novamente.',
      },
    },
  },
  timeline: {
    title: 'Linha do tempo',
    empty: 'Nenhuma atualização ainda.',
    emptyHint:
      'Compartilhe marcos, entregas, inspeções e problemas conforme a obra avança.',
    addUpdate: 'Nova atualização',
    newUpdate: 'Nova atualização',
    postUpdate: 'Publicar',
    updatePlaceholder: 'Compartilhe uma atualização com o cliente…',
    attachPhotos: 'Anexar fotos',
    type: 'Tipo',
    entryTitle: 'Título',
    entryNotes: 'Notas',
    couldNotLoad: 'Não foi possível carregar a linha do tempo.',
    couldNotPost: 'Não foi possível publicar a atualização.',
    postedBy: (name) => `por ${name}`,
    types: {
      note: 'Nota',
      stage_started: 'Etapa iniciada',
      stage_completed: 'Etapa concluída',
      stage_blocked: 'Etapa bloqueada',
      milestone: 'Marco',
      issue: 'Problema',
      delivery: 'Entrega',
      inspection: 'Vistoria',
      photo: 'Fotos',
      daily_log: 'Diário',
    },
  },
  photos: {
    title: 'Fotos',
    upload: 'Enviar foto',
    uploading: 'Enviando…',
    addPhotos: 'Adicionar fotos',
    empty: 'Nenhuma foto ainda.',
    captionPlaceholder: 'Descrição (opcional)',
    dropHere: 'Arraste as fotos aqui',
    photoCount: (n) => (n === 1 ? '1 foto' : `${n} fotos`),
    unsupportedType: (type) => `Tipo de arquivo não suportado: ${type || 'desconhecido'}`,
    tooLarge: (name) => `${name} tem mais de 25 MiB`,
    uploadFailed: 'Falha no envio.',
  },
  members: {
    title: 'Equipe',
    invite: 'Convidar',
    email: 'E-mail',
    role: 'Função',
    empty: 'Ninguém na equipe ainda.',
    remove: 'Remover',
    confirmRemove: (name) => `Remover ${name} desta obra?`,
    pixKey: 'Chave PIX',
    pixCopy: 'Copiar',
    pixCopied: 'Copiado',
    noPix: 'Sem chave PIX',
    addTitle: 'Adicionar pessoa que já tem conta', // W148 · 2026-05-28
    addSubtitle: 'E-mail da pessoa que já está cadastrada no Builder — engenheiro, mestre, técnico.', // W148 · 2026-05-28
    addCta: 'Adicionar',
    you: 'Você',
    memberCount: (n) => (n === 1 ? '1 membro' : `${n} membros`),
    joined: (date) => `Entrou em ${date}`,
    couldNotLoad: 'Não foi possível carregar a equipe.',
    couldNotAdd: 'Não foi possível adicionar o membro.',
    couldNotUpdate: 'Não foi possível atualizar a função.',
    couldNotRemove: 'Não foi possível remover o membro.',
    validEmail: 'Informe um e-mail válido.',
    inviteOwnerTitle: 'Convidar pessoa nova', // W148 · 2026-05-28
    inviteOwnerSubtitle:
      'Vai receber link por e-mail pra criar conta. Se não tiver e-mail, gere o link e envie pelo WhatsApp.', // W148 · 2026-05-28
    ownerEmail: 'E-mail do convidado (opcional)',
    ownerWhatsapp: 'WhatsApp do convidado (opcional)',
    ownerWhatsappPlaceholder: 'Ex.: (11) 99999-9999',
    inviteNoEmailHint: 'Sem e-mail? Clique em "Gerar link de convite" e envie o link pelo WhatsApp.',
    generateLink: 'Gerar link de convite',
    shareLink: 'Envie este link para o convidado:',
    copyLink: 'Copiar link',
    copied: 'Copiado!',
    shareViaWhatsApp: 'Enviar pelo WhatsApp',
    inviteEmpty: 'Nenhum convite ainda',
    inviteEmptyHint: 'Envie um convite acima para dar acesso ao cliente.',
    invitationsTitle: 'Convites enviados',
    accepted: (date) => `Aceito em ${date}`,
    expiredOn: (date) => `Expirou em ${date}`,
    expires: (date) => `Expira em ${date}`,
    revoke: 'Revogar',
    couldNotRevoke: 'Não foi possível revogar.',
    couldNotCreateInvitation: 'Não foi possível criar o convite.',
    roles: {
      admin: 'Admin',
      project_manager: 'Gerente',
      foreman: 'Mestre de obras',
      worker: 'Ajudante',
      client: 'Proprietário',
      broker: 'Fornecedor', // W152 · unificado de "Corretor" → "Fornecedor"
      buyer: 'Comprador', // W149
      controller: 'Financeiro', // W149
    },
    trade: 'Função',
    tradePlaceholder: 'Ex.: Engenheiro, Pedreiro, Eletricista',
    tradeOptional: 'Função (opcional)',
    additionalRolesLabel: 'Funções adicionais',
    invitationRole: 'Papel no convite',
  },
  owner: {
    welcome: (name) => `Olá, ${name}`,
    yourProjects: 'Suas obras',
    portalTitle: 'Portal do cliente',
    subtitle: 'Acompanhe o andamento das suas obras em tempo real.',
    empty: 'Nenhuma obra compartilhada.',
    emptyHint:
      'Quando o responsável vincular uma obra ao seu e-mail, ela vai aparecer aqui.',
    couldNotLoad: 'Não foi possível carregar suas obras.',
    backToPortal: 'Todas as obras',
    stages: 'Etapas',
    timeline: 'Atualizações',
    photos: 'Fotos',
    noStages: 'Nenhuma etapa cadastrada ainda.',
    noUpdates: 'Nenhuma atualização publicada ainda.',
    payTo: (name) => `Pagar ${name}`,
    paymentMethod: 'Forma de pagamento',
    pixLabel: 'PIX',
    pixInstruction: 'Copie a chave PIX abaixo para pagar o responsável pela obra.',
    transparency: 'Transparência',
    pendingIssues: 'Problemas em Aberto',
    anomalies: 'anomalias',
    noncompliances: 'conformidades',
    verifications: 'vistorias',
    autonomyActions: 'Ações Autônomas',
    decisionsMade: 'Decisões Tomadas',
    unreadMessages: 'Mensagens Não Lidas',
    last30Days: 'últimos 30 dias',
    pendingYourAttention: 'pendentes sua atenção',
    recentActivity: 'Atividade Recente',
    lastUpdate: 'Última atualização',
    lastPayment: 'Último pagamento',
    photosCount: 'Fotos compartilhadas',
    noUpdatesYet: 'Nenhuma atualização ainda',
    maintenanceBanner:
      '🔧 Builder está em manutenção. Você vê dados em modo leitura. Portal do Cliente continua atualizado.',
  },
  maintenance: {
    title: '🔧 Builder está em refactor',
    body:
      'Tô reorganizando o app pra ele virar uma jornada de obra de verdade, em vez de um monte de telas separadas. Voltamos em algumas semanas.',
    bulletsHeader: 'Enquanto isso:',
    bulletVitrine: 'O Portal do Cliente continua no ar:',
    bulletApproval:
      'Se você é dono de obra com link de aprovação ou Owner Portal, esses continuam funcionando.',
    bulletContact: 'Se for crítico, me chama:',
    signature: '— JP',
    vitrineLinkLabel: 'genese.nullscience.ai',
  },
  invitation: {
    loadingTitle: 'Carregando convite…',
    loadingBody: 'Só um instante enquanto validamos seu link.',
    invalidTitle: 'Convite indisponível',
    invalidBody: 'Este link parece incorreto. Peça um novo ao responsável pela obra.',
    expiredTitle: 'Convite expirado',
    expiredBody: 'Este convite já passou do prazo. Peça um novo ao responsável.',
    expiredTitleFor: (projectName) => `Peça um novo link para ${projectName}`,
    acceptedTitle: 'Convite já aceito',
    acceptedBody: (email, projectName) =>
      `O convite para ${email} já foi aceito. Faça login para acompanhar ${projectName}.`,
    acceptTitle: 'Você foi convidado',
    acceptSubtitle: (projectName) =>
      `Acompanhe ${projectName} como cliente: fotos, progresso e atualizações em um só lugar.`,
    acceptCta: 'Criar conta e aceitar',
    acceptExistingCta: 'Aceitar e entrar',
    accepting: 'Aceitando…',
    choosePassword: 'Escolha uma senha',
    existingAccountNote:
      'Encontramos uma conta com esse e-mail. Ao aceitar, vinculamos este projeto a ela — sem senha nova.',
    wrongAccount: 'Conta errada?',
    signInDifferentEmail: 'Entrar com outro e-mail',
    goHome: 'Ir para a página inicial',
    missingToken: 'Este link de convite está sem o token.',
  },
  // Wave pm-uom · 2026-05-17 · Unidades de medida customizadas.
  uom: {
    pageTitle: 'Unidades de medida',
    pageSubtitle: 'Gerencie as unidades disponíveis no orçamento e nas compras.',
    systemSection: 'Unidades do sistema',
    customSection: 'Suas unidades',
    addCta: 'Adicionar unidade',
    codePlaceholder: 'furo, cub-sp, pç…',
    codeHint: 'Código curto. Minúsculas, sem espaços.',
    labelPlaceholder: 'Furo, CUB-SP, Pç…',
    descriptionPlaceholder: 'Descrição opcional',
    categoryLabel: 'Categoria',
    categories: {
      comprimento: 'Comprimento',
      area: 'Área',
      volume: 'Volume',
      tempo: 'Tempo',
      massa: 'Massa',
      unidade: 'Unidade',
      outro: 'Outro',
    },
    badgeSystem: 'padrão',
    badgeCustom: 'personalizada',
    badgeInactive: 'inativa',
    deleteConfirm: 'Desativar esta unidade? Os itens já lançados não são afetados.',
    saved: 'Unidade salva.',
    deleted: 'Unidade desativada.',
    errorSave: 'Não foi possível salvar.',
    errorDelete: 'Não foi possível desativar.',
    empty: 'Nenhuma unidade personalizada ainda.',
    emptyHint: 'Adicione unidades como "Furo", "CUB-SP" ou "Verba".',
    codeRequired: 'Código obrigatório.',
    labelRequired: 'Nome obrigatório.',
    codeInvalid: 'Código deve ser minúsculas, sem espaços (use - ou .).',
  },
  settings: {
    title: 'Configurações',
    subtitle: 'Ajustes da sua conta.',
    profile: 'Perfil',
    profileSubtitle: 'Como você aparece nos registros, fotos e atualizações.',
    changePassword: 'Alterar senha',
    changePasswordSubtitle: 'Use uma senha forte que você não use em outros lugares.',
    currentPassword: 'Senha atual',
    newPassword: 'Nova senha',
    confirmNewPassword: 'Confirmar nova senha',
    passwordsDoNotMatch: 'As senhas não coincidem.',
    avatarUrl: 'URL da foto',
    avatarHint: 'Cole o link de uma imagem hospedada externamente.',
    pixKey: 'Chave PIX',
    pixKeyHint: 'CPF, CNPJ, e-mail, telefone ou chave aleatória.',
    profileSaved: 'Perfil atualizado.',
    passwordChanged: 'Senha atualizada.',
    couldNotSave: 'Não foi possível salvar.',
    couldNotChangePassword: 'Não foi possível alterar a senha.',
    // Wave iiiii — consolidated settings page sections
    role: 'Função',
    tenantName: 'Empresa',
    sectionPerfil: 'Perfil',
    sectionPreferencias: 'Preferências',
    sectionNotificacoes: 'Notificações',
    sectionSeguranca: 'Segurança',
    sectionSobre: 'Sobre',
    langLabel: 'Idioma',
    themeLabel: 'Tema',
    notifInApp: 'Notificações no app',
    notifEmailSummary: 'Resumo por email',
    notifEnabled: 'ativadas',
    twoFa: 'Autenticação em dois fatores',
    sessionsTitle: 'Sessões ativas',
    signOutOtherSessions: 'Sair de outras sessões',
    signOutOtherSessionsSuccess: 'Outras sessões encerradas.',
    appVersion: 'Versão',
    env: 'Ambiente',
    deployedAt: 'Deploy',
    supportEmail: 'suporte@nullscience.ai',
    supportLink: 'https://nullscience.ai/suporte',
  },
  time: {
    justNow: 'agora',
    minutesAgo: (n) => (n === 1 ? 'há 1 min' : `há ${n} min`),
    hoursAgo: (n) => (n === 1 ? 'há 1 h' : `há ${n} h`),
    daysAgo: (n) => (n === 1 ? 'há 1 dia' : `há ${n} dias`),
    weeksAgo: (n) => (n === 1 ? 'há 1 sem' : `há ${n} sem`),
  },
  wave3: {
    tabs: {
      finance: 'Medições',
      docs: 'Documentos',
      audit: 'Auditoria',
    },
    curvaS: {
      title: 'Curva S · Previsto × Realizado',
      planned: 'Previsto',
      actual: 'Realizado',
      empty: 'Sem histórico ainda — aguardando o primeiro snapshot semanal.',
      recompute: 'Recalcular agora',
      recomputing: 'Recalculando…',
      delta: (n) => `Δ ${n} pp`,
    },
    measurements: {
      title: 'Medições',
      subtitle: 'Faturamento periódico por etapa. O servidor impede cobrança acima de 100% da etapa.',
      empty: 'Nenhuma medição registrada.',
      newCta: 'Nova medição',
      status: {
        draft: 'Rascunho',
        approved: 'Aprovada',
        sent: 'Enviada',
        paid: 'Paga',
        rejected: 'Rejeitada',
      },
      period: 'Período',
      total: 'Total',
      approvedBy: (name, date) => `Aprovada por ${name} · ${date}`,
      paidAt: (date) => `Paga em ${date}`,
      approve: 'Aprovar',
      markPaid: 'Marcar como paga',
      viewItems: 'Ver itens',
      overBilling: 'Cobrança acima de 100% da etapa.',
      couldNotCreate: 'Não foi possível criar a medição.',
      createTitle: 'Nova medição',
      periodStart: 'Início do período',
      periodEnd: 'Fim do período',
      stage: 'Etapa',
      percentBilled: '% medido',
      unitPrice: 'Preço unitário (R$)',
      subtotal: 'Subtotal',
      addRow: 'Adicionar item',
      removeRow: 'Remover',
      notes: 'Observações',
      submit: 'Registrar medição',
    },
    purchases: {
      title: 'Solicitações de compra',
      subtitle: 'Pedidos do canteiro com aprovação do PM.',
      empty: 'Nenhuma solicitação.',
      newCta: 'Nova solicitação',
      status: {
        draft: 'Rascunho',
        pending: 'Aguardando aprovação',
        approved: 'Aprovada',
        rejected: 'Rejeitada',
        fulfilled: 'Atendida',
        cancelled: 'Cancelada',
      },
      requestedBy: (name, date) => `Solicitado por ${name} · ${date}`,
      approve: 'Aprovar',
      reject: 'Rejeitar',
      fulfill: 'Marcar como atendida',
      createTitle: 'Nova solicitação',
      itemDescription: 'Descrição',
      itemQuantity: 'Qtd.',
      itemUnit: 'Unidade',
      itemEstimate: 'Estimativa (R$)',
      rejectPlaceholder: 'Motivo da rejeição (opcional)',
    },
    attachments: {
      title: 'Documentos',
      subtitle: 'Contrato, ART, alvará, licenças, notas fiscais e boletins.',
      empty: 'Nenhum documento anexado.',
      upload: 'Enviar documento',
      uploading: 'Enviando…',
      kindLabel: 'Tipo',
      expiresAtLabel: 'Validade',
      kind: {
        contrato: 'Contrato',
        art: 'ART',
        alvara: 'Alvará',
        licenca: 'Licença',
        cnpj: 'CNPJ',
        nf: 'Nota fiscal',
        boletim: 'Boletim',
        other: 'Outro',
      },
      expiresAt: (date) => `Vence em ${date}`,
      expired: 'Vencido',
      expiringSoon: (days) => (days === 1 ? 'Vence amanhã' : `Vence em ${days} dias`),
      couldNotDelete: 'Não foi possível remover.',
      couldNotUpload: 'Falha no envio do arquivo.',
    },
    dependencies: {
      title: 'Dependências entre etapas',
      subtitle: 'Finish-to-start: a etapa sucessora só pode começar quando a predecessora estiver concluída.',
      empty: 'Nenhuma dependência cadastrada.',
      predecessor: 'Predecessora',
      successor: 'Sucessora',
      add: 'Adicionar dependência',
      blockedBy: (names) => `Aguardando: ${names}`,
      couldNotAdd: 'Não foi possível adicionar (ciclo ou etapa inválida).',
    },
    audit: {
      title: 'Trilha de auditoria',
      subtitle: 'Eventos imutáveis encadeados por hash — qualquer alteração quebra a cadeia.',
      empty: 'Sem eventos.',
      verifyCta: 'Verificar cadeia',
      chainOk: 'Cadeia íntegra ✓',
      chainBroken: (at) => `Cadeia quebrada em ${at}`,
      actorSystem: 'sistema',
      // Wave G: 'Ver payload' → human PT-BR 'Ver dados técnicos'
      showPayload: 'Ver dados técnicos',
      hidePayload: 'Ocultar',
    },
  },
  wave4: {
    tabs: {
      purchases: 'Compras',
      changeOrders: 'Aditivos',
    },
    suppliers: {
      title: 'Fornecedores',
      subtitle: 'Cadastro de fornecedores do seu canteiro.',
      empty: 'Nenhum fornecedor cadastrado.',
      new: 'Novo fornecedor',
      legalName: 'Razão social',
      displayName: 'Nome fantasia',
      displayNameHint: 'Opcional — usado no app.',
      cnpj: 'CNPJ',
      cnpjHint: '14 dígitos, sem pontuação.',
      email: 'E-mail',
      phone: 'Telefone',
      pixKey: 'Chave PIX',
      notes: 'Observações',
      archive: 'Arquivar',
      unarchive: 'Reativar',
      archivedBadge: 'Arquivado',
      showArchived: 'Mostrar arquivados',
      hideArchived: 'Ocultar arquivados',
      couldNotSave: 'Não foi possível salvar o fornecedor.',
      // Wave 1.5 · 2026-05-10 — filtros simples, dummy-friendly
      searchPlaceholder: 'Buscar por nome, telefone, e-mail, observação…',
      filterAll: 'Todos',
      filterSanctioned: 'Só sancionados',
      filterByCategory: 'Filtre por categoria',
      categories: 'Categorias',
      categoriesHint: 'Marque tudo que esse fornecedor atende.',
      categoryLabels: {
        materiais: 'Materiais (loja completa)',
        cimento_concreto: 'Cimento / Concreto',
        areia_brita: 'Areia / Brita / Pedra',
        ferro_aco: 'Ferro / Aço / Vergalhão',
        madeira: 'Madeira / Forros',
        hidraulica: 'Hidráulica / PVC',
        eletrica: 'Elétrica / Cabos',
        cobertura: 'Cobertura / Telhas',
        argamassa_imperm: 'Argamassa / Imperm.',
        tintas: 'Tintas / Pintura',
        vidro: 'Vidro / Espelho',
        esquadrias: 'Esquadrias / Portas',
        aluminio: 'Alumínio',
        ceramica: 'Cerâmica / Porcelanato',
        metais_sanitarios: 'Sanitários / Metais',
        isolamento: 'Isolamento Térmico',
        equipamentos: 'Equipamentos / Locação',
        servicos: 'Serviços / Mão de Obra',
      },
      noMatchTitle: 'Nada por aqui',
      noMatchHint: 'Tente outra busca ou limpe o filtro.',
      countResult: '{count} fornecedor(es)',
    },
    purchaseOrders: {
      title: 'Pedidos de compra',
      subtitle: 'Ordem de fornecimento para o seu canteiro.',
      empty: 'Nenhum pedido emitido.',
      new: 'Novo pedido',
      status: {
        draft: 'Rascunho',
        issued: 'Emitido',
        partially_received: 'Recebido parcial',
        received: 'Recebido',
        cancelled: 'Cancelado',
      },
      supplier: 'Fornecedor',
      selectSupplier: 'Selecione…',
      addSupplier: '+ Novo fornecedor',
      expectedDelivery: 'Entrega prevista',
      itemDescription: 'Descrição',
      itemQuantity: 'Qtd.',
      itemUnit: 'Un.',
      itemUnitPrice: 'Preço un. (R$)',
      itemSubtotal: 'Subtotal',
      addRow: 'Adicionar item',
      tax: 'Impostos (R$)',
      shipping: 'Frete (R$)',
      subtotal: 'Subtotal',
      total: 'Total',
      issue: 'Emitir',
      issueConfirm: 'Emitir pedido? Após emitido, itens ficam imutáveis.',
      cancel: 'Cancelar pedido',
      cancelReason: 'Motivo do cancelamento',
      issuedAt: (when, who) => `Emitido em ${when} por ${who}`,
      receiveCta: 'Registrar recebimento',
      couldNotIssue: 'Não foi possível emitir o pedido.',
      couldNotCancel: 'Não foi possível cancelar.',
      couldNotCreate: 'Não foi possível criar o pedido.',
      viewItems: 'Ver itens',
      linkedPr: 'Vinculado à solicitação',
      retentionLabel: 'Retenção de caução (%)',
      retentionHint: 'Padrão 5% para serviços; 0% dispensa a caução.',
      retentionPlaceholder: '0',
    },
    retention: {
      title: 'Caução retida',
      subtitle: 'Retenção técnica que garante a execução do pedido.',
      configured: (pct) => `${pct}% sobre cada pagamento emitido.`,
      notConfigured: 'Este pedido não possui retenção configurada.',
      withheld: 'Retido',
      released: 'Liberado',
      balance: 'Saldo em caução',
      release: 'Liberar caução',
      releaseTitle: 'Liberar caução',
      releaseAmount: 'Valor a liberar (R$)',
      releaseMethod: 'Forma',
      releaseCta: 'Liberar',
      releaseSuccess: 'Liberação registrada. Confirme a liquidação em Pagamentos.',
      releaseError: 'Não foi possível registrar a liberação.',
      noBalance: 'Nada a liberar neste pedido.',
      netAfterHoldback: (net) => `Líquido ao fornecedor: ${net}`,
      holdbackChip: (amount) => `Retido ${amount}`,
    },
    receipts: {
      title: 'Recebimentos',
      empty: 'Nenhum recebimento registrado.',
      new: 'Registrar recebimento',
      receivedAt: 'Data de recebimento',
      itemQuantityReceived: 'Qtd. recebida',
      itemQuantityDamaged: 'Qtd. avariada',
      itemNotes: 'Obs.',
      notesPlaceholder: 'Observações do recebimento…',
      submit: 'Registrar',
      receivedBy: (who, when) => `Recebido por ${who} em ${when}`,
      couldNotCreate: 'Não foi possível registrar o recebimento.',
    },
    payments: {
      title: 'Pagamentos',
      subtitle: 'Movimentações financeiras do projeto (pedidos, medições, reembolsos).',
      empty: 'Nenhum pagamento registrado.',
      new: 'Novo pagamento',
      status: {
        pending: 'Pendente',
        settled: 'Liquidado',
        failed: 'Falhou',
        cancelled: 'Cancelado',
      },
      kindLabel: 'Tipo',
      methodLabel: 'Método',
      kind: {
        po: 'Pedido',
        measurement: 'Medição',
        reimbursement: 'Reembolso',
        refund: 'Estorno',
        other: 'Outro',
      },
      method: {
        pix: 'PIX',
        boleto: 'Boleto',
        transfer: 'TED / DOC',
        cash: 'Dinheiro',
        other: 'Outro',
      },
      amount: 'Valor (R$)',
      amountHint: 'Use valor negativo para estornos.',
      occurredAt: 'Data',
      pixTxId: 'ID da transação PIX',
      reference: 'Referência',
      linkedTo: 'Vínculo',
      linkedPo: (code) => `Pedido ${code}`,
      linkedMeasurement: (period) => `Medição ${period}`,
      settle: 'Confirmar liquidação',
      settleConfirm: 'Confirmar que este pagamento foi liquidado?',
      settledAt: (when) => `Liquidado em ${when}`,
      recordedBy: (who) => `Registrado por ${who}`,
      couldNotCreate: 'Não foi possível registrar o pagamento.',
      couldNotSettle: 'Não foi possível liquidar.',
      submit: 'Registrar',
      settledTotal: 'Liquidado',
      pendingTotal: 'A liquidar',
    },
    changeOrders: {
      title: 'Aditivos',
      subtitle: 'Alterações de escopo com impacto em prazo e custo.',
      empty: 'Nenhum aditivo registrado.',
      new: 'Novo aditivo',
      status: {
        draft: 'Rascunho',
        submitted: 'Enviado',
        approved: 'Aprovado',
        rejected: 'Rejeitado',
        cancelled: 'Cancelado',
      },
      codeLabel: 'Código',
      deltaCents: 'Delta de custo (R$)',
      deltaCentsHint: 'Negativo reduz escopo.',
      deltaDays: 'Delta de prazo (dias)',
      deltaDaysHint: 'Negativo encurta.',
      submittedAt: (when) => `Enviado em ${when}`,
      decidedBy: (who, when) => `Decidido por ${who} em ${when}`,
      submit: 'Enviar para decisão',
      approve: 'Aprovar',
      reject: 'Rejeitar',
      rejectReason: 'Motivo da rejeição',
      couldNotCreate: 'Não foi possível criar o aditivo.',
      couldNotSubmit: 'Não foi possível enviar.',
      couldNotDecide: 'Não foi possível registrar a decisão.',
      // pm-friction P1 · 2026-05-21 — NextActionCard copy for terminal states.
      nextActionApprovedTitle: 'Mudança aprovada',
      nextActionApprovedImpact: (cost, days) =>
        `Impacto aplicado ao baseline: ${cost} · ${days}.`,
      nextActionApprovedImpactNeutral: 'Impacto aplicado ao baseline.',
      // W123.3 · JP-12: aditivo aplicado de verdade. Explainer pra PM
      // não ter que adivinhar onde a mudança aparece.
      nextActionApprovedImpactExplainer:
        'Orçamento e cronograma atualizados — abrir orçamento ou cronograma pra ver as mudanças.',
      nextActionApprovedCta: 'Ver impacto no cronograma',
      nextActionRejectedTitle: 'Mudança rejeitada',
      nextActionRejectedImpactDefault: 'O baseline segue inalterado.',
      nextActionRejectedImpactWithReason: (reason) => `Motivo registrado: "${reason}".`,
    },
    approvalPage: {
      title: 'Aprovação via link',
      loading: 'Validando token…',
      invalidTitle: 'Link inválido',
      invalidBody: 'Esse link não é reconhecido. Solicite um novo.',
      usedTitle: 'Link já utilizado',
      usedBody: 'Este token já foi resgatado. Cada link só vale para uma ação.',
      expiredTitle: 'Link expirado',
      expiredBody: 'O prazo deste link acabou. Peça para emitirem outro.',
      confirmTitle: (action) => `Confirmar ${action}`,
      confirmApproveBody: (entity) => `Você está prestes a APROVAR: ${entity}.`,
      confirmRejectBody: (entity) => `Você está prestes a REJEITAR: ${entity}.`,
      confirmCta: 'Confirmar',
      cancelCta: 'Cancelar',
      reasonLabel: 'Justificativa (opcional)',
      reasonPlaceholder: 'Por quê?',
      doneTitle: 'Pronto!',
      doneBody: 'Sua decisão foi registrada. Você pode fechar esta página.',
      entity: {
        purchase_request: 'Solicitação de compra',
        purchase_order: 'Pedido de compra',
        measurement: 'Medição',
        change_order: 'Aditivo',
      },
      action: {
        approve: 'aprovação',
        reject: 'rejeição',
      },
      goHome: 'Ir para o app',
    },
  },
  wave10: {
    tabs: {
      issues: 'Problemas',
    },
    issues: {
      title: 'Problemas',
      subtitle: 'Punch-list da obra — registre, atribua e resolva.',
      empty: 'Nenhum problema aberto.',
      emptyHint: 'Anote qualquer pendência da obra — itens de punch-list, ajustes e correções. Para não conformidades formais, use a seção Vistorias.',
      newCta: 'Novo problema',
      createTitle: 'Registrar problema',
      titleLabel: 'Título',
      descriptionLabel: 'Descrição (opcional)',
      severityLabel: 'Gravidade',
      dueDateLabel: 'Prazo (opcional)',
      stageLabel: 'Etapa relacionada',
      assigneeLabel: 'Responsável',
      noneOption: 'Nenhum',
      reporterLabel: (name) => `Reportado por ${name}`,
      filterStatus: 'Status',
      filterSeverity: 'Gravidade',
      filterAll: 'Todos',
      submit: 'Registrar',
      resolve: 'Resolver',
      reopen: 'Reabrir',
      discard: 'Descartar',
      markInProgress: 'Em andamento',
      markBlocked: 'Marcar bloqueado',
      confirmResolve: 'Marcar como resolvido?',
      resolutionNoteLabel: 'O que foi feito',
      resolutionNoteHint: 'Descreva brevemente a solução para registro histórico.',
      dueIn: (n) => (n === 1 ? 'Vence amanhã' : `Vence em ${n} dias`),
      overdueBy: (n) => (n === 1 ? 'Atrasado 1 dia' : `Atrasado ${n} dias`),
      dueToday: 'Vence hoje',
      couldNotCreate: 'Não foi possível registrar o problema.',
      couldNotUpdate: 'Não foi possível atualizar o problema.',
      severity: {
        baixa: 'Baixa',
        normal: 'Normal',
        alta: 'Alta',
        urgente: 'Urgente',
      },
      status: {
        aberta: 'Aberta',
        em_andamento: 'Em andamento',
        bloqueada: 'Bloqueada',
        resolvida: 'Resolvida',
        descartada: 'Descartada',
      },
    },
    comments: {
      title: 'Comentários',
      empty: 'Ainda não há comentários.',
      placeholder: 'Escreva um comentário…',
      post: 'Postar',
      posting: 'Postando…',
      postedBy: (name, when) => `${name} · ${when}`,
      deleted: '(removido)',
      delete: 'Excluir',
      confirmDelete: 'Excluir este comentário?',
      visibility: {
        internal: 'Interno',
        owner: 'Cliente',
        buyer: 'Comprador',
        public: 'Público',
      },
      couldNotPost: 'Não foi possível postar o comentário.',
      couldNotDelete: 'Não foi possível excluir.',
    },
    notifications: {
      title: 'Notificações',
      subtitle: 'Tudo o que está esperando sua atenção.',
      empty: 'Sem notificações por enquanto.',
      unreadCount: (n) =>
        n === 1 ? '1 não lida' : `${n} não lidas`,
      markAllRead: 'Marcar todas como lidas',
      markRead: 'Marcar lida',
      showAll: 'Ver todas',
      showUnread: 'Só não lidas',
      couldNotLoad: 'Não foi possível carregar notificações.',
    },
    admin: {
      title: 'Painel do administrador',
      subtitle: 'Visão geral da empresa.',
      totalProjects: 'Total de obras',
      inProgress: 'Em andamento',
      openIssues: 'Problemas abertos',
      pendingApprovals: 'Aprovações pendentes',
      overdueDocs: 'Documentos vencidos',
      recentEvents: 'Eventos recentes',
      noEvents: 'Sem eventos recentes.',
      noAccess: 'Acesso restrito ao administrador da conta.',
    },
    externalLinks: {
      title: 'Links externos',
      subtitle: 'Portais de cliente e comprador.',
      empty: 'Nenhum link emitido.',
      new: 'Novo link',
      roleLabel: 'Perfil',
      roleOwner: 'Cliente (obra inteira)',
      roleBuyer: 'Comprador (unidade)',
      unitIdLabel: 'Unidade',
      unitIdHint: 'Ex.: unidade-101',
      recipientEmailLabel: 'E-mail do destinatário (opcional)',
      recipientPhoneLabel: 'Telefone (opcional)',
      ttlDaysLabel: 'Validade (dias)',
      ttlDaysHint: 'Entre 1 e 365 dias.',
      submit: 'Gerar link',
      copiedLink: 'Link copiado!',
      copyLink: 'Copiar link',
      revoke: 'Revogar',
      revoked: 'Revogado',
      expires: (date) => `Expira em ${date}`,
      expired: (date) => `Expirou em ${date}`,
      viewCount: (n) => (n === 1 ? '1 acesso' : `${n} acessos`),
      lastViewed: (when) => `Último acesso: ${when}`,
      neverViewed: 'Nunca acessado',
      includeRevoked: 'Incluir revogados',
      couldNotCreate: 'Não foi possível gerar o link.',
      couldNotRevoke: 'Não foi possível revogar.',
    },
    buyerPortal: {
      loadingTitle: 'Carregando seu portal…',
      loadingBody: 'Validando link de acesso.',
      invalidTitle: 'Link inválido',
      invalidBody: 'Esse link não é reconhecido. Solicite um novo.',
      expiredTitle: 'Link expirado',
      expiredBody: 'O prazo desse link acabou. Peça um novo ao responsável da obra.',
      revokedTitle: 'Link revogado',
      revokedBody: 'Esse link foi revogado. Entre em contato com a construtora.',
      progressLabel: 'Progresso',
      stagesTitle: 'Etapas da obra',
      recentUpdatesTitle: 'Atualizações recentes',
      noStages: 'Ainda não há etapas cadastradas.',
      noUpdates: 'Sem atualizações recentes.',
      welcome: (clientName) => `Olá, ${clientName}`,
      roleBadgeBuyer: 'Comprador',
      roleBadgeOwner: 'Cliente',
    },
    bottomTabs: {
      ariaLabel: 'Navegação principal',
      // Wave 10 keys (kept for backwards-compat / read-only branch).
      // The `obras`/`fornecedores`/`admin` labels remain in the type
      // because copy translations might still reference them; the
      // crew strip stops using `obras`/`fornecedores`/`admin` in
      // Wave 352-A.
      obras: 'Obras',
      fornecedores: 'Fornec.',
      admin: 'Admin',
      owner: 'Minhas',
      // Wave 352-A · semantic flip: `inbox` now points the user to
      // the unified Wave 351-C inbox (`/inbox`). The read-only label
      // for the bell-style notifications page (`/notificacoes`) is
      // now `avisos` so the two are never confused. `campo` and
      // `hoje` are the two new crew tabs.
      inbox: 'Inbox',
      avisos: 'Avisos',
      campo: 'Campo',
      hoje: 'Hoje',
      eu: 'Eu',
    },
    offline: {
      bannerTitle: 'Você está offline — vamos sincronizar assim que voltar.',
      bannerHint: 'O app continua funcionando em modo leitura.',
    },
    uploads: {
      title: 'Envios',
      active: (n) => (n === 1 ? 'Enviando 1 arquivo…' : `Enviando ${n} arquivos…`),
      errored: (n) =>
        n === 1 ? '1 envio falhou' : `${n} envios falharam`,
      queued: 'Na fila…',
      uploaded: 'Enviado',
      done: 'Tudo enviado',
      clearDone: 'Limpar',
    },
    approval: {
      requestedBy: (name) => `solicitado por ${name}`,
      requestedAt: (when) => `Solicitado em ${when}`,
    },
    portal: {
      poweredBy: 'Builder · Acompanhe sua obra pelo celular',
    },
  },

  wave11: {
    tabs: {
      budget: 'Orçamento',
    },
    budget: {
      title: 'Orçamento da obra',
      subtitle:
        'Planejado × realizado. Os valores realizados vêm dos pedidos aprovados marcados com a categoria.',
      empty:
        'Nenhum item de orçamento ainda. Crie o primeiro para começar a acompanhar o previsto × realizado.',
      addItem: 'Adicionar item',
      addItemHint: 'Descreva brevemente o item e o valor planejado em R$.',
      descriptionPlaceholder: 'Ex.: Cimento CP-II 50kg (50 sacos)',
      plannedPlaceholder: 'R$ 0,00',
      cancel: 'Cancelar',
      save: 'Salvar',
      saving: 'Salvando…',
      includeArchived: 'Incluir arquivados',
      category: 'Categoria',
      description: 'Descrição',
      planned: 'Planejado',
      // W123.9 · JP-13: "Realizado" era ambíguo (englobava PO emitido +
      // pago). Renomeado pra "Comprometido" (PO ativo + gasto manual).
      // "Pago" e "Saldo" agora são colunas separadas.
      actual: 'Comprometido',
      paid: 'Pago',
      saldo: 'Saldo',
      variance: 'Variação',
      varianceFavorable: (amount) => `${amount} abaixo`,
      varianceUnfavorable: (amount) => `${amount} acima`,
      varianceOnTrack: 'No previsto',
      varianceNoActual: 'Sem realizado lançado',
      usage: (percent) => `${percent}% usado`,
      usageOver: 'Sem plano',
      poCount: (count) =>
        count === 1 ? '1 pedido' : `${count} pedidos`,
      totals: 'Totais por categoria',
      grandPlanned: 'Total planejado',
      grandActual: 'Total comprometido',
      grandPaid: 'Total pago',
      grandSaldo: 'Saldo',
      untagged: 'Sem categoria',
      untaggedHint:
        'Pedidos aprovados sem categoria. Edite o pedido para marcá-lo em um item do orçamento.',
      archived: 'Arquivado',
      archive: 'Arquivar',
      unarchive: 'Reativar',
      removeConfirm:
        'Tem certeza que deseja remover este item? Os pedidos ligados permanecem — só a linha do orçamento some.',
      categories: {
        obra: 'Obra',
        material: 'Material',
        mao_obra: 'Mão de obra',
        servico: 'Serviço',
        equipamento: 'Equipamento',
        outros: 'Outros',
      },
      errors: {
        descriptionTooShort:
          'Descrição precisa ter pelo menos 2 caracteres.',
        plannedNegative: 'O valor planejado não pode ser negativo.',
        loadFailed: 'Não foi possível carregar o orçamento.',
      },
    },
    audit: {
      title: 'Histórico',
      close: 'Fechar',
      empty: 'Sem eventos por aqui ainda.',
      verifyChain: 'Verificar cadeia',
      chainOk: 'Cadeia íntegra — nenhum evento foi adulterado.',
      chainBrokenAt: (id) => `Quebra detectada no evento ${id}.`,
      loadFailed: 'Não foi possível carregar o histórico.',
    },
    excelImport: {
      importBtn: 'Importar Excel',
      exportBtn: 'Exportar Excel',
      pageTitle: 'Importar planilha de orçamento',
      uploadStep: '1. Upload',
      mappingStep: '2. Colunas',
      diffStep: '3. Revisão',
      confirmStep: '4. Confirmar',
      dropzone: 'Arraste o arquivo .xlsx aqui',
      or: 'ou',
      browse: 'selecione no computador',
      acceptedFormats: 'Aceita .xlsx (Excel 2007+)',
      analyzing: 'Analisando planilha…',
      columnHeader: 'Coluna na planilha',
      fieldLabel: 'Campo do orçamento',
      confidence: 'Confiança',
      ignore: 'Ignorar',
      addsTitle: (n) => `Adicionar ${n} linha(s)`,
      updatesTitle: (n) => `Atualizar ${n} linha(s)`,
      conflictsTitle: (n) => `${n} conflito(s)`,
      conflictNote: 'Estas linhas não serão importadas — o total existente difere do calculado. Resolva manualmente.',
      applyBtn: (n) => `Aplicar ${n} mudança(s)`,
      applying: 'Aplicando…',
      successToast: (adds, updates) =>
        `Orçamento atualizado: +${adds} adicionados, ${updates} atualizados.`,
      cancel: 'Cancelar',
      back: 'Voltar',
      next: 'Próximo',
      noChanges: 'Nenhuma mudança detectada. O arquivo está sincronizado com o orçamento atual.',
      previewRows: 'Pré-visualização (primeiras 20 linhas)',
      sheetInfo: (sheet, rows) => `Planilha "${sheet}" · ${rows} linha(s) de dados`,
      mappingHint: 'Confira o mapeamento de colunas. Use "Ignorar" para excluir uma coluna da importação.',
      warnings: 'Avisos',
    },
  },

  wave12: {
    search: {
      label: 'Buscar',
      placeholder: 'Buscar obras, RDO, problemas, fornecedores…',
      shortcut: 'Ctrl K',
      noResults: 'Nada encontrado.',
      loading: 'Buscando…',
      recent: 'Abertos recentemente',
      openPalette: 'Abrir busca',
      groups: {
        projects: 'Obras',
        dailyLogs: 'RDO',
        issues: 'Problemas',
        suppliers: 'Fornecedores',
        purchaseOrders: 'Pedidos',
      },
    },
    rdoPdf: {
      export: 'Exportar PDF',
      generating: 'Gerando PDF…',
      title: (projectName) => `RDO — ${projectName}`,
      generatedAt: (when) => `Gerado em ${when}`,
      date: 'Data',
      weather: 'Clima',
      crewSize: 'Equipe',
      hoursWorked: 'Horas',
      temperature: 'Temperatura',
      workPerformed: 'Trabalhos executados',
      issues: 'Ocorrências',
      author: 'Responsável',
      footer: 'Builder · Diário de obra',
      fileName: (projectName, date) =>
        `rdo-${projectName}-${date}.pdf`.replace(/\s+/g, '-').toLowerCase(),
    },
    pwa: {
      installTitle: 'Instalar o Builder no celular?',
      installBody:
        'Adicione à tela inicial para abrir em tela cheia e receber notificações.',
      installCta: 'Instalar',
      later: 'Agora não',
      installed: 'App instalado',
    },
  },

  commandBar: {
    placeholder: 'Buscar em toda a plataforma…',
    noResults: 'Nada encontrado.',
    loading: 'Buscando…',
    recent: 'Visitados recentemente',
    groups: {
      projects: 'Obras',
      estimates: 'Orçamentos',
      budgetItems: 'Itens de orçamento',
      pos: 'Pedidos de compra',
      suppliers: 'Fornecedores',
      rdos: 'RDO',
      fvs: 'Vistorias',
      drawings: 'Pranchas',
      rfis: 'RFIs',
    },
  },

  wave14: {
    tabs: {
      dashboard: 'Visão geral',
    },
    dashboard: {
      title: 'Visão geral da obra',
      subtitle:
        'Indicadores, curva-S prevista × realizada e últimos eventos em um só lugar.',
      hero: {
        // Wave 314N — eyebrow zerado (a aba já se chama Visão geral, então
        // "Painel da obra" virava redundância visual ocupando espaço).
        eyebrow: '',
        progressLabel: 'Progresso geral',
        // "há 3 dias" já vem com o "há" embutido — antes ficava
        // "Atualizado em há 3 dias" (duplo "em + há"), pt-BR quebrado.
        lastUpdated: (when) => `Atualizado ${when}`,
      },
      kpi: {
        progress: 'Progresso',
        progressHint: (percent) =>
          percent >= 100
            ? 'Obra concluída'
            : percent === 0
              ? 'Ainda não iniciada'
              : `Faltam ${100 - percent}% para a entrega`,
        budgetUsage: 'Orçamento executado',
        budgetUsageHint: (actual, planned) => `${actual} de ${planned}`,
        budgetEmpty: 'Sem orçamento cadastrado',
        openPos: 'Pedidos em aberto',
        openPosHint: (count) =>
          count === 1
            ? '1 pedido emitido aguardando recebimento'
            : `${count} pedidos aguardando recebimento`,
        openIssues: 'Problemas abertos',
        openIssuesHint: (count) =>
          count === 0
            ? 'Nenhum problema em aberto'
            : count === 1
              ? '1 problema em andamento'
              : `${count} problemas em andamento`,
        expiringDocs: 'Docs vencendo (30d)',
        expiringDocsHint: (count) =>
          count === 1
            ? '1 documento vence nos próximos 30 dias'
            : `${count} documentos vencem nos próximos 30 dias`,
        noExpiring: 'Nenhum documento vencendo',
        lastRdo: 'Último RDO',
        lastRdoHint: (when) => `Assinado em ${when}`,
        noRdo: 'Ainda sem RDO',
      },
      curve: {
        title: 'Curva-S (previsto × realizado)',
        subtitle:
          'Previsto derivado do cronograma do projeto; realizado do orçamento executado.',
        planned: 'Previsto',
        actual: 'Realizado',
        empty:
          'Defina datas de início/entrega e orçamento para desenhar a curva.',
        axisStart: 'Início',
        axisEnd: 'Entrega',
      },
      activity: {
        title: 'Atividade recente',
        subtitle: 'Últimos 20 eventos registrados na obra.',
        empty: 'Nada por aqui ainda — mova a obra e os eventos aparecem.',
        loadFailed: 'Não conseguimos carregar os eventos.',
        viewAll: 'Ver trilha completa',
      },
      errors: {
        loadFailed: 'Não foi possível carregar o painel.',
      },
    },
    boletim: {
      title: 'Boletim de medição',
      subtitle:
        'Consolida medições aprovadas, pedidos emitidos e pagamentos do mês em um PDF pronto para o cliente.',
      monthLabel: 'Mês de referência',
      generateButton: 'Gerar PDF do mês',
      generating: 'Gerando boletim…',
      loadFailed: 'Não foi possível carregar o boletim.',
      emptyMonth:
        'Nenhuma medição aprovada, pedido ou pagamento neste mês. O PDF será gerado mesmo assim.',
      pdf: {
        docTitle: (project, month) => `Boletim de medição — ${project} · ${month}`,
        headerLabel: 'Boletim de medição',
        periodLabel: (start, end) => `Período: ${start} a ${end}`,
        clientLabel: 'Cliente',
        addressLabel: 'Endereço',
        generatedAt: (when) => `Gerado em ${when}`,
        measurementsTitle: 'Medições aprovadas',
        measurementsEmpty: 'Sem medições aprovadas neste mês.',
        poTitle: 'Pedidos de compra emitidos',
        poEmpty: 'Sem pedidos emitidos neste mês.',
        paymentsTitle: 'Pagamentos',
        paymentsEmpty: 'Sem pagamentos lançados neste mês.',
        totalsTitle: 'Totais do mês',
        totalMeasurements: 'Total medições',
        totalPos: 'Total pedidos',
        totalPaymentsSettled: 'Pagamentos liquidados',
        grandTotal: 'Total devido no mês (medições + pedidos)',
        signaturesTitle: 'Aprovação',
        signClient: 'Cliente / Contratante',
        signContractor: 'Construtora / Responsável técnico',
        footer: 'Builder · Boletim de medição',
        fileName: (projectSlug, month) => `boletim-${projectSlug}-${month}.pdf`,
        colPeriod: 'Período',
        colStatus: 'Status',
        colTotal: 'Total',
        colApprovedBy: 'Aprovado por',
        colCode: 'Código',
        colSupplier: 'Fornecedor',
        colTitle: 'Descrição',
        colIssuedAt: 'Emitido em',
        colKind: 'Tipo',
        colMethod: 'Método',
        colOccurredAt: 'Data',
        colAmount: 'Valor',
      },
    },
  },

  wave15: {
    dre: {
      title: 'DRE por obra',
      subtitle:
        'Receita realizada, custos comprometidos e fluxo de caixa da obra no período escolhido.',
      fromLabel: 'De',
      toLabel: 'Até',
      clearRange: 'Limpar período',
      loadFailed: 'Não foi possível carregar o DRE. Tente novamente.',
      allTime: 'Desde o início da obra',
      hero: {
        marginEyebrow: 'Margem bruta',
        grossProfit: 'Lucro bruto',
        profit: 'Lucro',
        loss: 'Prejuízo',
      },
      revenue: {
        heading: 'Receita',
        measurements: (count) =>
          count === 1 ? '1 medição aprovada' : `${count} medições aprovadas`,
        changeOrders: (count) =>
          count === 1 ? '1 aditivo aprovado' : `${count} aditivos aprovados`,
        total: 'Receita total',
      },
      costs: {
        heading: 'Custos',
        purchaseOrders: (count) =>
          count === 1 ? '1 pedido de compra' : `${count} pedidos de compra`,
        otherExpenses: (count) =>
          count === 1 ? '1 despesa operacional' : `${count} despesas operacionais`,
        refunds: (count) =>
          count === 1 ? '1 estorno' : `${count} estornos`,
        total: 'Custos totais',
      },
      cash: {
        heading: 'Caixa (realizado)',
        subtitle: 'Somente pagamentos com status liquidado.',
        receivedFromClient: 'Recebido do cliente',
        paidToSuppliers: 'Pago a fornecedores',
        otherOutflows: 'Outras saídas',
        refunds: 'Estornos',
        net: 'Saldo líquido',
      },
      generatedAt: (when) => `Gerado em ${when}`,
    },
    cronograma: {
      title: 'Cronograma físico-financeiro',
      subtitle:
        'Curva S acumulada: avanço físico previsto × realizado e gasto previsto × comprometido × pago.',
      granularityMonth: 'Mensal',
      granularityWeek: 'Semanal',
      loadFailed: 'Não foi possível carregar o cronograma.',
      empty: 'Sem dados para o período da obra.',
      emptyHint:
        'Defina data de início e prazo estimado da obra e registre medições para ver a curva S.',
      legend: {
        physicalPlanned: 'Físico previsto',
        physicalActual: 'Físico realizado',
        financialPlanned: 'Financeiro previsto',
        financialCommitted: 'Comprometido',
        financialPaid: 'Pago',
      },
      axis: {
        physical: 'Avanço físico',
        financial: 'Acumulado financeiro',
      },
      totals: {
        budget: 'Orçamento',
        committed: 'Comprometido',
        paid: 'Pago',
        commitmentOfBudget: (percent) => `${percent}% do orçamento`,
        paidOfBudget: (percent) => `${percent}% do orçamento`,
      },
      bucketCount: (n) => (n === 1 ? '1 período' : `${n} períodos`),
      generatedAt: (when) => `Gerado em ${when}`,
    },
  },

  wave17: {
    portfolio: {
      title: 'Carteira de obras',
      subtitle:
        'Visão consolidada de todas as obras que você acompanha — indicadores, saúde financeira e obras em risco.',
      empty: 'Nenhuma obra cadastrada ainda.',
      emptyHint:
        'Crie a primeira obra para começar a enxergar a carteira por aqui.',
      loadFailed: 'Não foi possível carregar a carteira.',
      hero: {
        eyebrow: 'Painel da carteira',
        // Wave 314N — formatRelative já entrega "há N dias"; remover "em"
        // pra não duplicar preposição.
        lastUpdated: (when) => `Atualizado ${when}`,
      },
      kpi: {
        activeProjects: 'Obras ativas',
        activeProjectsHint: (active, total) =>
          total === 1
            ? active === 1
              ? '1 de 1 em andamento'
              : '0 de 1 em andamento'
            : `${active} de ${total} em andamento`,
        avgProgress: 'Progresso médio',
        avgProgressHint: (percent) =>
          percent === 0
            ? 'Sem avanço registrado'
            : percent >= 100
              ? 'Obras concluídas'
              : `Média das obras ativas: ${percent}%`,
        atRisk: 'Obras em risco',
        atRiskHint: (count) =>
          count === 0
            ? 'Tudo sob controle'
            : count === 1
              ? '1 obra precisa de atenção'
              : `${count} obras precisam de atenção`,
        budget: 'Orçamento total',
        budgetHint: (committed) => `${committed} já comprometidos`,
        committed: 'Comprometido',
        committedHint: (percent) => `${percent} do orçamento`,
        paid: 'Pago',
        paidHint: (percent) => `${percent} do orçamento`,
        receivables: 'A receber',
        receivablesHint: (count) =>
          count === 0
            ? 'Nenhuma medição aguardando pagamento'
            : count === 1
              ? '1 medição aguardando pagamento'
              : `${count} medições aguardando pagamento`,
        openIssues: 'Problemas críticos',
        openIssuesHint: (count) =>
          count === 0
            ? 'Nenhum problema alta/urgente em aberto'
            : count === 1
              ? '1 problema alta/urgente em aberto'
              : `${count} problemas alta/urgente em aberto`,
      },
      redactedBanner:
        'Valores financeiros ocultos porque seu acesso é somente-leitura (cliente/corretor).',
      projectsHeading: 'Obras',
      projectsSubtitle:
        'Obras em risco aparecem primeiro, seguidas pelas ativas e depois pelas concluídas.',
      atRiskBadge: 'Em risco',
      statusLabel: (status) => {
        const map: Record<string, string> = {
          planning: 'Planejamento',
          in_progress: 'Em andamento',
          on_hold: 'Em espera',
          completed: 'Concluída',
          archived: 'Arquivada',
        };
        return map[status] ?? status;
      },
      progressLabel: (percent) => `${percent}% executado`,
      budgetLabel: (used, total) => `${used} / ${total}`,
      noBudget: 'Orçamento não definido',
      openIssuesBadge: (count) =>
        count === 1 ? '1 problema' : `${count} problemas`,
      pendingMeasurementsBadge: (count) =>
        count === 1 ? '1 medição pendente' : `${count} medições pendentes`,
      overdueHint: (date) => `Prazo vencido em ${date}`,
      openProject: 'Abrir obra',
      exportCsv: 'Exportar CSV',
      exportCsvHint: 'Baixa a carteira inteira em CSV (UTF-8) — uma linha por obra.',
      exportCsvBusy: 'Gerando CSV…',
      exportCsvFailed: 'Não foi possível gerar o CSV. Tente novamente.',
    },
  },

  wave18: {
    fluxoCaixa: {
      title: 'Fluxo de caixa',
      subtitle: 'Projeção mensal com entradas realizadas, saídas e medições previstas.',
      loadFailed: 'Não foi possível carregar o fluxo de caixa desta obra.',
      empty: 'Sem movimentações no período.',
      emptyHint:
        'Cadastre pedidos de compra, medições e pagamentos para ver a projeção.',
      hero: {
        eyebrow: 'Projeção de 12 meses',
        lastUpdated: (when) => `Atualizado ${when}`,
      },
      kpi: {
        realizedIn: 'Entradas realizadas',
        realizedOut: 'Saídas realizadas',
        projectedIn: 'Entradas previstas',
        projectedOut: 'Saídas previstas',
        netWindow: 'Saldo do período',
        endingBalance: 'Saldo acumulado final',
      },
      series: {
        realizedInflow: 'Entrada realizada',
        realizedOutflow: 'Saída realizada',
        projectedInflow: 'Entrada prevista',
        projectedOutflow: 'Saída prevista',
        cumulative: 'Saldo acumulado',
      },
      table: {
        month: 'Mês',
        realizedIn: 'Entrada realizada',
        realizedOut: 'Saída realizada',
        projectedIn: 'Entrada prevista',
        projectedOut: 'Saída prevista',
        net: 'Líquido',
        cumulative: 'Acumulado',
        currentMonth: 'Atual',
      },
    },
  },

  wave19: {
    alertas: {
      title: 'Alertas',
      subtitle:
        'O que precisa da sua atenção agora, agrupado por tipo e por obra.',
      loadFailed: 'Não foi possível carregar os alertas.',
      empty: 'Tudo em dia por aqui.',
      emptyHint: 'Nenhuma pendência urgente nas suas obras no momento.',
      groups: {
        docsExpiring: 'Documentos vencendo',
        overduePOs: 'Pedidos em atraso',
        pendingApprovals: 'Aprovações pendentes',
        overdueMeasurements: 'Medições em atraso',
        stalledStages: 'Etapas paradas',
      },
      severity: {
        critical: 'Crítico',
        warning: 'Atenção',
        info: 'Aviso',
      },
      countBadge: (n) => (n === 1 ? '1 item' : `${n} itens`),
      goToProject: 'Abrir obra',
    },
  },

  wave21: {
    supplier360: {
      title: 'Fornecedor 360',
      back: 'Voltar para fornecedores',
      loadFailed: 'Não foi possível carregar este fornecedor.',
      notFound: 'Fornecedor não encontrado.',
      noActivity: 'Este fornecedor ainda não tem atividade nas suas obras.',
      noActivityHint:
        'Pedidos de compra e pagamentos vinculados a este fornecedor aparecerão aqui.',
      archivedBadge: 'Arquivado',
      kpis: {
        totalCommitted: 'Comprometido',
        totalPaid: 'Pago',
        outstanding: 'A pagar',
        activePOs: 'Pedidos ativos',
        completedPOs: 'Pedidos recebidos',
        cancelledPOs: 'Cancelados',
        onTime: 'Entrega no prazo',
        onTimeNa: 'Sem dados',
        lastActivity: 'Última movimentação',
        lastActivityNever: 'Sem movimentações',
      },
      byProject: {
        title: 'Por obra',
        subtitle: 'Distribuição do fornecedor entre as obras que você acessa.',
        empty: 'Nenhuma obra com atividade para este fornecedor.',
        columnProject: 'Obra',
        columnCommitted: 'Comprometido',
        columnPaid: 'Pago',
        columnOutstanding: 'A pagar',
        columnActive: 'Ativos',
        activeCount: (n) => (n === 1 ? '1 pedido' : `${n} pedidos`),
      },
      recentPOs: {
        title: 'Pedidos recentes',
        subtitle: 'Últimos 20 pedidos emitidos para este fornecedor.',
        empty: 'Nenhum pedido emitido ainda.',
        columnCode: 'Código',
        columnProject: 'Obra',
        columnStatus: 'Status',
        columnTotal: 'Total',
        columnIssued: 'Emitido',
        columnExpected: 'Previsto',
        columnReceived: 'Recebido',
        onTime: 'No prazo',
        late: 'Atrasado',
        pending: 'Pendente',
      },
      redactedNote:
        'Valores financeiros omitidos para o seu perfil de acesso.',
    },
  },

  wave22: {
    approvals: {
      title: 'Caixa de aprovações',
      subtitle:
        'Tudo que está aguardando sua decisão em todas as obras.',
      back: 'Voltar',
      loadFailed: 'Não foi possível carregar a caixa de aprovações.',
      empty: 'Nenhuma pendência aberta.',
      emptyHint:
        'Quando alguém enviar uma solicitação, pedido de compra, aditivo ou medição para aprovação, ela aparece aqui.',
      filters: {
        all: 'Todos',
        purchase_request: 'Solicitações',
        purchase_order: 'Pedidos de compra',
        change_order: 'Aditivos',
        measurement: 'Medições',
      },
      counts: {
        total: 'Total',
        critical: 'Críticas',
        warning: 'Atenção',
        info: 'Recentes',
      },
      severity: {
        critical: 'Crítica',
        warning: 'Atenção',
        info: 'Recente',
      },
      byProject: {
        title: 'Por obra',
        subtitle: 'Onde as pendências estão concentradas.',
        countLabel: (n: number) => (n === 1 ? '1 pendência' : `${n} pendências`),
      },
      list: {
        columnType: 'Tipo',
        columnCode: 'Código',
        columnProject: 'Obra',
        columnSubmitted: 'Enviado',
        columnRequester: 'Solicitante',
        columnAmount: 'Valor',
        columnWaiting: 'Aguardando',
        waitingDays: (n: number) => (n === 1 ? '1 dia' : `${n} dias`),
        waitingToday: 'Hoje',
        openLink: 'Abrir',
      },
      redactedNote:
        'Valores financeiros omitidos para o seu perfil de acesso.',
      batch: {
        selectAll: 'Selecionar todos',
        selectItem: 'Selecionar pendência',
        selectedCount: (n: number, total: number) =>
          `${n} de ${total} selecionada${n === 1 ? '' : 's'}`,
        clear: 'Limpar',
        approveSelected: 'Aprovar selecionados',
        rejectSelected: 'Rejeitar selecionados',
        confirmApprove: (n: number, totalLabel: string) =>
          n === 1
            ? `Aprovar 1 pendência selecionada, somando ${totalLabel}? Isso libera o gasto.`
            : `Aprovar ${n} pendências selecionadas, somando ${totalLabel}? Isso libera os gastos.`,
        confirmReject: (n: number) =>
          n === 1
            ? 'Rejeitar 1 pendência selecionada? Essa ação não pode ser desfeita.'
            : `Rejeitar ${n} pendências selecionadas? Essa ação não pode ser desfeita.`,
        resultSummary: (ok: number, failed: number) =>
          failed === 0
            ? `${ok} concluída${ok === 1 ? '' : 's'} com sucesso.`
            : `${ok} concluída${ok === 1 ? '' : 's'} · ${failed} com erro.`,
      },
    },
  },

  wave23: {
    activity: {
      title: 'Atividade',
      subtitle:
        'O que aconteceu nos seus projetos — tudo que foi registrado, em ordem cronológica.',
      loadFailed: 'Não foi possível carregar a atividade.',
      empty: 'Sem registros no período.',
      emptyHint:
        'Eventos aparecem aqui assim que houver movimentação em algum projeto.',
      windowLabel: (days: number) =>
        days === 1 ? 'Últimas 24h' : `Últimos ${days} dias`,
      windowOption7: '7 dias',
      windowOption14: '14 dias',
      windowOption30: '30 dias',
      filters: {
        all: 'Tudo',
        rdo: 'RDO',
        purchase: 'Compras',
        change_order: 'Aditivos',
        measurement: 'Medições',
        docs: 'Documentos',
        admin: 'Admin',
        other: 'Outros',
      },
      counts: {
        total: 'Total',
        rdo: 'RDO',
        purchase: 'Compras',
        change_order: 'Aditivos',
        measurement: 'Medições',
        docs: 'Documentos',
      },
      byProject: {
        title: 'Por obra',
        subtitle: 'Eventos no período, por projeto.',
        countLabel: (n: number) =>
          n === 1 ? '1 evento' : `${n} eventos`,
      },
      eventTypes: {
        'rdo.created': 'RDO criado',
        'rdo.signed': 'RDO assinado',
        'rdo.correction_created': 'Correção de RDO',
        'purchase_request.created': 'Solicitação criada',
        'purchase_request.approved': 'Solicitação aprovada',
        'purchase_request.rejected': 'Solicitação rejeitada',
        'purchase_request.fulfilled': 'Solicitação atendida',
        'purchase_order.created': 'Pedido criado',
        'purchase_order.issued': 'Pedido emitido',
        'purchase_order.cancelled': 'Pedido cancelado',
        'receipt.created': 'Nota recebida',
        'payment.created': 'Pagamento lançado',
        'payment.settled': 'Pagamento liquidado',
        'payment.retention_release': 'Caução liberada',
        'supplier.created': 'Fornecedor cadastrado',
        'supplier.updated': 'Fornecedor atualizado',
        'change_order.created': 'Aditivo criado',
        'change_order.submitted': 'Aditivo enviado',
        'measurement.created': 'Medição criada',
        'measurement.approved': 'Medição aprovada',
        'measurement.paid': 'Medição paga',
        'attachment.uploaded': 'Anexo enviado',
        'attachment.deleted': 'Anexo removido',
        'comment.created': 'Comentário',
        'comment.deleted': 'Comentário removido',
        'issue.created': 'Problema aberto',
        'issue.deleted': 'Problema removido',
        'external_link.created': 'Link compartilhado',
        'external_link.revoked': 'Link revogado',
        'budget_item.created': 'Item de orçamento',
        'budget_item.deleted': 'Item removido',
        'budget_item.over_plan': 'Item acima do previsto',
        'progress.snapshot_recorded': 'Progresso registrado',
        'approval_token.used': 'Aprovação por link',
        'admin.user_created': 'Usuário criado',
        'admin.user_updated': 'Usuário atualizado',
        'admin.user_disabled': 'Usuário desativado',
        'admin.user_force_reset': 'Reset de senha forçado',
        'auth.password_reset_requested': 'Pedido de reset de senha',
        'auth.password_reset_completed': 'Reset de senha concluído',
        // W97.5 · eventos de sistema que apareciam crus no feed da Atividade.
        'budget_item.ia_accepted': 'IA aceitou linha do orçamento',
        'budget_item.ia_rejected': 'IA descartou linha do orçamento',
        'budget_approval.approved': 'Orçamento aprovado',
        'budget_approval.rejected': 'Orçamento recusado',
        'po_overdue_flagged': 'Pedido atrasado — alerta gerado',
        'po_overdue.flagged': 'Pedido atrasado — alerta gerado',
        'rdo_draft_stale_flagged': 'RDO em rascunho há mais de 7 dias',
        'rdo_draft_stale.flagged': 'RDO em rascunho há mais de 7 dias',
        'trena_funnel.stage1.entered': 'Trena · começou a estimativa',
        'trena_funnel.stage1.upload_started': 'Trena · enviando arquivo',
        'trena_funnel.stage1.upload_completed': 'Trena · arquivo enviado',
        'trena_funnel.stage1.extract_completed': 'Trena · medidas extraídas',
        'trena_funnel.stage2.entered': 'Trena · revisando medidas',
        'trena_funnel.stage3.entered': 'Trena · gerando composição',
        'trena_funnel.stage4.entered': 'Trena · revisando preços',
        'trena_funnel.stage5.entered': 'Trena · finalizando estimativa',
        'trena_funnel.completed': 'Trena · estimativa concluída',
      },
      // W97.5 · "OUTROS" e jargão técnico foram substituídos por labels PT-BR humanos.
      // Wave 295 — never expose raw DB keys ("inspection.signed") to users.
      unknownEvent: (code: string) => {
        // Mapeamento de fallback pra padrões comuns que ainda não foram
        // explicitamente listados acima.
        const lower = code.toLowerCase();
        if (lower.includes('overdue')) return 'Pedido atrasado';
        if (lower.includes('stale')) return 'Rascunho parado';
        if (lower.includes('ia_accepted')) return 'IA aceitou';
        if (lower.includes('ia_rejected')) return 'IA descartou';
        if (lower.includes('approved')) return 'Aprovado';
        if (lower.includes('rejected')) return 'Recusado';
        // Strip the prefix and underscore-spaces ("signed", "over plan").
        const tail = code.split('.').pop()?.replace(/_/g, ' ') ?? code;
        // Capitaliza primeira letra ("created" → "Created"), evita gritar.
        return tail.charAt(0).toUpperCase() + tail.slice(1);
      },
      bySystem: 'Sistema',
      relativeToday: 'Hoje',
      relativeYesterday: 'Ontem',
    },
  },

  wave24: {
    nfe: {
      sectionTitle: 'Importar NFe',
      sectionSubtitle:
        'Envie o XML da nota fiscal e o pagamento é criado automaticamente com os dados validados pela SEFAZ.',
      dropHint: 'Arraste o XML aqui ou',
      chooseFile: 'escolher arquivo',
      selectedFile: (name: string) => `Selecionado: ${name}`,
      parseButton: 'Pré-visualizar',
      ingestButton: 'Importar e criar pagamento',
      cancelButton: 'Cancelar',
      parsing: 'Lendo XML…',
      ingesting: 'Importando…',
      parsedOk: 'XML lido. Confira os dados antes de importar.',
      ingestedOk: 'NFe importada com sucesso.',
      chaveAcesso: 'Chave de acesso',
      cnpjEmitente: 'CNPJ emitente',
      razaoSocial: 'Razão social',
      nomeFantasia: 'Nome fantasia',
      numeroSerie: 'Nº / Série',
      dataEmissao: 'Emissão',
      valorTotal: 'Valor total',
      numeroProtocolo: 'Protocolo',
      noProtocol: '— (NFe ainda não autorizada)',
      supplierMatchOk: 'CNPJ bate com o fornecedor da OC.',
      supplierMatchWarn:
        'CNPJ emitente difere do fornecedor da OC — confira antes de liquidar.',
      duplicateError:
        'Esta NFe já foi importada neste tenant; nenhum pagamento duplicado foi criado.',
      parseError: (reason: string) => `Falha ao ler o XML: ${reason}`,
      methodLabel: 'Método de pagamento (default)',
      methodHint:
        'Apenas um palpite — você ajusta na liquidação. NFe recebida ≠ dinheiro movimentado.',
      methodTransfer: 'Transferência',
      methodPix: 'PIX',
      methodBoleto: 'Boleto',
      methodCash: 'Dinheiro',
      methodOther: 'Outro',
      paymentCreatedHeader: 'Pagamento criado',
      paymentCreatedBody: (ref: string) =>
        `Linha pendente criada na ficha financeira (${ref}). Abra a liquidação para confirmar método e valores.`,
    },
  },

  wave25: {
    share: {
      sectionTitle: 'Link de compartilhamento',
      medicaoSubtitle:
        'Gere um link público com o total da medição — sem login, sem custos internos.',
      dreSubtitle:
        'Gere um link público com receita, custo, lucro e margem — sem detalhe de fornecedor.',
      empty: 'Nenhum link ativo',
      ttlLabel: 'Válido por',
      ttl7: '7 dias',
      ttl14: '14 dias',
      ttl30: '30 dias',
      createButton: 'Gerar link',
      createFailed: 'Não foi possível gerar o link. Tente novamente.',
      tokenShowOnce:
        'Copie agora — o link já foi para a área de transferência',
      copyButton: 'Copiar',
      copied: 'Copiado',
      expiresAt: (when: string) => `Expira em ${when}`,
      views: (n: number) =>
        n === 0
          ? 'Nenhum acesso ainda'
          : n === 1
            ? '1 acesso'
            : `${n} acessos`,
      revokeButton: 'Revogar',
      revokeConfirm: 'Confirmar revogação',
      revokeCancel: 'Cancelar',
    },
    public: {
      header: 'Compartilhado por Builder',
      footer: (projectName: string, expiresAt: string) =>
        `${projectName} · expira em ${expiresAt}`,
      invalidTitle: 'Link indisponível',
      invalidBody:
        'Este link expirou, foi revogado ou nunca existiu. Peça um novo ao responsável pela obra.',
    },
  },

  publicErrors: {
    common: {
      contextLeilao: 'Leilão reverso',
      contextCotacao: 'Cotação',
      contextBudgetApproval: 'Aprovação de orçamento',
      contextResetPassword: 'Redefinir senha',
      contextBuyerPortal: 'Portal do cliente',
      contextOwnerPortal: 'Portal do proprietário',
      contextSupplierPortal: 'Portal do fornecedor',
      contextApprove: 'Aprovação rápida',
      contextSharedMedicao: 'Boletim de medição',
      contextSharedDre: 'Resultado da obra',
      ctaGoHome: 'Ir para Builder',
      ctaRequestNewLink: 'Pedir um link novo',
      ctaTryAgain: 'Tentar novamente',
      ctaBackToLogin: 'Voltar para o login',
      ctaForgotPassword: 'Pedir novo link de senha',
      // Wave Codex-R8 · 2026-05-19 · density upgrade
      faqHeading: 'Por que isso aconteceu?',
      faqWhyExpired: 'O link tem validade?',
      faqWhyExpiredAnswer:
        'Sim. Links públicos do Builder caducam para reduzir risco de exposição se forem encaminhados sem querer.',
      faqIsItSafe: 'Meus dados estão seguros?',
      faqIsItSafeAnswer:
        'Estão. O conteúdo continua arquivado na obra — só esse link específico parou de funcionar.',
      faqStillNeedHelp: 'Preciso desse acesso ainda hoje',
      faqStillNeedHelpAnswer:
        'Quem enviou o link consegue gerar um novo em segundos pelo painel do Builder.',
      contactSupportLabel: 'Falar com o suporte',
      contactSupportEmail: 'suporte@builder.nullscience.ai',
      expiryHintPrefix: 'Esse link parou de funcionar em',
      settingsButtonLabel: 'Preferências',
      settingsPanelTitle: 'Preferências',
      settingsLanguageLabel: 'Idioma',
      settingsThemeLabel: 'Tema',
      settingsCloseLabel: 'Fechar preferências',
    },
    leilao: {
      title: 'Esse leilão não está mais disponível',
      body:
        'Links de leilão expiram em 7 dias ou são encerrados quando a obra define o ganhador. Peça um link novo à gestora da obra para participar de novo.',
      hint:
        'Se você já entregou um lance, o histórico fica salvo no painel do fornecedor — a gestora consegue ver os valores que você enviou.',
    },
    cotacao: {
      title: 'Essa cotação não está mais aberta',
      body:
        'Os links de cotação valem 7 dias e são desativados quando a obra encerra a coleta de preços. Fale com a gestora para receber um link novo.',
      hint:
        'Se você respondeu antes do prazo, a obra já recebeu suas propostas — não precisa enviar de novo.',
    },
    budgetApproval: {
      title: 'Esse link de aprovação não funciona mais',
      body:
        'Links de aprovação de orçamento valem 7 dias. Peça à gestora da obra para gerar um novo link e enviar de novo.',
      hint:
        'O orçamento original continua salvo — só o link que expirou. A gestora consegue reenviar a qualquer momento.',
    },
    resetPassword: {
      title: 'Esse link de senha expirou',
      body:
        'Links de redefinição de senha valem 30 minutos por segurança. Peça um novo na página de recuperação e tente outra vez.',
      hint:
        'Se você não pediu nenhuma redefinição, pode ignorar o email — sua senha continua a mesma.',
    },
    buyerPortal: {
      title: 'Esse acesso não está mais disponível',
      body:
        'O link do portal do cliente foi revogado ou expirou. Fale com a gestora da obra para receber um link novo.',
      hint:
        'Cada link é pessoal — peça à gestora para gerar um específico para você.',
    },
    ownerPortal: {
      title: 'Esse acesso não está mais disponível',
      body:
        'O link do portal do proprietário foi revogado ou expirou. Fale com a equipe responsável pela obra para receber um link novo.',
      hint:
        'Os dados da obra continuam protegidos — só esse link específico parou de funcionar.',
    },
    supplierPortal: {
      title: 'Esse link não funciona mais',
      body:
        'Links de fornecedor valem 7 dias por segurança. Para receber um link novo, fale com a gestora da obra.',
      hint:
        'Se você é gestora e precisa gerar um novo link, acesse o painel da obra e vá em Fornecedores.',
    },
    approve: {
      title: 'Esse link de aprovação não está mais válido',
      body:
        'Links de aprovação rápida (WhatsApp ou email) valem 7 dias e são de uso único. Peça um link novo à equipe que enviou.',
      hint:
        'Se você já aprovou ou recusou, a decisão foi registrada — não precisa fazer de novo.',
    },
    sharedMedicao: {
      title: 'Esse boletim não está mais disponível',
      body:
        'Links de medição valem o período definido pela gestora (geralmente 7 a 30 dias). Peça um link novo se ainda precisa consultar.',
      hint:
        'A medição continua arquivada na obra — só esse link de visualização expirou.',
    },
    sharedDre: {
      title: 'Esse relatório não está mais disponível',
      body:
        'Links de DRE valem o período definido pela gestora (geralmente 7 a 30 dias). Peça um link novo se precisa consultar os números.',
      hint:
        'Os números continuam arquivados na obra — só esse link de visualização expirou.',
    },
  },

  wave27: {
    tabs: {
      curvaAbc: 'Curva ABC',
    },
    curva: {
      pageTitle: 'Curva ABC',
      subtitle:
        'Itens ordenados por participação no gasto (Pareto). A = 80%, B = 95%, C = cauda longa.',
      empty:
        'Sem pedidos de compra ainda. A Curva ABC aparece assim que os primeiros pedidos forem emitidos.',
      totalsSpend: 'Gasto total',
      totalsItems: 'Itens únicos',
      totalsPos: 'Pedidos',
      classHeader: 'Distribuição por classe',
      classA: 'A',
      classB: 'B',
      classC: 'C',
      classACaption: 'Alto impacto — priorize cotação e contratos',
      classBCaption: 'Impacto médio — revise periodicamente',
      classCCaption: 'Cauda longa — compras pontuais',
      tableItem: 'Item',
      tableUnit: 'Un.',
      tableQty: 'Qtde',
      tableSpend: 'Gasto',
      tablePercent: '% do total',
      tableCumulative: '% acum.',
      tableSupplier: 'Fornecedor principal',
      tablePos: 'Pedidos',
      generatedAt: (when: string) => `Atualizado em ${when}`,
    },
    catalog: {
      sectionTitle: 'Catálogo do fornecedor',
      sectionSubtitle:
        'Preços de referência por item para agilizar novos pedidos.',
      empty: 'Nenhum item no catálogo ainda.',
      addButton: 'Novo item',
      archiveButton: 'Arquivar',
      restoreButton: 'Restaurar',
      nameLabel: 'Nome',
      codeLabel: 'Código (opcional)',
      unitLabel: 'Unidade',
      priceLabel: 'Último preço (R$)',
      notesLabel: 'Observações',
      cancel: 'Cancelar',
      save: 'Salvar',
      saveFailed: 'Não foi possível salvar. Verifique os campos e tente novamente.',
    },
  },

  wave28: {
    tabs: {
      punchList: 'Entrega',
    },
    page: {
      title: 'Punch list de entrega',
      subtitle:
        'Pendências do walk-through final — o que ainda falta ajustar antes da entrega.',
      empty:
        'Nenhum item na lista. Comece registrando o que o dono apontou na visita.',
      addButton: 'Novo item',
      readinessLabel: 'Prontidão',
      totalLabel: 'Itens',
      pendingChip: 'Pendentes',
      inProgressChip: 'Em andamento',
      doneChip: 'Concluídos',
      dismissedChip: 'Dispensados',
    },
    columns: {
      pendente: 'Pendente',
      em_andamento: 'Em andamento',
      concluido: 'Concluído',
      nao_aplicavel: 'Dispensado',
      countSuffix: (n: number) => (n === 1 ? '1 item' : `${n} itens`),
    },
    priority: {
      alta: 'Alta',
      normal: 'Normal',
      baixa: 'Baixa',
    },
    form: {
      heading: 'Novo item da punch list',
      titleLabel: 'O que ajustar',
      titlePlaceholder: 'Ex.: Retocar pintura na parede da sala',
      descriptionLabel: 'Detalhes (opcional)',
      descriptionPlaceholder: 'Observações, medidas, referências…',
      locationLabel: 'Local',
      locationPlaceholder: 'Ex.: Suíte master',
      priorityLabel: 'Prioridade',
      cancel: 'Cancelar',
      save: 'Adicionar',
      saveFailed: 'Não foi possível salvar. Tente novamente.',
    },
    actions: {
      start: 'Iniciar',
      finish: 'Concluir',
      reopen: 'Reabrir',
      dismiss: 'Dispensar',
      restore: 'Restaurar',
    },
    meta: {
      reportedBy: (name: string, when: string) => `Por ${name} · ${when}`,
      location: (loc: string) => `Local: ${loc}`,
      assignedTo: (name: string) => `Responsável: ${name}`,
      resolvedAt: (when: string) => `Concluído ${when}`,
    },
  },

  wave29: {
    tabs: {
      maoDeObra: 'Mão-de-obra',
    },
    page: {
      title: 'Mão-de-obra diária',
      subtitle:
        'Efetivo por função no dia — pedreiros, serventes, mestre de obra. Base para custo real e alocação.',
      empty:
        'Nenhum lançamento ainda. Comece registrando o efetivo de hoje por função.',
      addButton: 'Novo lançamento',
      rangeLabel: 'Período',
      rangeAll: 'Tudo',
      rangeLast7: 'Últimos 7 dias',
      rangeLast30: 'Últimos 30 dias',
      rangeMonth: 'Mês atual',
    },
    kpi: {
      totalHours: 'Horas totais',
      totalCost: 'Custo total',
      personDays: 'Diárias',
      entries: 'Lançamentos',
      hoursUnit: (n: number) => (n === 1 ? '1 hora' : `${n} horas`),
      personDaysUnit: (n: number) => (n === 1 ? '1 diária' : `${n} diárias`),
      entriesUnit: (n: number) =>
        n === 1 ? '1 lançamento' : `${n} lançamentos`,
    },
    roles: {
      pedreiro: 'Pedreiro',
      servente: 'Servente',
      carpinteiro: 'Carpinteiro',
      armador: 'Armador',
      eletricista: 'Eletricista',
      encanador: 'Encanador',
      pintor: 'Pintor',
      gesseiro: 'Gesseiro',
      azulejista: 'Azulejista',
      mestre_obra: 'Mestre de obra',
      encarregado: 'Encarregado',
      outro: 'Outro',
    },
    rollup: {
      heading: 'Distribuição por função',
      roleCol: 'Função',
      personDaysCol: 'Diárias',
      hoursCol: 'Horas',
      costCol: 'Custo',
      shareCol: '% do custo',
      nothingYet: 'Sem dados no período selecionado.',
    },
    entries: {
      heading: 'Lançamentos',
      dateCol: 'Data',
      roleCol: 'Função',
      headcountCol: 'Efetivo',
      hoursPerPersonCol: 'h/pessoa',
      rateCol: 'R$/h',
      totalCol: 'Total',
      notesCol: 'Observações',
      actionsCol: '',
      edit: 'Editar',
      delete: 'Excluir',
      deleteConfirm: (date: string, role: string) =>
        `Excluir lançamento de ${role} em ${date}?`,
      deleteFailed: 'Não foi possível excluir. Tente novamente.',
    },
    form: {
      heading: 'Novo lançamento',
      editHeading: 'Editar lançamento',
      dateLabel: 'Data',
      roleLabel: 'Função',
      headcountLabel: 'Efetivo (pessoas)',
      headcountPlaceholder: 'Ex.: 4',
      hoursPerPersonLabel: 'Horas por pessoa',
      hoursPerPersonPlaceholder: 'Ex.: 8',
      hourlyRateLabel: 'Valor hora (R$)',
      hourlyRateHint: 'Custo direto + encargos. Pode ajustar depois.',
      notesLabel: 'Observações (opcional)',
      notesPlaceholder: 'Frente de serviço, subempreiteiro, etc.',
      previewLine: (hours: string, cost: string) =>
        `${hours} trabalhadas · ${cost}`,
      cancel: 'Cancelar',
      save: 'Salvar',
      saveFailed: 'Não foi possível salvar. Tente novamente.',
    },
    meta: {
      loggedBy: (name: string, when: string) => `Por ${name} · ${when}`,
      dateRange: (from: string, to: string) => `${from} — ${to}`,
    },
  },
  wave30: {
    tabs: {
      rnc: 'Não conformidades',
    },
    page: {
      title: 'Não conformidades',
      subtitle:
        'Tudo que destoou do projeto, norma ou contrato — com severidade, responsável e assinatura dupla (corretor + verificador).',
      empty:
        'Nenhuma não conformidade registrada. Abra a primeira quando encontrar algo fora do padrão.',
      addButton: 'Nova não conformidade',
      filterLabel: 'Filtro',
      filterAll: 'Todas',
      filterOpen: 'Abertas',
      filterAwaitingVerification: 'Aguardando verificação',
      filterOverdue: 'Em atraso',
    },
    kpi: {
      total: 'Total',
      open: 'Em aberto',
      overdue: 'Em atraso',
      conformance: 'Conformidade',
      overdueSuffix: (n: number) =>
        n === 1 ? 'item vencido' : 'itens vencidos',
    },
    severity: {
      baixa: 'Baixa',
      media: 'Média',
      alta: 'Alta',
      critica: 'Crítica',
    },
    severitySelect: {
      baixa: 'Baixa — não impede o andamento',
      media: 'Média — atrasa mas tem solução',
      alta: 'Alta — para uma equipe',
      critica: 'Crítica — para a obra',
    },
    status: {
      aberto: 'Aberto',
      em_analise: 'Em análise',
      corrigido: 'Corrigido',
      verificado: 'Verificado',
    },
    statusNext: {
      startAnalysis: 'Iniciar análise',
      markCorrected: 'Marcar corrigido',
      verify: 'Verificar e fechar',
      reopen: 'Reabrir',
    },
    list: {
      heading: 'Registros',
      titleCol: 'Título',
      areaCol: 'Área',
      severityCol: 'Severidade',
      statusCol: 'Status',
      dueCol: 'Prazo',
      ownerCol: 'Responsável',
      actionsCol: '',
      edit: 'Editar',
      delete: 'Excluir',
      deleteConfirm: (title: string) => `Excluir não conformidade "${title}"?`,
      deleteFailed: 'Não foi possível excluir. Tente novamente.',
      noDueDate: '—',
      overdueBadge: 'Vencida',
    },
    form: {
      heading: 'Nova não conformidade',
      editHeading: 'Editar não conformidade',
      titleLabel: 'Título',
      titlePlaceholder: 'Ex.: Desaprumo em parede do hall social',
      descriptionLabel: 'Descrição (opcional)',
      descriptionPlaceholder:
        'O que foi encontrado, referência da norma ou item do projeto.',
      areaLabel: 'Área / local',
      areaPlaceholder: 'Ex.: Fachada norte, suíte 203, instalação hidráulica',
      severityLabel: 'Severidade',
      dueDateLabel: 'Prazo (opcional)',
      dueDateHint: 'Data-alvo para a correção.',
      assigneeLabel: 'Responsável',
      noneOption: 'Nenhum',
      cancel: 'Cancelar',
      save: 'Salvar',
      saveFailed: 'Não foi possível salvar. Tente novamente.',
    },
    meta: {
      reportedBy: (name: string, when: string) =>
        `Aberta por ${name} · ${when}`,
      resolvedBy: (name: string, when: string) =>
        `Corrigida por ${name} · ${when}`,
      verifiedBy: (name: string, when: string) =>
        `Verificada por ${name} · ${when}`,
    },
  },
  wave31: {
    tabs: {
      fvs: 'Vistorias',
    },
    page: {
      title: 'Vistorias',
      subtitle:
        'Check-list preventivo por pacote de serviço. O inspetor preenche; um verificador assina. O que estiver não conforme vira uma não conformidade.',
      empty:
        'Nenhuma vistoria aberta. Escolha um modelo ou crie um check-list avulso.',
      addButton: 'Nova vistoria',
      manageTemplates: 'Modelos',
    },
    kpi: {
      total: 'Total',
      open: 'Em aberto',
      awaiting: 'Aguardando verificação',
      conformance: 'Conformidade',
      nonconformingSuffix: (n: number) =>
        n === 1 ? 'vistoria com não conformidade' : 'vistorias com não conformidade',
    },
    status: {
      aberta: 'Aberta',
      preenchida: 'Preenchida',
      verificada: 'Verificada',
    },
    statusNext: {
      submit: 'Enviar para verificação',
      verify: 'Verificar e fechar',
      reopen: 'Reabrir',
    },
    response: {
      conforme: 'Conforme',
      nao_conforme: 'Não conforme',
      na: 'N/A',
    },
    list: {
      heading: 'Vistorias do projeto',
      templateCol: 'Serviço / modelo',
      stageCol: 'Etapa',
      statusCol: 'Status',
      scheduledCol: 'Agendada',
      inspectorCol: 'Inspetor',
      verifierCol: 'Verificador',
      actionsCol: '',
      open: 'Abrir',
      edit: 'Editar',
      delete: 'Excluir',
      deleteConfirm: (title: string) => `Excluir a vistoria "${title}"?`,
      deleteFailed: 'Não foi possível excluir. Tente novamente.',
      nonconformityChip: 'Com não-conformidade',
      notScheduled: '—',
      noStage: '—',
    },
    form: {
      heading: 'Nova vistoria',
      editHeading: 'Editar vistoria',
      templateLabel: 'Modelo',
      templatePlaceholder: 'Escolha um modelo',
      templateHint: 'Ou preencha um check-list avulso abaixo.',
      inlineTemplateLabel: 'Título do serviço',
      inlineTemplateHint:
        'Ex.: Concretagem de pilares — 3º pavimento',
      inlineItemLabel: 'Item de verificação',
      addItem: 'Adicionar item',
      removeItem: 'Remover',
      stageLabel: 'Etapa (opcional)',
      stagePlaceholder: 'Vincular a uma etapa',
      noStage: 'Sem etapa',
      scheduledLabel: 'Data prevista (opcional)',
      notesLabel: 'Observações (opcional)',
      cancel: 'Cancelar',
      save: 'Salvar',
      saveFailed: 'Não foi possível salvar. Tente novamente.',
    },
    fill: {
      heading: 'Preenchimento',
      instructions:
        'Para cada item, marque Conforme, Não conforme ou N/A. Use a nota para registrar evidência, referência ou desvio.',
      itemHeader: (index: number, total: number) =>
        `Item ${index} de ${total}`,
      noteLabel: 'Observação',
      notePlaceholder: 'Registre evidência, medida ou referência.',
      save: 'Salvar rascunho',
      submit: 'Enviar para verificação',
      verify: 'Verificar e fechar',
      reopen: 'Reabrir',
      lockedNotice:
        'Vistoria travada. Reabra para editar respostas.',
      verifyGuardSelf:
        'O inspetor e o verificador precisam ser pessoas diferentes.',
      saveFailed: 'Não foi possível salvar. Tente novamente.',
    },
    templates: {
      title: 'Modelos de vistoria',
      subtitle:
        'Reutilize check-lists padrão para pacotes de serviço recorrentes.',
      empty: 'Nenhum modelo ainda — crie o primeiro.',
      createTitle: 'Novo modelo',
      editTitle: 'Editar modelo',
      nameLabel: 'Nome',
      namePlaceholder: 'Ex.: Alvenaria estrutural',
      descriptionLabel: 'Descrição (opcional)',
      descriptionPlaceholder: 'Quando aplicar este check-list.',
      itemsLabel: 'Itens de verificação',
      itemLabelPlaceholder: 'Texto do item (ex.: Prumo conferido)',
      itemDescriptionPlaceholder: 'Nota de apoio (opcional)',
      addItem: 'Adicionar item',
      removeItem: 'Remover',
      save: 'Salvar modelo',
      cancel: 'Cancelar',
      edit: 'Editar',
      delete: 'Excluir',
      deleteConfirm: (name: string) => `Excluir o modelo "${name}"?`,
      itemCount: (n: number) => (n === 1 ? '1 item' : `${n} itens`),
      saveFailed: 'Não foi possível salvar. Tente novamente.',
    },
    meta: {
      scheduledFor: (when: string) => `Previsto para ${when}`,
      inspectorSigned: (name: string, when: string) =>
        `Preenchida por ${name} · ${when}`,
      verifierSigned: (name: string, when: string) =>
        `Verificada por ${name} · ${when}`,
    },
  },
  ajuda: {
    // W123.4 · 2026-05-26 · labels do AjudaContextual.
    label: 'Ajuda',
    tela: 'O que é esta tela',
    proximaAcao: 'O que faço agora',
    glossario: 'Glossário',
    eyebrow: 'Ajuda',
    title: 'Como o Builder funciona',
    subtitle:
      'Cada módulo em uma linha. Toque pra abrir e ver perguntas frequentes.',
    searchPlaceholder: 'Buscar na ajuda…',
    noResults: 'Nenhum artigo encontrado.',
    pickArticle: 'Escolha um artigo à esquerda',
    pickArticleHint:
      'Ou use a busca para encontrar um tema específico.',
    hub: {
      modulesHeader: 'Módulos principais',
      modulesSubheader:
        'Os 10 módulos que você usa toda semana. Clique pra ver o que faz e como começa.',
      articlesHeader: 'Artigos detalhados',
      readMoreCta: 'Saiba mais',
      closeDrawer: 'Fechar',
      retakeTourCta: 'Refazer tour interativo',
      contactHeader: 'Precisa de ajuda direta?',
      contactBody:
        'Fala com a gente no WhatsApp. A equipe responde em até 2 horas em dias úteis.',
      contactWhatsappCta: 'Falar no WhatsApp',
      versionLabel: (version) => `Versão ${version}`,
      changelogLink: 'Ver novidades',
      modules: {
        trena: {
          title: 'Trena · orçamento inteligente',
          description:
            'Importa foto, PDF ou planta da obra. A IA extrai medidas e calcula o preço pela tabela oficial de preços (SINAPI) regional automaticamente.',
          faq:
            'Tipos aceitos: PDF de planta, foto de planta impressa, IFC ou Excel.\n' +
            'Cobertura da tabela de referência: 290 códigos brasileiros e multiplicador regional pros 27 UFs.\n' +
            'Stages do wizard: Importar · Revisar · Confirmar. Você sempre pode editar antes de commitar.',
        },
        budget: {
          title: 'Orçamento da obra',
          description:
            'Lista hierárquica de até 3 níveis: capítulo, sub-capítulo e sub-sub-capítulo. Edita inline. Exporta Excel.',
          faq:
            '4 tipos de obra: residencial popular, residencial médio, comercial loja, industrial galpão.\n' +
            'Sub-sub-capítulos: organize obras grandes em até 3 níveis (ex: Fundação > Sapatas > Aço CA-50).\n' +
            'Importação em massa: cole do Excel direto, o Builder faz match por descrição.',
        },
        rdo: {
          title: 'Diário de obra (RDO)',
          description:
            'Registro do dia com foto, equipe presente, atividades por etapa e clima. Vira PDF pra cliente.',
          faq:
            'Salva rascunho local: se a internet cair, o conteúdo volta.\n' +
            'Até 20 fotos por RDO, upload assíncrono.\n' +
            'PDF mensal: exporta tudo com fotos embutidas pra anexar à medição.',
        },
        fvs: {
          title: 'Vistorias · qualidade por etapa',
          description:
            'Checklist de verificação por serviço com foto de evidência. Pega o problema antes de virar retrabalho.',
          faq:
            '3 estados: Aberta · Preenchida · Verificada.\n' +
            'Item não conforme abre uma não conformidade automaticamente.\n' +
            'Template por tipo de obra (residencial, comercial, industrial) vem pré-carregado.',
        },
        compras: {
          title: 'Compras · do pedido ao pagamento',
          description:
            'Marca item do orçamento, abre cotação com fornecedor, gera o pedido de compra. Pagamento PIX, boleto ou transferência.',
          faq:
            'Fluxo: solicitação > cotação > pedido > recebimento > pagamento.\n' +
            'Aprovação por WhatsApp pra pedidos acima do limite (sem precisar logar).\n' +
            'NFe automática: cola o XML, o Builder casa com o pedido.',
        },
        chat: {
          title: 'Chat · mensagens instantâneas',
          description:
            'Balão de fala no topo. Conversa rápida com emoji e presence. Histórico de 7 dias.',
          faq:
            'Quando usar: dúvida rápida, alinhamento de hora, foto do canteiro.\n' +
            'Presence: você vê quem está online em tempo real.\n' +
            'Sem assunto, sem formal. Pra coisa séria use o Inbox.',
        },
        inbox: {
          title: 'Inbox · mensagens formais',
          description:
            'Envelope no topo. Tarefas, decisões, audit. Tem assunto, retenção permanente, sempre exportável.',
          faq:
            'Quando usar: aprovação formal, decisão técnica, comunicação com cliente.\n' +
            'Tudo fica em auditoria — útil pra rastrear quem decidiu o quê.\n' +
            'Diferente do chat: aqui o histórico nunca apaga.',
        },
        ownerReport: {
          title: 'Owner report · cliente fica por dentro',
          description:
            'PDF semanal pro cliente com fotos, % de avanço e fotos da semana. Sem custos, sem fornecedor.',
          faq:
            'Envio automático toda segunda 8h ou manual a qualquer momento.\n' +
            'Conteúdo: progresso por etapa, fotos públicas, marcos atingidos.\n' +
            'Cliente recebe por e-mail e pode abrir num link sem precisar logar.',
        },
        hoje: {
          title: 'Hoje · próximos passos',
          description:
            'As 3 ações mais importantes da sua obra agora. Cockpit pra começar o dia sem pensar.',
          faq:
            'O Builder olha tudo e prioriza: aprovação pendente, RDO atrasado, não conformidade crítica.\n' +
            'Cross-obras: mostra de todas as obras que você toca, ordem por urgência.\n' +
            'Acesso rápido pelo logo do Builder no topo.',
        },
        comando: {
          title: 'Comando · visão admin cross-obras',
          description:
            'Kanban operacional com todas as obras da sua carteira. Filtro por status, alerta, deadline.',
          faq:
            'Visível só pra admin. PMs usam o Hoje.\n' +
            'Consolida ~41 surfaces admin numa tela só, ação direta sem precisar navegar.\n' +
            'Drag-and-drop pra mudar prioridade ou status.',
        },
      },
    },
    fiveCards: {
      header: 'Como o Builder funciona em 2 minutos',
      subheader:
        'Os 5 conceitos centrais. Decora esses, o resto vai por dedução.',
      cta: 'Quero fazer isso agora',
      ctaAria: (title) => `Abrir ${title}`,
      cards: {
        obra: {
          title: 'Obra',
          concept: 'Sua obra física e seus dados.',
        },
        equipe: {
          title: 'Equipe',
          concept: 'Quem trabalha na obra.',
        },
        trena: {
          title: 'Trena',
          concept: 'Medir + orçar virou um wizard.',
        },
        compras: {
          title: 'Compras',
          concept: 'Pedir, cotar, comprar.',
        },
        qualidade: {
          title: 'Qualidade',
          concept: 'Vistorias e não conformidades garantem qualidade.',
        },
      },
    },
  },
  wave33: {
    page: {
      title: 'Aprovar cadastros',
      subtitle:
        'Novos usuários ficam pendentes até serem aprovados e atribuídos a pelo menos uma obra.',
      empty: 'Nenhum cadastro pendente no momento.',
      errorLoading: 'Não foi possível carregar a fila.',
      pendingChip: (count: number) =>
        count === 1 ? '1 pendente' : `${count} pendentes`,
    },
    columns: {
      user: 'Usuário',
      signedUp: 'Cadastrado',
      via: 'Via',
      actions: '',
    },
    via: {
      google: 'Google',
      email: 'E-mail',
    },
    approve: {
      button: 'Aprovar',
      dialogTitle: (name: string) => `Aprovar ${name}`,
      projectsLabel: 'Atribuir às obras',
      projectsHint:
        'Pelo menos uma obra. Pode adicionar quantas precisar — mesma obra duas vezes não, mas vários papéis em obras diferentes sim.',
      pickProject: 'Selecione uma obra…',
      scopeHint:
        'Você só pode atribuir o usuário a obras das quais você também participa.',
      role: 'Papel',
      addAnother: '+ Adicionar mais uma obra',
      remove: 'Remover',
      cancel: 'Cancelar',
      confirm: 'Aprovar e atribuir',
      saving: 'Salvando…',
      saveFailed: 'Não foi possível aprovar. Tente novamente.',
      outOfScope:
        'Essa obra não está na sua lista — peça a um administrador para aprovar.',
    },
    reject: {
      button: 'Rejeitar',
      confirm: (name: string) =>
        `Rejeitar o cadastro de ${name}? O acesso fica bloqueado.`,
      saveFailed: 'Não foi possível rejeitar. Tente novamente.',
    },
    roles: {
      project_manager: 'Gestor de obra',
      foreman: 'Mestre de obras',
      worker: 'Operário',
      client: 'Cliente',
      broker: 'Fornecedor', // W152 · unificado de "Corretor" → "Fornecedor"
      admin: 'Administrador',
    },
    login: {
      pendingTitle: 'Conta aguardando aprovação',
      pendingBody:
        'Seu cadastro foi recebido. Um administrador ou gestor de obra vai aprovar o acesso.',
    },
    register: {
      pendingTitle: 'Cadastro recebido!',
      pendingBody:
        'Sua conta está aguardando liberação. Um administrador ou gestor de obra vai aprovar o acesso e você receberá um e-mail assim que estiver tudo certo. Não precisa cadastrar de novo.',
    },
  },
  wave32: {
    nav: {
      executivo: 'Executivo',
    },
    page: {
      title: 'Relatório executivo',
      subtitle: 'Consolidação financeira, qualidade e risco de toda a carteira',
      generatedAt: (when: string) => `Atualizado em ${when}`,
      fromTo: (from: string, to: string) => `${from} até ${to}`,
      financialsRedacted:
        'Indicadores financeiros ocultos — seu perfil é somente leitura.',
      empty: 'Sem obras acessíveis para montar o relatório.',
      errorLoading: 'Não foi possível carregar o relatório.',
    },
    range: {
      last3: 'Últimos 3 meses',
      last6: 'Últimos 6 meses',
      last12: 'Últimos 12 meses',
      ytd: 'Ano corrente',
    },
    kpi: {
      projects: 'Obras',
      inProgress: 'Em execução',
      budget: 'Orçamento',
      committed: 'Comprometido',
      paid: 'Pago',
      pending: 'A pagar',
      receivables: 'A receber',
      rncOpen: 'Não conformidades em aberto',
      fvsConformance: 'Conformidade das vistorias',
      laborCost: 'Mão-de-obra',
      manDays: 'homens-dia',
    },
    burn: {
      heading: 'Curva de desembolso',
      subtitle: 'Pagamentos liquidados + mão-de-obra por mês',
      paid: 'Pagamentos',
      labor: 'Mão-de-obra',
      total: 'Total no período',
      empty: 'Sem lançamentos no período.',
    },
    suppliers: {
      heading: 'Concentração de fornecedores',
      subtitle: 'Top 10 — pedidos emitidos + recebidos',
      col: {
        supplier: 'Fornecedor',
        pos: 'Pedidos',
        total: 'Comprometido',
        share: '% do top 10',
      },
      empty: 'Sem pedidos no período.',
    },
    risk: {
      heading: 'Radar de risco',
      subtitle: 'Obras em risco primeiro, depois por comprometimento',
      col: {
        project: 'Obra',
        status: 'Status',
        budget: 'Orçamento',
        committed: 'Comprometido',
        overBudget: 'Desvio',
        schedule: 'Prazo',
        rnc: 'Não conformidades',
        issues: 'Problemas',
      },
      atRiskChip: 'Em risco',
      onTrack: 'OK',
      noBudget: 'Sem orçamento',
      noSchedule: '—',
      scheduleAhead: (points: number) => `${points} pts adiantado`,
      scheduleBehind: (points: number) => `${points} pts atrasado`,
      openProject: 'Abrir obra',
    },
  },
  evidenceViewer: {
    openViewer:       'Ver evidência no documento',
    close:            'Fechar visualizador',
    pageOf:           'Página {page} de {total}',
    prevPage:         'Página anterior',
    nextPage:         'Próxima página',
    prevEvidence:     'Evidência anterior',
    nextEvidence:     'Próxima evidência',
    loadingDoc:       'Carregando documento…',
    errorLoadingDoc:  'Não foi possível carregar o documento.',
    noDocStored:      'Documento original não disponível (extraído antes de Wave 392).',
    ifcNoElement:     'Não foi possível localizar este elemento no modelo 3D.',
    ifcFallbackTitle: 'Quantidades extraídas deste modelo',
    citationLabel:    'Trecho citado',
    pdfAriaLabel:     'Documento PDF · página {page} de {total}',
    ifcAriaLabel:     'Modelo 3D IFC',
    legacyBannerTitle:       'Evidência antiga detectada',
    legacyBannerCountOne:    '1 item usa evidência de antes do visualizador.',
    legacyBannerCountOther:  (count: number) => `${count} itens usam evidência de antes do visualizador.`,
    legacyBannerSubtitle:    'Reenvie o documento original para habilitar "Ver no documento" nesses itens.',
    legacyBannerActionLabel: 'Reenviar documento',
    panelEmpty:              'Selecione uma medição com evidência para ver o documento aqui.',
  },
  /** Slice 4 · Ready-to-commit yellow row acknowledgements (pt-BR). */
  readyToCommit: {
    yellowAckSectionTitle: 'Itens em amarelo · confirmação necessária',
    yellowAckCheckboxLabel: 'Confirmo que revisei este item amarelo',
    yellowAckHelp: 'Cada item amarelo precisa de confirmação antes do envio ao orçamento. Vermelhos continuam bloqueando o envio.',
  },
  wave43: {
    list: {
      title: 'Portal do Cliente',
      subtitle: 'Casas publicadas para venda em uma página pública.',
      new: 'Novo portal',
      empty: 'Nenhum portal criado ainda.',
      errorLoading: 'Não foi possível carregar os portais.',
      status: {
        draft: 'Rascunho',
        published: 'Publicada',
        sold: 'Vendida',
        archived: 'Arquivada',
      },
      columns: {
        title: 'Título',
        slug: 'URL',
        status: 'Status',
        location: 'Localização',
        price: 'Preço',
        updated: 'Atualizado',
      },
      openPublic: 'Abrir site',
    },
    create: {
      title: 'Novo portal',
      subtitle: 'Associe uma obra existente ao portal público. Demais campos são editados em seguida.',
      projectLabel: 'Obra',
      projectHint: 'A obra precisa ter `slug` preenchido.',
      submit: 'Criar portal',
      cancel: 'Cancelar',
      error: 'Não foi possível criar o portal.',
    },
    details: {
      backToList: 'Portal do Cliente',
      openPublic: 'Abrir site público',
      openPublicDisabled: 'Publique para abrir site',
      tabs: {
        general: 'Geral',
        photos: 'Fotos',
        documents: 'Documentos',
        leads: 'Interessados',
      },
    },
    general: {
      marketingHeading: 'Marketing',
      marketingHint: 'O que aparece no topo do site público.',
      locationHeading: 'Localização',
      featuresHeading: 'Características',
      statusHeading: 'Publicação',
      seoHeading: 'SEO',
      fields: {
        heroEyebrow: 'Linha de destaque (acima do título)',
        heroHeadline: 'Título principal',
        heroSubheadline: 'Frase de apoio',
        neighborhood: 'Bairro',
        city: 'Cidade',
        state: 'Estado (UF)',
        bedrooms: 'Dormitórios',
        bathrooms: 'Banheiros',
        parkingSpots: 'Vagas',
        areaSqm: 'Área construída (m²)',
        lotSqm: 'Terreno (m²)',
        amenities: 'Também conta com',
        amenitiesHint: 'Separe por vírgula (ex.: Piscina, Lareira, Deck)',
        listingPrice: 'Preço de venda (R$)',
        estimatedDelivery: 'Entrega estimada (AAAA-MM)',
        status: 'Status',
        seoTitle: 'Título SEO',
        seoDescription: 'Meta descrição',
      },
      save: 'Salvar alterações',
      saving: 'Salvando…',
      saved: 'Alterações salvas.',
      saveFailed: 'Não foi possível salvar.',
    },
    photos: {
      heading: 'Fotos publicadas',
      subtitle: 'Escolha quais fotos do RDO aparecem no Portal do Cliente. Tudo o mais continua privado.',
      empty: 'Ainda não há fotos nesta obra. Adicione pelo RDO.',
      publishedChip: 'Publicada',
      publishBtn: 'Publicar',
      unpublishBtn: 'Despublicar',
      uploadedOn: (when: string) => `Capturada em ${when}`,
    },
    documents: {
      heading: 'Pacote de documentos',
      subtitle: 'Documentos privados liberados por token a interessados aprovados, compradores e entrega.',
      checklist: {
        mandatoryMissing: (n: number) => `${n} obrigatório${n === 1 ? '' : 's'} faltando`,
        recommendedMissing: (n: number) => `${n} recomendado${n === 1 ? '' : 's'} faltando`,
        complete: 'Checklist completo',
      },
      empty: 'Nenhum documento adicionado ainda.',
      deleteConfirm: (title: string) => `Remover "${title}"? Compradores perdem o acesso.`,
      deleteBtn: 'Remover',
      tier: {
        mandatory: 'Obrigatório',
        recommended: 'Recomendado',
        optional: 'Opcional',
      },
      access: {
        public: 'Público',
        prospect: 'Interessado aprovado',
        buyer: 'Comprador',
      },
    },
    leads: {
      heading: 'Interessados',
      subtitle: 'Contatos recebidos pelo formulário público. Aprove para gerar um link de acesso.',
      pendingChip: (n: number) => `${n} pendente${n === 1 ? '' : 's'}`,
      showAll: 'Ver todos',
      showPending: 'Só pendentes',
      empty: 'Nenhum interessado ainda.',
      columns: {
        name: 'Nome',
        contact: 'Contato',
        message: 'Mensagem',
        created: 'Recebido',
        status: 'Status',
      },
      status: {
        pending: 'Pendente',
        approved: 'Aprovado',
        rejected: 'Rejeitado',
        expired: 'Expirado',
      },
      approveBtn: 'Aprovar',
      rejectBtn: 'Rejeitar',
      rejectConfirm: (name: string) => `Rejeitar o contato de ${name}? Eles não receberão acesso.`,
      approveDialog: {
        title: (name: string) => `Aprovar ${name}`,
        subtitle: 'Gere um link de acesso temporário aos documentos privados.',
        ttlLabel: 'Validade',
        ttlDaysOption: (n: number) => `${n} dia${n === 1 ? '' : 's'}`,
        labelLabel: 'Rótulo interno',
        labelPlaceholder: 'Opcional — ajuda a lembrar quem é',
        submit: 'Gerar link',
        cancel: 'Cancelar',
        tokenLabel: 'Link gerado',
        tokenHint: 'Copie e envie ao interessado — ele expira na validade escolhida.',
        copyBtn: 'Copiar link',
      },
    },
  },
  broker: {
    dashboardTitle: 'Painel do Fornecedor',
    openInvitations: 'Convites em aberto',
    activeBids: 'Lances ativos',
    winRate: 'Taxa de ganho',
    history: 'Histórico',
    noInvitations: 'Nenhum convite encontrado.',
    noBids: 'Nenhum lance registrado.',
    noHistory: 'Nenhum histórico de rodadas encerradas.',
    viewRound: 'Ver rodada',
    placeBid: 'Enviar lance',
    outcome: {
      won: 'Ganhou',
      lost: 'Não ganhou',
      cancelled: 'Cancelado',
    },
    myBest: 'Meu melhor preço',
    marketBest: 'Melhor do mercado',
    delta: 'Diferença',
    rank: 'Posição',
    totalWon: 'Total ganho',
    wonRounds: 'Rodadas ganhas',
    totalRounds: 'Total de rodadas',
    columns: {
      round: 'Rodada',
      project: 'Obra',
      status: 'Status',
      outcome: 'Resultado',
      myBid: 'Meu lance',
      winnerBid: 'Vencedor',
      delta: 'Δ',
      date: 'Encerrado em',
    },
  },
  trenaEditAudit: {
    sectionTitle: 'Histórico de edições',
    loading: 'Carregando histórico…',
    error: 'Erro ao carregar histórico.',
    empty: 'Sem revisões registradas.',
    colWhen: 'Quando',
    colEditor: 'Editor',
    colChange: 'Antes → Depois',
    colImpact: 'Δ Custo',
    colReason: 'Motivo',
    toggleAriaLabel: 'Histórico de edições',
    toggleLabel: 'Histórico',
  },
  adminTrenaStorage: {
    title: 'Armazenamento Trena · Documentos R2',
    objects: 'Documentos',
    totalSize: 'Tamanho total',
    oldest: 'Mais antigo',
    newest: 'Mais recente',
    orphans: '{n} órfãos não referenciados',
    truncated: 'Bucket excede limite; mostrando primeiro lote',
    refresh: 'Atualizar',
    empty: 'Bucket vazio',
  },
  trenaOcrFallback: {
    ocrBadge: 'Documento extraído via OCR · valores podem ter menor precisão',
    ocrProgress: (current: number, total: number) => `Extraindo via OCR · página ${current} de ${total}…`,
  },
  compositionsCompatEdit: {
    editAriaLabel: 'Editar chaves de compatibilidade',
    editFormTitle: 'Editar compatibilidade',
    requiredKeysLabel: 'Chaves obrigatórias (required)',
    blockedKeysLabel: 'Chaves bloqueadas (blocked)',
    addKeyPlaceholder: 'Buscar chave de medição…',
    reasonLabel: 'Motivo da edição',
    reasonPlaceholder: 'Opcional — informe o motivo desta alteração…',
    save: 'Salvar',
    cancel: 'Cancelar',
    saving: 'Salvando…',
    saveError: 'Erro ao salvar. Tente novamente.',
    invalidKeyError: (keys: string[]) =>
      `Chave(s) inválida(s): ${keys.join(', ')}. Use apenas chaves presentes em MEASURE_POLICIES.`,
    auditTitle: 'Histórico de alterações',
    auditLoading: 'Carregando histórico…',
    auditError: 'Erro ao carregar histórico.',
    auditEmpty: 'Sem alterações registradas.',
    auditRowSummary: (added: number, removed: number) =>
      `${added} adicionada(s), ${removed} removida(s)`,
    auditRowReason: 'Motivo:',
    auditToggle: 'Histórico',
  },
  trenaRowActions: {
    triggerAriaLabel: 'Ações da linha',
    reject: 'Rejeitar',
    restore: 'Restaurar',
    promoteToAssumption: 'Promover para premissa',
    promoteToMeasurement: 'Promover para medição',
    viewHistory: 'Ver histórico',
    rejectFormTitle: 'Motivo da rejeição',
    rejectReasonPlaceholder: 'Ex: valor inconsistente com o levantamento…',
    rejectReasonHint: (len: number) => `${len} / 5 mín.`,
    rejectConfirm: 'Confirmar rejeição',
    rejecting: 'Rejeitando…',
    restoring: 'Restaurando…',
    cancel: 'Cancelar',
    rejectedBadge: 'Rejeitado',
    promoteDialogTitleMeasToAssump: 'Promover medição → premissa',
    promoteDialogTitleAssumpToMeas: 'Promover premissa → medição',
    promoteDialogDescMeasToAssump:
      'A medição será removida e inserida como premissa com o mesmo valor. A operação é atômica.',
    promoteDialogDescAssumpToMeas:
      'A premissa será removida e inserida como medição com o valor e unidade que você informar.',
    promoteValueLabel: 'Valor *',
    promoteUnitLabel: 'Unidade *',
    promoteScopeLabel: 'Escopo *',
    promoteReasonLabel: 'Motivo',
    promoteReasonOptional: '(opcional)',
    promoteReasonPlaceholder: 'Ex: valor é uma premissa contratual, não uma medida de campo…',
    promoteConfirm: 'Confirmar promoção →',
    promoting: 'Promovendo…',
    promoteError: 'Erro ao promover.',
    rejectError: 'Erro ao rejeitar.',
    scopeProjeto: 'Projeto (escopo global)',
    scopeAmbiente: 'Ambiente (room-scoped)',
    scopeExterna: 'Externa (referência externa)',
  },
  projectTrenaOverview: {
    title: 'Trena · Estimativas',
    newEstimate: 'Nova estimativa',
    filterStatus: 'Status',
    statusAll: 'Todos',
    statusDraft: 'Rascunho',
    statusCalculated: 'Calculada',
    statusReviewed: 'Revisada',
    statusApproved: 'Aprovada',
    statusExported: 'Enviada',
    statusArchived: 'Arquivada',
    colName: 'Nome',
    colStatus: 'Status',
    colDist: 'Distribuição',
    colPendingOverrides: 'Pendentes',
    colTotal: 'Total',
    colLastEdit: 'Última edição',
    colApprovedAt: 'Aprovada em',
    emptyTitle: 'Nenhuma estimativa neste projeto.',
    emptyDescription: 'Comece uma nova estimativa para calcular o custo da obra.',
    notCommitted: '—',
    distTooltip: (dist) =>
      `✓ ${dist.safe_to_use} ok · ⚠ ${dist.needs_confirmation} revisão · ✗ ${dist.blocked} bloqueado · ✎ ${dist.manually_reviewed} revisado · ○ ${dist.legacy} legado`,
    autoRefresh: 'Atualiza a cada 30s',
    deleteAction: 'Apagar',
    deleteAriaLabel: (name: string) => `Apagar estimativa ${name}`,
  },
  deleteEstimateDialog: {
    title: 'Apagar estimativa?',
    body: (name: string) => `Você está prestes a apagar "${name}".`,
    warning:
      'Esta ação esconde a estimativa e seus itens. Não é recuperável pela interface — entre em contato com o suporte se precisar restaurar.',
    reasonLabel: 'Motivo',
    reasonOptional: '(opcional)',
    reasonPlaceholder: 'Ex: rascunho duplicado, dados incorretos…',
    cancel: 'Cancelar',
    confirm: 'Apagar',
    confirming: 'Apagando…',
  },
  multiDocChecks: {
    duplicateSectionTitle: (count) =>
      `Documentos divergem — ${count} medida${count === 1 ? '' : 's'} com valores inconsistentes`,
    duplicateSectionDesc:
      'Múltiplos documentos reportam a mesma medida com valores que diferem além da tolerância. Verifique e resolva antes de enviar ao orçamento.',
    mismatchWarnTitle: (count) =>
      `Fontes inconsistentes — ${count} divergência${count === 1 ? '' : 's'} entre memorial/planta/IFC`,
    mismatchWarnDesc:
      'Memorial descritivo, planta e/ou exportação IFC reportam valores diferentes para a mesma medida crítica. Confirme qual fonte é a correta.',
    mismatchBlockTitle: (count) =>
      `Fontes inconsistentes — ${count} divergência${count === 1 ? '' : 's'} extrema${count === 1 ? '' : 's'} entre fontes`,
    mismatchBlockDesc:
      'Divergência extrema (>25%) entre memorial, planta e/ou IFC. Necessário resolver antes de aprovar o orçamento.',
    goToConflict: 'Ir para o conflito de consolidação',
    spreadLabel: 'Divergência',
    toleranceLabel: 'Tolerância',
    docCountLabel: 'Docs',
  },
  obraHoje: {
    title: 'Obra Hoje',
    subtitle: 'Fila operacional do projeto',
    updatedAt: (time) => `Atualizado às ${time}`,
    refresh: 'Atualizar',
    nextActionHeading: 'Próxima ação recomendada',
    nextActionCta: 'Resolver agora',
    bucketApproveToday: 'Aprovar hoje',
    bucketMustBuy: 'Comprar agora',
    bucketMustReceive: 'Receber',
    bucketMustPay: 'Pagar',
    bucketLate: 'Atrasado',
    bucketBlocked: 'Bloqueado',
    bucketChanged: 'Mudou hoje',
    bucketBackground: 'Em observação',
    bucketOpenIssues: 'Problemas abertos',
    bucketBudgetApproval: 'Orçamento aguarda aprovação',
    seeAll: (n) => `Ver todos · ${n}`,
    emptyBucket: 'Nenhum item nesta categoria',
    severityCritical: 'Crítico',
    severityWarn: 'Atenção',
    severityInfo: 'Informativo',
    allClearTitle: 'Tudo em dia!',
    allClearSubtitle: 'Volte amanhã para conferir as próximas ações.',
    notStartedTitle: 'Sua obra está pronta pra começar',
    notStartedSubtitle:
      'Bate o martelo do início pra Builder começar a acompanhar RDOs, etapas e compras. Antes disso, nada de alerta de atraso.',
    notStartedCtaStart: 'Iniciar obra',
    notStartedCtaStages: 'Definir primeira etapa',
    completedTitle: 'Obra concluída',
    completedSubtitle: 'Histórico continua disponível, mas a fila operacional fica em silêncio.',
    archivedTitle: 'Obra arquivada',
    archivedSubtitle: 'Reative pelo menu de configurações para voltar a receber alertas.',
    startConfirmTitle: 'Iniciar a obra agora?',
    startConfirmBody:
      'Builder vai registrar a data de hoje como início, mudar o status pra "em andamento" e começar a monitorar RDOs e prazos.',
    startConfirmConfirm: 'Sim, bater o martelo',
    startConfirmCancel: 'Ainda não',
    loading: 'Carregando fila da obra…',
    errorLoading: 'Não foi possível carregar a fila da obra.',
    deleteDemoCta: 'Apagar obra de exemplo',
    deleteDemoConfirmTitle: 'Apagar a obra de exemplo?',
    deleteDemoConfirmBody:
      'Vamos remover essa obra fictícia e tudo dentro dela (RDOs, compras, ' +
      'orçamento, não conformidades). Nenhuma obra real é afetada. Pode seedar de novo ' +
      'depois pelo onboarding.',
    deleteDemoConfirmConfirm: 'Sim, apagar',
    deleteDemoConfirmCancel: 'Cancelar',
    deleteDemoLoading: 'Apagando obra de exemplo…',
    deleteDemoError: 'Não consegui apagar agora. Tenta de novo.',
  },
  obraCockpit: {
    blockHoje: 'Agora',
    blockComprar: 'Comprar',
    blockReceber: 'Receber',
    blockPagar: 'Pagar',
    blockTravado: 'Travado',
    blockProxima7d: 'Próximos 7 dias',
    hojeSubtitle: (n) => n === 1 ? '1 ação urgente' : `${n} ações urgentes`,
    comprarSubtitle: (n) => n === 1 ? '1 item sem pedido' : `${n} itens sem pedido`,
    receberSubtitle: (n) => n === 1 ? '1 entrega pendente' : `${n} entregas pendentes`,
    pagarSubtitle: (n) => n === 1 ? '1 pagamento a vencer' : `${n} pagamentos a vencer`,
    travadoSubtitle: (n) => n === 1 ? '1 item bloqueado' : `${n} itens bloqueados`,
    proxima7dSubtitle: (n) => n === 1 ? '1 item programado' : `${n} itens programados`,
    hojeEmpty: 'Nenhuma ação urgente no momento.',
    proxima7dEmpty: 'Nada programado pra esta semana.',
    allClearTitle: 'Obra em dia',
    allClearDescription: 'Sem ações pendentes pra você agora. A próxima ação provável é fazer o RDO de hoje.',
    allClearCta: 'Criar RDO de hoje',
    feedsInto: 'alimenta o cronograma e os relatórios',
    errorTitle: 'Não foi possível carregar o cockpit da obra.',
    errorDescription: 'Verifica a internet e tenta de novo.',
    retry: 'Tentar novamente',
    refreshLabel: 'Atualizar cockpit',
    loadingLabel: 'Carregando cockpit',
  },
  trenaQtyPendencies: {
    bannerTitle: (count) => `Pendências de quantidade (${count})`,
    removeAll: 'Remover todas sem quantidade',
    bannerDesc:
      'Preencha a quantidade ou remova o item. Calcular ficará disponível quando todas estiverem resolvidas.',
    calcularBlockedTooltip: (count) =>
      `Resolva ${count} pendência${count === 1 ? '' : 's'} de quantidade primeiro.`,
    calcularBadge: (count) =>
      `Calcular (${count} pendência${count === 1 ? '' : 's'})`,
    pendencyBadge: (count) =>
      `${count} pendência${count === 1 ? '' : 's'} de quantidade`,
    removeItem: 'Remover',
    skippedNotice: (count) =>
      `${count} item${count === 1 ? ' foi pulado' : 's foram pulados'} por falta de quantidade.`,
  },
  trenaWizard: {
    stage1Label: 'Importar',
    stage2Label: 'Revisar & Calcular',
    stage3Label: 'Confirmar',
    saveDraft: 'Salvar rascunho',
    cancel: 'Cancelar',
    saveDraftAction: 'Salvar como rascunho',
    deleteAction: 'Apagar definitivamente',
    backToWizard: 'Voltar ao wizard',
    advanceToConfirm: 'Avançar para confirmar →',
    stage1Title: 'Nome desta estimativa',
    stage3Title: 'Pronto para enviar ao orçamento?',
    stage3SendCta: 'Enviar ao orçamento',
    stage3GoBack: '← Voltar e ajustar',
    doneTitle: 'Orçamento atualizado!',
    doneTotalLabel: 'Total estimado',
    doneViewBudget: 'Ver no Orçamento →',
    doneCreateAnother: 'Criar outra estimativa',
    deleteSuccessToast: 'Estimativa apagada',
  },
  trenaPmQuestions: {
    panelTitle: 'Perguntas para você responder',
    pendingHeader: 'Pendentes — precisam de resposta',
    answeredHeader: 'Já respondidas',
    didNotUnderstandHeader: 'Trechos que não consegui entender',
    emptyState: 'Tudo respondido! Pronto pra calcular o orçamento.',
    saveButton: 'Salvar resposta',
    skipButton: 'Pular (usar padrão)',
    changeAnswer: 'Mudar resposta',
    dismissDidNotUnderstand: 'Resolvi manual, descartar',
    reExtractButton: 'Re-extrair com minhas respostas',
    reExtractRunning: 'Re-extraindo…',
    reExtractError: 'Erro ao re-extrair. Tente novamente.',
    reExtractSuccess: 'Re-extração concluída. Revise os resultados.',
    reExtractNoSourceDoc: 'Não achei o PDF original. Faça upload novamente — suas respostas serão usadas automaticamente.',
    blockedCalcMessage: 'Responda as perguntas acima antes de avançar.',
    unblockableMessage: (count: number) =>
      `${count} pergunta${count === 1 ? '' : 's'} será${count === 1 ? '' : 'ão'} respondida${count === 1 ? '' : 's'} com o valor padrão — pode causar imprecisão no orçamento.`,
    otherOption: 'Outro (digite)',
    otherPlaceholder: 'Sua resposta',
    saving: 'Salvando…',
    loading: 'Carregando perguntas…',
    loadError: 'Não conseguimos carregar as perguntas.',
    catSistemaConstrutivo: 'Sistema construtivo',
    catAcabamento: 'Acabamento',
    catEstrutura: 'Estrutura',
    catInstalacao: 'Instalação',
    catAmbiente: 'Ambiente',
    catOutro: 'Outro',
    kindTabelaComplexa: 'Tabela complexa',
    kindAmbiguidade: 'Ambiguidade',
    kindIlegivel: 'Ilegível',
    kindForaDeEscopo: 'Fora de escopo',
    kindValorSuspeito: 'Valor suspeito',
    kindOutro: 'Outro',
    fillDataButton: 'Preencher dados',
    fillDataPlaceholder: 'Descreva os dados em texto livre (ex: 12 degraus, 1.20m largura, piso granito, corrimão aço inox)',
    fillDataSave: 'Salvar resposta',
    fillDataCancel: 'Cancelar',
    fillDataSaved: 'Respondido em',
    fillDataChangeAnswer: 'Mudar resposta',
    fillDataSavedShort: '✓ Resposta salva',
    saveError: 'Erro ao salvar. Tente novamente.',
    savedFlash: 'Salvo',
    customizarPorAmbiente: (count: number) => `Customizar por ambiente (${count})`,
    customizarPorAmbienteHeader: 'Personalizar por ambiente',
    emptyStateCta: 'Ver orçamento detalhado',
  },
  comprasExcelImport: {
    importBtn: 'Importar Excel',
    exportBtn: 'Exportar Excel',
    pageTitle: 'Importar planilha de Compras',
    uploadStep: '1. Upload',
    mappingStep: '2. Colunas',
    diffStep: '3. Revisão',
    confirmStep: '4. Confirmar',
    dropzone: 'Arraste o arquivo .xlsx aqui',
    or: 'ou',
    browse: 'selecione no computador',
    acceptedFormats: 'Aceita .xlsx (Excel 2007+)',
    analyzing: 'Analisando planilha…',
    columnHeader: 'Coluna na planilha',
    fieldLabel: 'Campo de Compras',
    confidence: 'Confiança',
    ignore: 'Ignorar',
    addsTitle: (n: number) => `Adicionar ${n} linha(s)`,
    updatesTitle: (n: number) => `Atualizar ${n} linha(s)`,
    conflictsTitle: (n: number) => `${n} conflito(s)`,
    conflictNote: 'Estas linhas não serão importadas — o total existente difere do calculado. Resolva manualmente.',
    applyBtn: (n: number) => `Aplicar ${n} mudança(s)`,
    applying: 'Aplicando…',
    successToast: (adds: number, updates: number) =>
      `Compras atualizadas: +${adds} adicionados, ${updates} atualizados.`,
    cancel: 'Cancelar',
    back: 'Voltar',
    next: 'Próximo',
    noChanges: 'Nenhuma mudança detectada. O arquivo está sincronizado com as compras atuais.',
    previewRows: 'Pré-visualização (primeiras 20 linhas)',
    sheetInfo: (sheet: string, rows: number) => `Planilha "${sheet}" · ${rows} linha(s) de dados`,
    mappingHint: 'Confira o mapeamento de colunas. Use "Ignorar" para excluir uma coluna da importação.',
    warnings: 'Avisos',
  },
  cronogramaExcelImport: {
    importBtn: 'Importar Excel',
    exportBtn: 'Exportar Excel',
    pageTitle: 'Importar planilha de Cronograma',
    uploadStep: '1. Upload',
    mappingStep: '2. Colunas',
    diffStep: '3. Revisão',
    confirmStep: '4. Confirmar',
    dropzone: 'Arraste o arquivo .xlsx aqui',
    or: 'ou',
    browse: 'selecione no computador',
    acceptedFormats: 'Aceita .xlsx (Excel 2007+)',
    analyzing: 'Analisando planilha…',
    columnHeader: 'Coluna na planilha',
    fieldLabel: 'Campo do Cronograma',
    confidence: 'Confiança',
    ignore: 'Ignorar',
    addsTitle: (n: number) => `Adicionar ${n} etapa(s)`,
    updatesTitle: (n: number) => `Atualizar ${n} etapa(s)`,
    conflictsTitle: (n: number) => `${n} conflito(s)`,
    conflictNote: 'Estas etapas não serão importadas — conflito de status. Resolva manualmente.',
    applyBtn: (n: number) => `Aplicar ${n} mudança(s)`,
    applying: 'Aplicando…',
    successToast: (adds: number, updates: number) =>
      `Cronograma atualizado: +${adds} adicionadas, ${updates} atualizadas.`,
    cancel: 'Cancelar',
    back: 'Voltar',
    next: 'Próximo',
    noChanges: 'Nenhuma mudança detectada. O arquivo está sincronizado com o cronograma atual.',
    previewRows: 'Pré-visualização (primeiras 20 linhas)',
    sheetInfo: (sheet: string, rows: number) => `Planilha "${sheet}" · ${rows} linha(s) de dados`,
    mappingHint: 'Confira o mapeamento de colunas. Use "Ignorar" para excluir uma coluna da importação.',
    warnings: 'Avisos',
  },
  fornecedoresExcelImport: {
    importBtn: 'Importar Excel',
    exportBtn: 'Exportar Excel',
    pageTitle: 'Importar planilha de Fornecedores',
    uploadStep: '1. Upload',
    mappingStep: '2. Colunas',
    diffStep: '3. Revisão',
    confirmStep: '4. Confirmar',
    dropzone: 'Arraste o arquivo .xlsx aqui',
    or: 'ou',
    browse: 'selecione no computador',
    acceptedFormats: 'Aceita .xlsx (Excel 2007+)',
    analyzing: 'Analisando planilha…',
    columnHeader: 'Coluna na planilha',
    fieldLabel: 'Campo de Fornecedor',
    confidence: 'Confiança',
    ignore: 'Ignorar',
    addsTitle: (n: number) => `Adicionar ${n} fornecedor(es)`,
    updatesTitle: (n: number) => `Atualizar ${n} fornecedor(es)`,
    conflictsTitle: (n: number) => `${n} conflito(s)`,
    conflictNote: 'Estes fornecedores não serão importados — CNPJ já cadastrado com outro nome. Edite manualmente.',
    applyBtn: (n: number) => `Aplicar ${n} mudança(s)`,
    applying: 'Aplicando…',
    successToast: (adds: number, updates: number) =>
      `Fornecedores atualizados: +${adds} adicionados, ${updates} atualizados.`,
    cancel: 'Cancelar',
    back: 'Voltar',
    next: 'Próximo',
    noChanges: 'Nenhuma mudança detectada. O arquivo está sincronizado com o cadastro atual.',
    previewRows: 'Pré-visualização (primeiras 20 linhas)',
    sheetInfo: (sheet: string, rows: number) => `Planilha "${sheet}" · ${rows} linha(s) de dados`,
    mappingHint: 'Confira o mapeamento de colunas. Use "Ignorar" para excluir uma coluna da importação.',
    warnings: 'Avisos',
  },
  medicoesExcelImport: {
    importBtn: 'Importar Excel',
    exportBtn: 'Exportar Excel',
    pageTitle: 'Importar planilha de Medições',
    uploadStep: '1. Upload',
    mappingStep: '2. Colunas',
    diffStep: '3. Revisão',
    confirmStep: '4. Confirmar',
    dropzone: 'Arraste o arquivo .xlsx aqui',
    or: 'ou',
    browse: 'selecione no computador',
    acceptedFormats: 'Aceita .xlsx (Excel 2007+)',
    analyzing: 'Analisando planilha…',
    columnHeader: 'Coluna na planilha',
    fieldLabel: 'Campo da Medição',
    confidence: 'Confiança',
    ignore: 'Ignorar',
    addsTitle: (n: number) => `Adicionar ${n} medição(ões)`,
    updatesTitle: (n: number) => `Atualizar ${n} medição(ões)`,
    conflictsTitle: (n: number) => `${n} conflito(s)`,
    conflictNote: 'Estas medições não serão importadas — status não permite sobrescrita. Resolva manualmente.',
    applyBtn: (n: number) => `Aplicar ${n} mudança(s)`,
    applying: 'Aplicando…',
    successToast: (adds: number, updates: number) =>
      `Medições atualizadas: +${adds} adicionadas, ${updates} atualizadas.`,
    cancel: 'Cancelar',
    back: 'Voltar',
    next: 'Próximo',
    noChanges: 'Nenhuma mudança detectada. O arquivo está sincronizado com as medições atuais.',
    previewRows: 'Pré-visualização (primeiras 20 linhas)',
    sheetInfo: (sheet: string, rows: number) => `Planilha "${sheet}" · ${rows} linha(s) de dados`,
    mappingHint: 'Confira o mapeamento de colunas. Use "Ignorar" para excluir uma coluna da importação.',
    warnings: 'Avisos',
  },
  excelImportShell: {
    uploadStep: '1. Upload',
    previewStep: '2. Preview',
    diffStep: '3. Diff',
    confirmStep: '4. Confirmar',
    dropzone: 'Arraste o arquivo .xlsx ou .csv aqui',
    or: 'ou',
    browse: 'selecione no computador',
    acceptedFormats: 'Aceita .xlsx (Excel 2007+) ou .csv',
    analyzing: 'Analisando…',
    pasteToggle: 'Colar tabela do Excel',
    pasteToggleClose: 'Fechar área de cola',
    pasteLabel: 'Cole aqui os dados copiados do Excel ou Google Sheets:',
    pastePlaceholder: 'Coluna1\tColuna2\tColuna3\nValor1\tValor2\tValor3',
    pasteCta: 'Importar dados colados',
    exportCurrentTitle: 'Baixar estado atual',
    exportCurrentDesc: 'Exporte o que está no sistema agora antes de importar.',
    exportCurrentBtn: 'Baixar Excel',
    cancel: 'Cancelar',
    back: 'Voltar',
    next: 'Continuar',
    applying: 'Aplicando…',
    applied: 'Aplicado com sucesso',
    undoAvailable: (s: number) => `Desfazer disponível por ${s}s`,
    undoBtn: 'Desfazer',
    undoing: 'Desfazendo…',
    undone: 'Importação desfeita com sucesso.',
    importAnother: 'Importar outro arquivo',
    close: 'Fechar',
    noChanges: 'Nenhuma mudança detectada. O arquivo está sincronizado com o estado atual.',
    errorsInFile: (n: number) => `${n} erro(s) de parsing`,
    fixFirst: (n: number) => `${n} erro(s) — corrija primeiro`,
    applyChanges: (n: number) => `Aplicar ${n} mudança(s)`,
    added: 'Adicionados',
    updated: 'Atualizados',
    deleted: 'Removidos',
    skipped: 'Ignorados',
    errors: 'Erros',
    parseErrors: (n: number) => `Erros de parsing (${n})`,
  },
  verbaExcelImport: {
    pageTitle: 'Importar Verba Mensal',
    feedsInto: 'alimenta orçamento e curva ABC',
    importBtn: 'Importar Excel',
    exportBtn: 'Exportar Excel',
  },
  estoqueExcelImport: {
    pageTitle: 'Importar Estoque',
    feedsInto: 'alimenta relatórios de insumos e movimentações',
    importBtn: 'Importar Excel',
    exportBtn: 'Exportar Excel',
  },
  rdosExcelImport: {
    pageTitle: 'Importar RDOs históricos',
    feedsInto: 'útil para migrar histórico de obras anteriores',
    importBtn: 'Importar Excel',
    exportBtn: 'Exportar Excel',
  },
  pranchasExcelImport: {
    pageTitle: 'Importar Pranchas em lote',
    feedsInto: 'cadastra pranchas sem fazer upload dos PDFs',
    importBtn: 'Importar Excel',
    exportBtn: 'Exportar Excel',
  },
  trenaExcelImport: {
    pageTitle: 'Importar Medições da Trena',
    feedsInto: 'alimenta os ambientes e orçamentos da Trena',
    importBtn: 'Importar Excel',
    exportBtn: 'Exportar Excel',
  },
  emptyStatesLlll: {
    compras: {
      title: 'Comece pelo primeiro pedido',
      description: 'Crie um pedido de compra ou importe seu pipeline atual.',
      primaryCta: '+ Novo pedido',
      importExcel: 'Importar Excel',
      viewExample: 'Ver exemplo',
    },
    orcamento: {
      title: 'Importe seu orçamento ou crie do zero',
      description: 'Excel pronto? Importe em segundos. Sem orçamento? Comece com um template.',
      primaryCta: '+ Adicionar item',
      importExcel: 'Importar Excel',
      templateResidential: 'Template residencial',
      templateCommercial: 'Template comercial',
    },
    cronograma: {
      title: 'Planeje as etapas da obra',
      description: 'Crie etapas, marcos e dependências. Importe de Excel ou comece com um template.',
      primaryCta: '+ Nova etapa',
      templateResidential: 'Template residencial',
      importExcel: 'Importar Excel',
    },
    fornecedores: {
      title: 'Cadastre seu primeiro fornecedor',
      description: 'Pre-cadastre fornecedores ou importe sua lista atual de Excel.',
      primaryCta: '+ Adicionar fornecedor',
      importExcel: 'Importar Excel',
    },
    diarioObra: {
      title: 'Comece o diário de hoje',
      description: 'Registre clima, equipe, atividades e fotos. Leva 1 minuto.',
      primaryCta: '+ Lançar RDO de hoje',
      viewExample: 'Ver exemplo de RDO',
    },
    qualidade: {
      title: 'Acompanhe a qualidade da obra',
      description: 'Crie checklists de vistoria, registre não conformidades e mantenha o histórico.',
      primaryCta: '+ Nova vistoria',
      templatesFvs: 'Modelos de vistoria',
      viewGuide: 'Ver guia',
    },
  },
  inlineTutorials: {
    budget: {
      title: 'Comece pelo orçamento da obra',
      subtitle: 'O orçamento é a base. Tudo na obra (compras, medições, financeiro) puxa daqui.',
      step1Title: 'Importe via Trena',
      step1Description: 'Foto da planta, PDF do projeto ou digite manual. A Trena estima quantidades por você.',
      step2Title: 'Revise quantidades e preços',
      step2Description: 'Ajuste o que precisar. Você decide o que entra e quanto cada item vale.',
      step3Title: 'Aprovado vira fonte de verdade',
      step3Description: 'Daqui, compras puxam itens, medições comparam, financeiro acompanha gasto.',
      primaryCta: 'Importar com Trena',
      secondaryCta: 'Adicionar item manual',
    },
    rdo: {
      title: 'Registre o dia da obra',
      subtitle: 'RDO (Relatório Diário de Obra) documenta o que aconteceu: clima, equipe, atividades e fotos.',
      step1Title: 'Tire fotos do canteiro',
      step1Description: 'Frente de serviço, problemas, entregas. O que o cliente perguntar amanhã, você responde com foto.',
      step2Title: 'Liste quem trabalhou',
      step2Description: 'Equipe presente e as atividades do dia. Tudo num lugar só, sem WhatsApp.',
      step3Title: 'Marque clima e ocorrências',
      step3Description: 'Chuva, parada, acidente. Vira histórico oficial pra justificar prazo e medição.',
      primaryCta: 'Criar RDO de hoje',
      secondaryCta: 'Ver exemplo',
    },
    compras: {
      title: 'Compre o que a obra precisa',
      subtitle: 'Compras puxa do orçamento aprovado. Cote com 3+ fornecedores, escolha e gere o pedido de compra.',
      step1Title: 'Marque itens para cotar',
      step1Description: 'No orçamento, marque os itens que precisam de compra. Eles aparecem na lista de itens para cotar aqui.',
      step2Title: 'Envie cotação por email',
      step2Description: 'Builder dispara email pros fornecedores. Cada um responde com proposta sem precisar de conta.',
      step3Title: 'Compare e gere o pedido',
      step3Description: 'Veja propostas lado a lado, escolha o vencedor de cada linha. O pedido nasce sozinho.',
      primaryCta: 'Ir ao orçamento',
      secondaryCta: 'Cadastrar fornecedor',
    },
    fvs: {
      title: 'Verifique a qualidade',
      subtitle: 'A vistoria (Ficha de Verificação de Serviço) checa se cada etapa cumpriu os padrões antes de seguir.',
      step1Title: 'Escolha um template',
      step1Description: 'Estrutura, alvenaria, instalações. Cada etapa tem checklist próprio com os pontos críticos.',
      step2Title: 'Marque itens conferidos',
      step2Description: 'No canteiro, marque o que está OK e o que ficou pendente. Builder calcula conformidade.',
      step3Title: 'Foto e assinatura',
      step3Description: 'Evidência fotográfica + assinatura do responsável. Pronto pra auditoria do cliente.',
      primaryCta: 'Nova inspeção',
      secondaryCta: 'Ver templates',
    },
  },
  prumoQueue: {
    bucketApproveToday: 'Aprovar hoje',
    bucketBlocked: 'Bloqueados',
    bucketFyi: 'Para saber',
    bucketBackgroundHealth: 'Saúde da obra',
    fazerAgora: 'Fazer agora',
    porQueExpand: 'Por que esta sugestão?',
    porQueCollapse: 'Esconder detalhes',
    techDetails: 'Ver detalhes técnicos',
    approveBtn: 'Aprovar',
    declineBtn: 'Recusar',
    declineConfirmBtn: 'Confirmar recusa',
    declineCancelBtn: 'Cancelar',
    declineNotePlaceholder: 'Por que recusou? (opcional)',
    resolverBtn: 'Resolver',
  },
  notificationsBell: {
    title: 'Notificações',
    openBell: 'Abrir notificações',
    empty: 'Nenhuma notificação por aqui',
    markAllRead: 'Marcar todas como lidas',
    loading: 'Carregando…',
    unread: 'Não lida',
  },
  pranchas: {
    pageTitle: 'Pranchas',
    noDrawings: 'Nenhuma prancha cadastrada',
    noDrawingsDesc: 'Cadastre uma prancha acima para começar.',
    filterByDiscipline: 'Filtrar disciplina',
    allDisciplines: 'Todas as disciplinas',
    addDrawing: 'Cadastrar prancha',
    code: 'Código',
    name: 'Nome',
    discipline: 'Disciplina',
    revisionLabel: 'Revisão',
    revisionLabelPlaceholder: 'V1, R00, Rev A…',
    uploadPdf: 'Selecionar PDF',
    uploadRevision: 'Enviar revisão',
    uploadingRevision: 'Enviando…',
    revisionUploaded: 'Revisão enviada com sucesso.',
    noRevision: 'Sem revisão',
    revisionsTimeline: 'Histórico de revisões',
    currentRevision: 'Revisão atual',
    markupTools: 'Ferramentas de markup',
    toolBox: 'Retângulo',
    toolCircle: 'Círculo',
    toolArrow: 'Seta',
    toolText: 'Texto',
    toolSelect: 'Selecionar',
    markupLabelPlaceholder: 'Anotação…',
    anchorRfi: 'Criar RFI aqui',
    anchorRfiTitle: 'Ancorar RFI nesta coordenada',
    rfiPinTooltip: 'Ver RFI',
    rfiDetails: 'Detalhes do RFI',
    decisionHistory: 'Histórico de decisão',
    noAnchors: 'Nenhum RFI ancorado nesta revisão.',
    loadingPdf: 'Carregando PDF…',
    pdfError: 'Erro ao carregar o PDF.',
    pageOf: (current: number, total: number) => `Página ${current} de ${total}`,
    prevPage: 'Anterior',
    nextPage: 'Próxima',
    noMarkups: 'Nenhum markup nesta página.',
    notes: 'Notas',
    notesPlaceholder: 'Observações sobre esta revisão…',
    disciplines: {
      arquitetura: 'Arquitetura',
      estrutural: 'Estrutural',
      hidraulica: 'Hidráulica',
      eletrica: 'Elétrica',
      gas: 'Gás',
      spda: 'SPDA',
      prevenivel: 'Prevenível',
      outros: 'Outros',
    },
    dwgStub: {
      title: 'Builder ainda não converte DWG automaticamente',
      description: 'Por enquanto, exporte como PDF do AutoCAD antes de fazer upload:',
      steps: [
        'Abra o arquivo DWG no AutoCAD',
        'Vá em Arquivo → Plotar (Ctrl+P)',
        'Selecione a impressora DWG to PDF.pc3',
        'Clique em OK e salve o PDF',
      ],
      howToExportLink: 'https://knowledge.autodesk.com/support/autocad/learn-explore/caas/sfdcarticles/sfdcarticles/How-to-create-a-PDF-from-AutoCAD.html',
      howToExportLabel: 'Como exportar PDF do AutoCAD',
      confirmBtn: 'Tudo bem, vou exportar',
    },
  },

  adminHome: {
    pageTitle: 'Painel Admin',
    pageSubtitle: 'Visão geral de todas as superfícies administrativas.',
    refresh: 'Atualizar',
    loading: 'Carregando contagens…',
    error: 'Erro ao carregar o painel. Tente novamente.',
    tiles: {
      overrides: {
        title: 'Overrides Trena',
        descriptor: 'esta semana',
        zero: '0 esta semana',
      },
      storage: {
        title: 'Armazenamento R2',
        descriptor: 'objetos',
        zero: 'Bucket vazio',
      },
      compositions: {
        title: 'Composições',
        descriptor: 'total',
        compatSuffix: 'com compat',
        zero: 'Nenhuma composição',
      },
      followUps: {
        title: 'Follow-ups',
        descriptor: 'hoje',
        kindsSuffix: 'tipos',
        zero: 'Nenhum hoje',
      },
      auctions: {
        title: 'Leilões',
        descriptor: 'ativos',
        zero: 'Nenhum ativo',
      },
      users: {
        title: 'Usuários',
        descriptor: 'ativos (7 dias)',
        zero: 'Nenhum ativo',
      },
    },
  },

  projectActivity: {
    pageTitle: 'Atividade da obra',
    pageSubtitle: 'Histórico cronológico de tudo que aconteceu nesta obra.',
    widgetTitle: 'Atividade recente',
    widgetViewAll: 'Ver todo o histórico',
    loadFailed: 'Não foi possível carregar o histórico de atividade.',
    empty: 'Nenhuma atividade registrada no período.',
    emptyFilter: 'Nenhuma atividade deste tipo no período.',
    loadMore: 'Carregar mais',
    refreshLabel: 'Atualizar',
    filterAll: 'Tudo',
    kindLabels: {
      rdo_created: 'RDO criado',
      po_created: 'Pedido criado',
      po_received: 'Recebimento',
      estimate_edited: 'Medição editada',
      estimate_committed: 'Medição confirmada',
      fvs_passed: 'Vistoria aprovada',
      fvs_failed: 'Vistoria reprovada',
      rfi_opened: 'RFI aberto',
      rfi_answered: 'RFI respondido',
      rnc_created: 'Não conformidade criada',
      photo_uploaded: 'Foto enviada',
      override_committed: 'Aditivo submetido',
      doc_uploaded: 'Documento enviado',
      stage_completed: 'Etapa concluída',
      budget_changed: 'Orçamento alterado',
    },
    actorSystem: 'Sistema',
    today: 'Hoje',
    yesterday: 'Ontem',
    // Wave merge-activity-audit · 2026-05-21 · Tabs (Eventos vs Trilha técnica).
    tabEvents: 'Eventos da obra',
    tabAudit: 'Trilha técnica',
    tabsAriaLabel: 'Visões do histórico',
  },

  projectSwitcher: {
    placeholder: 'Selecione um projeto',
    triggerAriaLabel: 'Trocar projeto',
    searchPlaceholder: 'Buscar obras...',
    noResults: 'Nenhuma obra encontrada.',
    viewAll: 'Ver todos os projetos',
  },

  // Wave A · Mobile resilience pass.
  mobileResilient: {
    errorTitle: 'Não consegui carregar agora',
    errorDescription: 'Verifica a internet e tenta de novo.',
    retryLabel: 'Tentar novamente',
    showDetails: 'Mostrar detalhes técnicos',
    offlineLabel: 'Sem conexão',
    offlineShowingSaved: 'mostrando dados salvos',
    offlineReconnecting: 'tentando reconectar',
    timedOutTitle: 'Está demorando mais que o normal',
    timedOutDescription: 'Pode ser a conexão. Quer tentar de novo?',
  },

  // Wave B · EmptyState system.
  emptyState: {
    projects: {
      title: 'Cadastre sua primeira obra',
      description: 'Vamos centralizar diários, fotos, RDOs, orçamento e compras em um só lugar.',
      primaryAction: 'Criar obra',
      feedsInto: 'o RDO diário, a Curva ABC e os relatórios para o cliente',
    },
    fvs: {
      title: 'Nenhuma vistoria ainda',
      description: 'A vistoria é o checklist de qualidade por serviço. Use modelos prontos (alvenaria, instalações, fachada).',
      primaryAction: 'Usar modelo NBR',
      secondaryCreate: 'Criar do zero',
      feedsInto: 'o relatório de qualidade da obra',
    },
    verba: {
      title: 'Configure as verbas da obra',
      description: 'Defina os limites por categoria. O orçamento e as compras vão usar essas verbas.',
      primaryAction: 'Configurar verba',
      secondaryImport: 'Importar planilha',
    },
    estoque: {
      title: 'Estoque vazio',
      description: 'Registre entradas via Compras ou direto. O estoque acompanha o consumo do RDO.',
      primaryAction: 'Registrar entrada',
    },
    compras: {
      title: 'Nenhuma compra ainda',
      description: 'Marque itens no orçamento, importe planilha de pedido ou crie uma compra rápida.',
      primaryAction: 'Ir ao orçamento',
      secondaryImport: 'Importar planilha',
      secondaryQuick: 'Compra rápida',
      feedsInto: 'o estoque, a verba e a curva ABC',
    },
    inbox: {
      title: 'Caixa de entrada vazia',
      description: 'Quando aparecer algo precisando da sua atenção, mostra aqui primeiro.',
    },
    trena: {
      title: 'Sem estimativas ainda',
      description: 'O Trena extrai medidas do PDF ou IFC. É o passo zero antes do orçamento.',
      primaryAction: 'Nova estimativa',
      feedsInto: 'o orçamento e a Curva ABC da obra',
    },
    pranchas: {
      title: 'Nenhum desenho ainda',
      description: 'Faça upload do PDF da prancha. Você pode adicionar marcações e vincular RFIs.',
      primaryAction: 'Enviar prancha',
    },
    photos: {
      title: 'Galeria vazia',
      description: 'Registre o avanço da obra com fotos. Aparecem no relatório do cliente.',
      primaryAction: 'Enviar fotos',
    },
    cronograma: {
      title: 'Cronograma sem etapas',
      description: 'Defina as etapas da obra para acompanhar o avanço e a produtividade.',
      primaryAction: 'Criar etapa',
      feedsInto: 'o RUP e o relatório de avanço do cliente',
    },
    // DP3 · empty-state ruthless audit
    members: {
      title: 'Ninguém na equipe ainda',
      description: 'Adicione a equipe para registrar o diário, avanço de etapas e vistorias.',
      primaryAction: 'Convidar membro',
      feedsInto: 'o diário de obra, as etapas e as vistorias',
    },
    invitations: {
      title: 'Nenhum convite pendente',
      description: 'Convites enviados aparecem aqui até o membro aceitar.',
      primaryAction: 'Convidar cliente',
    },
    notifications: {
      title: 'Sem notificações',
      description: 'Alertas de aprovação, vencimento e atualizações aparecem aqui.',
      primaryAction: 'Ver caixa de entrada',
    },
    ownerStages: {
      title: 'Aguardando etapas da obra',
      description: 'O gerente cadastra cada etapa quando ela começa. As próximas aparecem aqui assim que forem registradas.',
    },
    ownerTimeline: {
      title: 'Aguardando atualizações',
      description: 'O gerente publica marcos e fotos aqui à medida que a obra avança.',
    },
    ownerPhotos: {
      title: 'Aguardando fotos da obra',
      description: 'O gerente registra o progresso com fotos. Volte em alguns dias.',
    },
    stockMovements: {
      title: 'Sem movimentações registradas',
      description: 'Registre entradas e saídas para ver o histórico aqui.',
    },
    activityByProject: {
      title: 'Sem atividade recente',
      description: 'Eventos de diário, fotos e vistorias aparecem aqui por obra.',
    },
    activityDays: {
      title: 'Nenhuma atividade no período',
      description: 'Registre RDOs, fotos ou vistorias para ver o histórico aqui.',
      primaryAction: 'Ir ao diário de obra',
    },
    approvalsByProject: {
      title: 'Sem pendências por obra',
      description: 'Aprovações de orçamento e compras aparecem agrupadas aqui.',
    },
    laborRollup: {
      title: 'Sem dados de equipe ainda',
      description: 'Registre marcações de presença para ver o resumo por função.',
      primaryAction: 'Registrar presença',
    },
    savedReports: {
      title: 'Sem relatórios salvos',
      description: 'Execute um relatório e salve para acessar rapidamente depois.',
    },
    checklists: {
      title: 'Sem checklists nesta etapa',
      description: 'Inicie um checklist de verificação de serviço para esta etapa.',
      primaryAction: 'Iniciar checklist',
    },
    quotations: {
      title: 'Nenhuma cotação ainda',
      description: 'Cotações partem de um pedido de compra com itens marcados.',
      primaryAction: 'Nova cotação',
      secondaryAction: 'Ir ao orçamento',
      feedsInto: 'as compras e a Curva ABC da obra',
    },
    stageBudgetItems: {
      title: 'Sem itens do orçamento marcados',
      description: 'Vá ao orçamento e marque itens para gerar as etapas.',
      primaryAction: 'Ir ao orçamento',
    },
  },

  // Wave F · Compras onboarding + Settings cleanup
  waveF: {
    comprasOnboarding: {
      heading: 'Vamos começar a comprar',
      description: 'Você ainda não marcou itens para comprar — e está tudo bem. Escolha um caminho: marque itens no orçamento, importe uma planilha ou crie uma compra rápida.',
      feedsInto: 'Alimenta a verba · o estoque · a curva ABC',
      tileOrcamento: 'Ir ao orçamento',
      tileOrcamentoSub: 'Marque itens com "para comprar" e eles aparecem aqui',
      tileImport: 'Importar pedido de material',
      tileImportSub: 'Planilha pronta? Sobe em segundos',
      tileQuickCreate: 'Criar compra rápida',
      tileQuickCreateSub: 'Sem precisar do orçamento — só descrição e valor',
      tileRecomendacao: 'Recomendação da semana',
      tileRecomendacaoSub: 'Top itens do orçamento com urgência de compra',
    },
    quickCreate: {
      title: 'Criar compra rápida',
      subtitle: 'Cria um pedido sem cotação prévia — útil quando você já tem preço e fornecedor na mão.',
      fieldDescription: 'Descrição do item',
      fieldQty: 'Quantidade',
      fieldUnit: 'Unidade',
      fieldPrice: 'Preço estimado (R$)',
      fieldSupplier: 'Fornecedor',
      noSupplier: 'Sem fornecedor definido',
      ctaCreate: 'Criar pedido',
      errDescription: 'Informe a descrição do item.',
      errQty: 'Quantidade inválida.',
      errPrice: 'Preço estimado inválido.',
      errGeneric: 'Não foi possível criar a compra.',
    },
    recomendacao: {
      title: 'Recomendação da semana',
      subtitle: 'Itens do orçamento marcados para comprar, ordenados por valor. Selecione os que quer transformar em pedido de compra agora.',
      empty: 'Nenhum item marcado para comprar no orçamento ainda.',
      emptySub: 'Vá ao orçamento e marque itens como "para comprar" para vê-los aqui.',
      selected: (n: number, total: number) => `${n} de ${total} selecionado${n !== 1 ? 's' : ''}`,
      ctaCreate: 'Criar pedido consolidado dos selecionados',
    },
  },

  // Wave E · Trena confidence UX · 2026-05-17
  trenaConfidence: {
    preupload: {
      title: 'Que tipo de arquivo funciona?',
      dismissBtn: 'Ocultar dica',
      showBtn: 'Mostrar dica de arquivo',
      goodTitle: 'Arquivo bom',
      goodDesc: 'Memorial descritivo com áreas detalhadas, projeto arquitetônico com dimensões, IFC do BIM.',
      borderlineTitle: 'Arquivo borderline',
      borderlineDesc: 'Foto de planta com letras pequenas, PDF escaneado de baixa qualidade. Vou tentar OCR mas a confiança vai ser baixa.',
      badTitle: 'Arquivo ruim',
      badDesc: 'Documento sem dimensões, foto fora de ângulo, planilha sem coluna de áreas. Vou pedir pra você informar as medidas manualmente.',
    },
    narration: {
      taskComodos: 'Identificando cômodos',
      taskAreas: 'Calculando áreas',
      taskEsquadrias: 'Identificando esquadrias (portas e janelas)',
      taskRevestimentos: 'Detectando revestimentos',
      taskInconsistencias: 'Procurando inconsistências',
      completeMsg: 'Pronto! Vou abrir a revisão.',
    },
    confidencePanel: {
      title: 'Confiança por categoria',
      clearFilter: 'Ver todas',
      summaryPrefix: 'Confiança geral:',
      summaryOverall: (label: string) => label,
    },
    checklist: {
      title: (remaining: number) =>
        remaining === 0
          ? 'Todos os itens confirmados'
          : `Preciso que você confirme ${remaining} ponto${remaining !== 1 ? 's' : ''}`,
      allDoneTitle: 'Todos os itens confirmados',
      subtitle: 'Medidas com confiança baixa ou em conflito entre documentos.',
      confirmBtn: 'Confirmar',
      correctBtn: 'Corrigir com evidência',
      reviewItem: 'Revisar este item',
    },
    budgetCompare: {
      title: 'Comparação com orçamento atual',
      emptyDesc: 'Sem orçamento anterior pra comparar.',
      emptySubtitle: 'Esta é a primeira estimativa Trena para este projeto.',
      colOld: 'Orçamento atual',
      colNew: 'Estimativa Trena',
      colDelta: 'Diferença total',
      topDiffsTitle: 'Maiores diferenças por categoria',
    },
    onboarding: {
      titleChoice: 'Como você quer começar?',
      subtitleChoice: 'Documento traz mais precisão. Sem doc dá pra ver uma estimativa preliminar.',
      recommendedBadge: 'recomendado',
      optionWithDocs: 'Tenho documentos do projeto',
      descriptionWithDocs:
        'Sobe memorial, projeto padrão, plantas etc. O sistema extrai os dados e propõe uma estimativa inicial pra você revisar.',
      continueWithDocs: 'Continuar com docs',
      optionWithoutDocs: 'Quero estimar sem documentos (preview)',
      descriptionWithoutDocs:
        'Você digita 5 dados básicos e recebe uma estimativa inicial. Pra confirmar essa estimativa e usar em compras/contratos, precisará subir documentos depois.',
      continueWithoutDocs: 'Continuar sem docs',
      backToChoice: 'Voltar pra escolha',
      uploadTitle: 'Sobe o documento principal do projeto',
      uploadSubtitle:
        'Memorial descritivo, projeto arquitetônico ou similar. Sistema extrai medidas, padrão e região automaticamente.',
      uploadDropLabel: 'Clique ou arraste PDFs aqui',
      uploadAcceptHint: 'PDF(s) até 20MB cada · máx. 8 arquivos · 50MB no total.',
      uploadingFile: 'Processando documentos…',
      processingFile: 'Lendo {file}…',
      multipleFilesHint: 'Você pode selecionar múltiplos PDFs de uma vez.',
      processingFiles: '{done} de {total} arquivo(s) processado(s)',
      tooManyFiles: 'Máximo 8 arquivos por vez.',
      totalSizeExceeded: 'Soma dos arquivos excede 50MB. Remova algum ou comprima.',
      skipDocsHint: 'Ainda sem documentos?',
      skipToPreview: 'Estimar em modo preview',
      reviewTitle: 'Revise os dados extraídos',
      reviewSubtitle:
        'Confirme ou corrija os campos pré-populados. Você pode editar qualquer um antes de gerar a estimativa.',
      extractionSummary: 'Resumo da extração',
      changeDoc: 'Trocar documento(s)',
      evidenceFor: 'Evidência',
      evidenceLabel: 'Citação do documento',
      alternativeSource: '+ {n} outro(s)',
      viewSources: 'Ver fontes',
    },
    preview: {
      bannerTitle: 'Esta estimativa foi gerada sem documentos.',
      bannerBody:
        'Você pode aprovar e usar mesmo assim — Builder gerou o ballpark pelos parâmetros do Stage 0. Pra estreitar a confiança, suba memorial ou projeto a qualquer momento.',
      bannerCta: 'Subir documentos (opcional)',
      bannerInFormTitle: 'Sem documentos',
      bannerInFormBody:
        'Tudo bem rodar sem docs — a estimativa fica como ballpark. Você pode subir memorial ou projeto depois pra aumentar a confiança.',
      previewBlocksApprovalError:
        'Estimate em modo preview. Suba memorial ou projeto antes de aprovar.',
    },
    stage0: {
      title: 'Vamos começar pela visão geral',
      subtitle: 'Cinco perguntas curtas pra gerar uma estimativa inicial com banda de confiança.',
      tipologia: 'Tipologia da obra',
      tipologiaResidencialUni: 'Residencial unifamiliar',
      tipologiaResidencialMulti: 'Residencial multifamiliar',
      tipologiaComercial: 'Comercial',
      tipologiaIndustrial: 'Industrial',
      areaConstruida: 'Área construída total',
      areaPlaceholder: 'Ex.: 287',
      padrao: 'Padrão de acabamento',
      padraoBaixo: 'Baixo',
      padraoMedio: 'Médio',
      padraoAlto: 'Alto',
      padraoLuxo: 'Luxo',
      padraoLuxoExtremo: 'Luxo extremo',
      padraoBaixoHint: '~R$ 2.280/m² (SP, jan/2025)',
      padraoMedioHint: '~R$ 2.890/m² (SP, jan/2025)',
      padraoAltoHint: '~R$ 3.650/m² (SP, jan/2025)',
      padraoLuxoHint: '~R$ 4.720/m² (SP estimado)',
      padraoLuxoExtremoHint: '~R$ 6.850/m² (SP estimado)',
      regiaoUf: 'Estado (UF)',
      regiaoCidade: 'Cidade (opcional)',
      regiaoCidadePlaceholder: 'Ex.: São Paulo',
      prazo: 'Prazo previsto da obra',
      prazoPlaceholder: 'Ex.: 14',
      submit: 'Gerar estimativa inicial →',
      submitting: 'Calculando…',
      errorGeneric: 'Não foi possível gerar a estimativa. Tente novamente.',
      errorCubNotAvailable: 'Essa região ainda não está coberta pela base CUB. Por enquanto, use SP.',
    },
    panel: {
      title: 'Orçamento estimado',
      totalCentral: 'Estimativa central',
      band: 'Banda de confiança 90%',
      uncertaintyBreakdownTitle: 'De onde vem a incerteza',
      uncBreakSistemas: 'Sistemas indefinidos',
      uncBreakPadrao: 'Padrão por ambiente',
      uncBreakVolatilidade: 'Volatilidade de preço',
      uncBreakCub: 'CUB regional aproximado',
      uncBreakIndiretos: 'Indiretos não declarados (BDI, encargos, canteiro)',
      uncBreakOutros: 'Outros',
      bucketsTitle: 'Por bucket',
      bucketEstrutura: 'Estrutura',
      bucketAlvenaria: 'Alvenaria',
      bucketAcabamento: 'Acabamento',
      bucketInstalacoes: 'Instalações',
      bucketExternos: 'Externos',
      bucketBdiCanteiro: 'BDI + canteiro',
      reduceBandTitle: 'Pra reduzir a banda',
      reduceBandCta: 'Responder perguntas-chave',
      reduceBy: (amount: string) => `Pra reduzir a banda em ~${amount}:`,
      respondQuestions: (count: number) =>
        `Responder ${count} pergunta${count !== 1 ? 's' : ''} →`,
      expandBucket: 'Expandir bucket',
      collapseBucket: 'Recolher bucket',
      bucketSourcesLabel: 'Fontes usadas',
      bucketSubItems: 'Sub-itens estimados',
      bucketSources: 'Fontes',
      bucketRefineHints: 'Pra refinar',
      bandReductionTitle: 'Apertar a banda',
      bandCurrent: (band: string, pct: string) => `Banda atual: ± ${band} (${pct})`,
      bandMinPossible: (band: string, pct: string) =>
        `Banda mínima possível: ± ${band} (${pct})`,
      answeredOfTotal: (answered: number, total: number) =>
        `Você respondeu ${answered} de ${total} perguntas`,
      answerMoreCta: 'Responda mais perguntas pra reduzir a banda →',
    },
    questions: {
      estimatedReduction: (amount: string) => `🎯 Reduz ± ${amount}`,
      estimatedReductionTooltip: (amount: string) =>
        `Responder esta pergunta reduz o intervalo de incerteza em ± ${amount}`,
      contextualHeader: 'Perguntas pra apertar a banda',
    },
    omissions: {
      title: (count: number) =>
        count === 1 ? 'Coisa que sua estimativa NÃO inclui (1)' : `Coisas que sua estimativa NÃO inclui (${count})`,
      subtitle: 'Categorias que toda obra residencial BR precisa cobrir',
      hiddenCost: (range: string) => `Custo oculto estimado: ${range}`,
      respondNow: 'Responder agora',
      addManual: 'Adicionar valor manual',
      allCovered: 'Estimativa cobre todas as categorias canônicas.',
      statusAmbiguoTooltip:
        'Existe assertion mas o valor é zero ou "nenhum" — confirme se realmente não tem.',
      priorityCritical: 'Crítico',
      priorityHigh: 'Importante',
      priorityMedium: 'Atenção',
    },
    volatility: {
      title: 'Volatilidade de preços (INCC + insumos)',
      expectedInflation: (months: number, pct: string) =>
        `Inflação INCC esperada em ${months} meses: ${pct}`,
      worstCase: (pct: string) => `Cenário pessimista (p90): ${pct}`,
      hint:
        'Prazo longo = mais exposição a inflação de aço, cimento e mão de obra. Banda aumenta proporcionalmente.',
    },
    telemetry: {
      title: 'Histórico desta estimativa',
      summaryLine: (inferences: number, overrides: number) =>
        `${inferences} inferência${inferences === 1 ? '' : 's'} · ${overrides} override${overrides === 1 ? '' : 's'} do PM`,
      bandJourney: (fromPct: string, toPct: string) =>
        `Banda foi de ±${fromPct} → ±${toPct}`,
      sinceMinutes: (min: number) => `em ${min} min`,
      detailsCta: 'Ver detalhes →',
      drawerTitle: 'Histórico da estimativa',
      historySection: 'Inferências do motor',
      overridesSection: 'Alterações do PM',
      featuresSection: 'Sinais usados na última inferência',
      emptyHistory: 'Sem inferências ainda.',
      overrideCount: (n: number) => `${n} alteração${n === 1 ? '' : 'ões'}`,
      closeCta: 'Fechar',
    },
    analogical: {
      title: (k: number) =>
        k === 0
          ? 'Obras similares no banco'
          : `Obras similares no banco (${k} encontrada${k === 1 ? '' : 's'})`,
      emptyTitle: 'Modelo ainda está aprendendo',
      emptyHint:
        'Sua primeira obra desse tipo no banco analogical. Banda mais ampla até termos mais histórico parecido.',
      avgCostPerM2: (cost: string) => `Custo médio ${cost}/m²`,
      yourEstimate: (cost: string) => `Sua estimativa: ${cost}/m²`,
      confBaixa: 'Confiança baixa',
      confMedia: 'Confiança média',
      confAlta: 'Confiança alta',
      detailsCta: 'Ver vizinhos →',
      drawerTitle: 'Vizinhos analógicos',
      statusProjected: 'Projetada',
      statusInProgress: 'Em obra',
      statusClosedActual: 'Fechada',
    },
    actuals: {
      title: 'Sua experiência (Planejado vs Realizado)',
      avgVariancePrefix: (n: number) =>
        `Suas últimas ${n} obra${n === 1 ? '' : 's'} errou em média:`,
      avgVarianceSuffix: (pct: number) =>
        pct >= 0
          ? `+${pct.toFixed(0)}% pra mais (você subestima)`
          : `${pct.toFixed(0)}% pra menos (você superestima)`,
      worstLabel: 'Bucket pior:',
      bestLabel: 'Bucket melhor:',
      recommendationLabel: 'Recomendação pra esta obra:',
      recommendationBody: (bucketLabel: string, pct: number) =>
        `Considere lado superior da banda no bucket ${bucketLabel} (+${pct.toFixed(0)}% histórico).`,
      historyLabel: 'Suas obras anteriores:',
      detailsCta: 'Ver detalhes',
      emptyTitle: '🌱 Modelo ainda está aprendendo',
      emptyHint:
        'Quando você fechar suas primeiras obras, o sistema vai mostrar aqui o quanto você costuma errar e em quais buckets — pra ajustar próximas estimativas.',
      drawerTitle: 'Planejado vs Realizado por bucket',
      colBucket: 'Bucket',
      colPlanned: 'Planejado',
      colActual: 'Realizado',
      colVariance: 'Variância',
      bucketEstrutura: 'Estrutura',
      bucketAlvenaria: 'Alvenaria',
      bucketAcabamento: 'Acabamento',
      bucketInstalacoes: 'Instalações',
      bucketExternos: 'Externos',
      bucketBdiCanteiro: 'BDI + canteiro',
    },
  },

  // Wave G · Language cleanup — human-friendly PT-BR labels for technical terms.
  glossary: {
    selfHeal: 'correção automática',
    drift: 'mudança fora do esperado',
    anomaly: 'ponto de atenção',
    anomalies: 'pontos de atenção',
    proposal: 'sugestão',
    proposals: 'sugestões',
    trace: 'histórico técnico',
    tier: 'nível',
    payload: 'dados',
    guardrail: 'regra de proteção',
    guardrails: 'regras de proteção',
    kindSelfHealFailed: 'Falha na correção automática',
    kindAnomalyHigh: 'Ponto de atenção',
    kindAnomalyResolve: 'Ponto de atenção',
    nudgeAnomalyOpen: 'Ponto de atenção aberto',
    tierLevel: 'nível',
    tierLevelN: 'Nível',
    tier1Label: 'automático',
    tier2Label: 'supervisionado',
    tier3Label: 'exigirá sua aprovação',
    tier4Label: 'decisão sua',
    originSystem: 'Sistema Builder (automático)',
    originPrumo: 'Assistente de obra · regra automática',
    auditTitle: 'Auditoria',
    auditWho: 'Quem decidiu',
    auditWhen: 'Quando',
    auditWhy: 'Por quê',
    auditImpact: 'Impacto',
    auditOrigin: 'Origem',
    auditRawJson: 'Ver dados brutos (admin)',
    techDetailsAdmin: 'Detalhes técnicos (admin)',
    prumoScanDaily: 'O assistente de obra varre as obras todo dia de manhã e coloca cards aqui quando algo precisar do seu OK.',
  },
  // Wave M · Admin Trena Sanity Ranges
  adminTrenaSanityRanges: {
    title: 'Faixas de Sanidade Trena',
    subtitle: 'Configure faixas aceitáveis de consumo por insumo. Overrides do tenant substituem os valores padrão do sistema.',
    systemDefault: 'Padrão sistema',
    tenantOverride: 'Override tenant',
    overrideCount: (n: number) => `${n} override${n !== 1 ? 's' : ''} ativos`,
    colComposition: 'Insumo',
    colUnit: 'Unidade base',
    colSystemRange: 'Faixa sistema',
    colTenantRange: 'Faixa tenant',
    colStatus: 'Status',
    statusOverridden: 'Personalizado',
    statusDefault: 'Padrão',
    addOverride: 'Adicionar override',
    editOverride: 'Editar',
    deleteOverride: 'Remover',
    confirmDelete: 'Remover este override e voltar ao padrão do sistema?',
    rangeMin: 'Mínimo',
    rangeMax: 'Máximo',
    rangeLabel: 'Label (opcional, ex: un/m²)',
    saveOverride: 'Salvar',
    saving: 'Salvando...',
    overrideSaved: 'Override salvo com sucesso.',
    overrideDeleted: 'Override removido. Usando padrão do sistema.',
    emptyTitle: 'Nenhuma faixa customizada',
    emptyDesc: 'Todas as faixas usam os padrões do sistema. Adicione um override pra ajustar para este tenant.',
    feedbackBadgeTitle: 'Baseado em medições reais',
    feedbackBadgeSuffix: 'medições reais.',
    feedbackSuggest: (min: number, max: number) => `Sugiro ajuste pra ${min}–${max}`,
    applyFeedbackRanges: 'Aplicar sugestões de feedback',
    applyingRanges: 'Aplicando...',
    rangesApplied: (n: number) => `${n} faixa${n !== 1 ? 's' : ''} ajustada${n !== 1 ? 's' : ''} com base em dados reais.`,
    noFeedbackData: 'Sem dados de medições reais ainda.',
  },
  // DP1 · NextActionCard namespace
  nextAction: {
    loading: 'Carregando próxima ação…',
    projectsLateRdoSingle: (name: string) => `${name} está com RDO atrasado hoje`,
    projectsLateRdoMultiple: (count: number) => `${count} obras com RDO atrasado hoje`,
    projectsLateImpact: (total: number) =>
      `${total} ${total === 1 ? 'diário' : 'diários'} em atraso — cliente pode cobrar atualização`,
    projectsLateCtaLabel: 'Abrir obra',
    overviewEmptyTitle: 'Obra criada — comece pelo orçamento',
    overviewEmptyImpact: 'Trena extrai quantitativos do PDF da planta em minutos',
    overviewEmptyCta: 'Começar com Trena',
    budgetPendingVersions: (count: number) =>
      `${count} ${count === 1 ? 'versão' : 'versões'} aguardando aprovação`,
    budgetPendingImpact: 'Aprovação libera comprometimento de verba para compras',
    budgetPendingCta: 'Aprovar versão pendente',
    budgetHighUsage: (category: string, pct: number) =>
      `Verba de ${category} em ${pct}% usada`,
    budgetHighUsageImpact: 'Risco de estourar categoria antes do fim da obra',
    budgetHighUsageCta: 'Revisar itens',
    budgetNoSinapi: 'Orçamento sem preço de referência',
    budgetNoSinapiImpact: 'Vincular preços de referência (SINAPI) melhora rastreabilidade',
    budgetNoSinapiCta: 'Revisar referências',
    purchasesWaiting: (count: number, days: number) =>
      `${count} ${count === 1 ? 'cotação aguarda' : 'cotações aguardam'} resposta há ${days}+ dias`,
    purchasesWaitingImpact: 'Fornecedor pode cancelar proposta — prazo vencendo',
    purchasesWaitingCta: 'Enviar follow-up',
    qualidadeOverdueSingle: (projects: number) =>
      `1 vistoria atrasada em ${projects} ${projects === 1 ? 'obra' : 'obras'}`,
    qualidadeOverdueMultiple: (count: number, projects: number) =>
      `${count} vistorias atrasadas em ${projects} ${projects === 1 ? 'obra' : 'obras'}`,
    qualidadeImpact: 'Vistoria em atraso bloqueia etapa e aumenta risco de retrabalho',
    qualidadeCta: 'Abrir lista de vistorias',
    financialHighUsage: (category: string, pct: number) =>
      `Verba de ${category} em ${pct}% usada`,
    financialHighUsageImpact: 'Risco de estouro antes do fim da obra',
    financialHighUsageCta: 'Revisar',
    scheduleMilestone: (name: string, daysLabel: string) =>
      `"${name}" ${daysLabel}`,
    scheduleImpact: 'Confirme o avanço para manter o cronograma atualizado',
    scheduleCta: 'Confirmar avanço',
    photosUnlinked: (count: number) =>
      `${count} ${count === 1 ? 'foto sem RDO' : 'fotos sem RDO'} associado`,
    photosUnlinkedImpact: 'Fotos soltas não aparecem no relatório do cliente',
    photosUnlinkedCta: 'Vincular ao RDO',
    trenaDraftOld: (days: number) => `Estimativa aberta há ${days} dias sem commit`,
    trenaDraftImpact: 'Estimativas abertas não alimentam o orçamento da obra',
    trenaDraftCta: 'Continuar estimativa',
    inboxPending: (count: number) =>
      `${count} ${count === 1 ? 'decisão pendente' : 'decisões pendentes'}`,
    inboxImpact: 'Decisões pendentes podem travar compras e pagamentos',
    inboxCta: 'Revisar próxima',
  },

  dp5: {
    inlineSearch: {
      placeholder: 'Buscar…',
      clearAriaLabel: 'Limpar busca',
      countLabel: (filtered: number, total: number) => `${filtered} de ${total}`,
    },
    keyboardShortcuts: {
      drawerTitle: 'Atalhos',
      groupSearch: 'Pesquisa',
      groupNav: 'Navegação',
      groupGeneral: 'Geral',
      globalSearch: 'Busca global',
      listSearch: 'Busca na lista',
      goHome: 'Ir para Hoje',
      goProjects: 'Ir para Obras',
      goInbox: 'Ir para Inbox',
      goNotifications: 'Ir para Notificações',
      showShortcuts: 'Ver atalhos',
      closeOverlay: 'Fechar overlay',
      mobileNote: 'Atalhos funcionam em desktop. No mobile use a barra inferior.',
      then: 'então',
    },
    autocomplete: {
      noResults: 'Nenhum resultado',
      create: (text: string) => `Criar "${text}"`,
      clearAriaLabel: 'Limpar seleção',
    },
    recentItems: {
      sectionTitle: 'Vistos recentemente',
    },
  },
  pageHeader: {
    more: 'Mais',
    moreActionsAriaLabel: 'Mais ações',
  },
  welcome: {
    greeting: (firstName) => `Bem-vindo ao Builder, ${firstName}`,
    subtitle:
      'Gestão de obra que cabe no celular: orçamento, diário de obra (RDO), compras e medições, ' +
      'tudo no mesmo lugar. Como você quer começar?',
    demoTitle: 'Quero explorar com uma obra de exemplo',
    demoDescription:
      'Obra fictícia de reforma 120m² já com tudo preenchido. Pode editar, apagar, ' +
      'brincar — não interfere em nenhuma obra real.',
    demoCta: 'Ver obra exemplo',
    demoInventory: '15 diários de obra · 12 pedidos de compra · 50 itens de orçamento · 2 pendências de qualidade · 3 perguntas técnicas',
    demoEstimatedTime: '5 minutos pra explorar',
    createTitle: 'Quero criar uma obra real',
    createDescription:
      'Comece uma obra nova. Em 1 minuto você cadastra cliente, endereço e tipo de obra, ' +
      'e o Builder sugere os próximos passos.',
    createCta: 'Criar obra',
    importTitle: 'Importar de planilha Excel',
    importDescription:
      'Já tem orçamento em Excel? Importa direto: o Builder lê sua planilha em 30s, ' +
      'preview com diferenças, confirma ou desfaz.',
    importCta: 'Importar planilha',
    skip: 'Pular por enquanto',
    demoLoading: 'Preparando obra exemplo…',
    demoError: 'Não consegui preparar a obra exemplo agora. Tenta de novo em alguns segundos.',
    demoRetry: 'Tentar de novo',
  },
  // W123.4 · 2026-05-26 · Próxima ação (substitui marca "Prumo").
  proximaAcao: {
    title: 'Próxima ação',
  },
  tour: {
    skip: 'Pular tour',
    next: 'Próximo',
    back: 'Voltar',
    finish: 'Pronto, vamos começar',
    counter: (current, total) => `Passo ${current} de ${total}`,
    retake: 'Refazer tour',
    startCta: 'Conhecer o Builder em 2 minutos',
    steps: {
      welcome: {
        title: 'Bem-vindo ao Builder',
        body: 'Vou te mostrar em 2 minutos como tudo se conecta. Você pode pular a qualquer momento.',
      },
      search: {
        title: 'Busca global · Cmd+K',
        body: 'Encontra qualquer obra, RDO, pedido ou fornecedor. Funciona em qualquer tela.',
      },
      chat: {
        title: 'Chat rápido',
        body: 'Balão de fala pra mensagem instantânea com a equipe. Histórico 7 dias, emoji, presence.',
      },
      inbox: {
        title: 'Inbox formal',
        body: 'Envelope pra mensagens com assunto e auditoria. Tudo fica registrado pra rastreio.',
      },
      hoje: {
        title: 'Hoje · suas próximas ações',
        body: 'Cockpit do dia: as 3 coisas mais importantes pra você resolver agora, cross-obras.',
      },
      trena: {
        title: 'Trena · orçamento inteligente',
        body: 'Importa foto ou PDF, a IA extrai medidas e o Builder calcula o preço pela tabela oficial de preços (SINAPI).',
      },
      comando: {
        title: 'Comando · visão admin',
        body: 'Painel kanban com todas as obras da carteira. Filtro por urgência, drag-and-drop.',
      },
      done: {
        title: 'Pronto. Hora de criar sua primeira obra.',
        body: 'O resto você aprende usando. Se travar, abre a Central de ajuda ou refaz o tour.',
      },
    },
  },
  status: {
    title: 'Status do Builder',
    subtitle: 'Estado atual da plataforma. Atualiza automaticamente a cada 30 segundos.',
    pillOk: 'Tudo operacional',
    pillDegraded: 'Operação parcial',
    pillDown: 'Indisponível',
    cardDb: 'Banco de dados',
    cardR2: 'Armazenamento (fotos)',
    cardCron: 'Tarefas agendadas',
    latency: (ms) => `${ms} ms`,
    lastCronAt: (when) => `Última execução: ${when}`,
    lastCronNever: 'Nenhuma execução registrada ainda',
    historyTitle: 'Últimas 24 horas',
    historyEmpty: 'Sem histórico ainda — o monitor está coletando dados.',
    loading: 'Carregando status…',
    errorTitle: 'Não foi possível carregar o status',
    errorBody: 'Tentaremos de novo automaticamente em alguns segundos.',
    updatedAt: (when) => `Atualizado em ${when}`,
  },
  firstRun: {
    cancel: 'Cancelar',
    autoSaving: 'auto-salvando',
    savedAt: (time) => `salvo às ${time}`,
    resumeTitle: 'Você tem progresso não salvo',
    resumeBody: (ago) => `Iniciado ${ago}. Continuar ou começar do zero?`,
    resumeContinue: 'Continuar',
    resumeStartFresh: 'Começar novo',
    next: 'Próximo',
    back: 'Voltar',
    complete: 'Concluir',
    stepOf: (current, total) => `${current} de ${total}`,
    defaultBlockMsg: 'Preencha os campos obrigatórios para continuar.',
  },
  chat: {
    sendPlaceholder: 'Mensagem…',
    sendHint: 'Enter para enviar · Shift+Enter para nova linha',
    noMessages: 'Nenhuma mensagem ainda.',
    online: 'Online agora',
    minimize: 'Minimizar',
    close: 'Fechar',
    openChat: (name) => `Enviar mensagem para ${name}`,
    closeChat: (name) => `Fechar chat com ${name}`,
  },
  moduleProgress: {
    stepsReady: (done, total) => `${done} de ${total} passos prontos`,
    progressBarLabel: (module) => `Progresso de ${module}`,
    lockedPrefix: 'Bloqueado:',
    rdo: {
      moduleLabel: 'Diário de Obra',
      completedLabel: 'Diário configurado — seu cliente vai adorar.',
    },
    fvs: {
      moduleLabel: 'Inspeção de Qualidade',
      completedLabel: 'Qualidade em dia — continue inspecionando.',
    },
    orcamento: {
      moduleLabel: 'Orçamento',
      completedLabel: 'Tudo pronto para começar a comprar.',
    },
    verba: {
      moduleLabel: 'Verba',
      completedLabel: 'Verba configurada — acompanhe o consumo.',
    },
    compras: {
      moduleLabel: 'Compras',
      completedLabel: 'Primeiro ciclo de compra concluído.',
    },
    pranchas: {
      moduleLabel: 'Pranchas',
      completedLabel: 'Projeto importado e vinculado às perguntas.',
    },
    trena: {
      moduleLabel: 'Trena',
      completedLabel: 'Estimativa aprovada e no orçamento.',
    },
  },
  adminJargon: {
    storageTitle: 'Armazenamento',
    storageDescription: 'Estatísticas do armazenamento de documentos.',
    organization: 'Organização',
    organizations: 'Organizações',
    organizationSwitcherTitle: 'Gerenciador de Organizações',
    organizationSwitcherSubtitle: 'Listar organizações disponíveis e alternar contexto.',
    organizationListHeading: (n) => `Organizações (${n})`,
    organizationsTabLabel: 'Organizações',
    organizationsTabDescription: 'Organizações e contextos da plataforma.',
    organizationsTabHelp:
      'Esta aba mostra as organizações da plataforma. Use para alternar contexto entre organizações ou verificar contagens de projetos.',
    organizationsEmptyTitle: 'Nenhuma organização',
    organizationsEmptyDescription: 'Nenhuma organização encontrada na plataforma.',
    organizationProjectCount: (n) => `${n} projeto${n !== 1 ? 's' : ''}`,
    organizationSwitch: 'Alternar',
    organizationSwitching: 'Alternando…',
    selfHealing: 'Correção automática',
    selfHealingT1: 'Correção automática T1',
    traceTabLabel: 'Histórico técnico',
    bulkOpsDescription: 'Operações em massa (excluir, reatribuir, etc.).',
    driftBadge: 'fora do esperado',
    driftBadgeTitle:
      'Uma ou mais chaves nesta composição não existem no catálogo — possível mudança fora do esperado entre regra e compatibilidade.',
    driftKeyTooltip: (key) =>
      `Chave "${key}" não está no catálogo — possível mudança fora do esperado`,
  },
  obraType: {
    residencial_popular: 'Casa (até 80m²)',
    residencial_medio: 'Casa ou sobrado maior (80-200m²)',
    comercial_loja: 'Loja ou comércio (até 500m²)',
    industrial_galpao: 'Galpão ou industrial',
    recommendedForYourType: 'Recomendado pro seu tipo de obra',
    otherTemplates: 'Outros modelos',
    label: 'Tipo de obra',
    optional: 'Tipo de obra (opcional)',
    helperText: 'Ajuda o Trena a sugerir orçamentos certos pro seu tipo.',
    placeholder: 'Selecione…',
  },
  region: {
    label: 'Estado da obra',
    optional: 'Estado da obra (opcional)',
    helperText:
      'Trena usa o preço de referência (SINAPI) do estado escolhido; cai pro nacional quando não há dado local.',
    national: 'Nacional (CAIXA · default)',
    pilotGroup: 'Estados piloto',
    otherGroup: 'Outros estados',
  },
  sinapiSource: {
    ufExact: 'Preço UF oficial',
    derived: 'Preço regional estimado',
    national: 'Preço nacional',
    derivedHint:
      'Estimado a partir do preço nacional × fator regional da UF. Pra precisão exata, importe a planilha SINAPI da CAIXA da sua UF.',
  },

  trenaExport: {
    exportCsv: 'Exportar Excel/CSV',
    exportCsvHint: 'Formato universal — abre no Excel ou em qualquer ERP.',
    exportCsvTooltip:
      'Abre no Excel ou em qualquer ERP de construção (SISCONTROLE, SIENGE, Sage etc).',
  },

  chatInstant: {
    launcher: {
      tooltip: 'Conversas',
      unreadSuffix: (n) => `${n} não lida${n > 1 ? 's' : ''}`,
    },
    panel: {
      title: 'Conversas',
      subtitle: 'Mensagens rápidas. Some em 7 dias.',
      searchPlaceholder: 'Buscar pessoa ou grupo',
      newButton: 'Nova',
      newSearchPlaceholder: 'Digite o nome de alguém',
      newNoResults: 'Ninguém encontrado.',
      tabs: {
        all: 'Tudo',
        unread: 'Não lidas',
        groups: 'Grupos',
      },
      empty: 'Você ainda não tem conversas.',
      emptyUnread: 'Tudo lido por aqui.',
      emptySearch: 'Nada encontrado nessa busca.',
      emptyCta: 'Começar nova conversa',
    },
    window: {
      placeholder: 'Digite uma mensagem...',
      send: 'Enviar',
      typing: (name) => `${name} está digitando...`,
      online: 'online',
      offline: (when) => `última vez ${when}`,
      empty: 'Mande a primeira mensagem.',
      openEmojis: 'Abrir emojis',
    },
    dock: {
      newCount: (n) => (n === 1 ? '1 nova' : `${n} novas`),
      moreSenders: (n) => `+ ${n} outras`,
      dismiss: 'Dispensar',
      openWith: (name) => `Abrir conversa com ${name}`,
    },
    pills: {
      label: 'Conversas recentes',
      moreCount: (n) => `+${n} outras`,
      dismissPin: 'Fechar pin desta conversa',
      openWith: (name) => `Abrir conversa com ${name}`,
      openGeneric: 'Abrir conversa',
    },
  },
  maps: {
    addressPlaceholder: 'Rua, número, bairro, cidade…',
    loadingSdk: 'Carregando sugestões de endereço',
    autocompleteUnavailable:
      'Sugestões offline — preencha o endereço manualmente e a gente geocoda no servidor.',
    loadingThumbnail: 'Carregando mapa',
    thumbnailUnavailable: 'Mapa indisponível',
    staticMapAlt: 'Mapa do endereço',
    interactiveMapAria: 'Mapa interativo do endereço',
    loadingMap: 'Carregando mapa…',
    refreshGeo: 'Atualizar geo deste endereço',
    refreshGeoQueued: 'Pedimos um novo geocoding. Atualize a página em alguns segundos.',
    distanceBadge: (km, min) => `${km.toFixed(1).replace('.', ',')} km · ${min} min`,
    geoCaptured: 'Localização capturada do autocomplete',
  },
  weather: {
    chipAria: (description, tempC) => `Clima: ${description}, ${Math.round(tempC)}°C`,
    chipSummaryDry: 'próx 3d sem chuva',
    chipSummaryRain: (label) => `chuva ${label}`,
    expand: 'Ver previsão 7 dias',
    collapse: 'Esconder previsão',
    forecastTitle: 'Previsão 7 dias',
    unavailable: 'Clima indisponível agora',
    missingGeo: 'Adicione o endereço da obra pra ver o clima',
    rainWarning: 'Chuva forte prevista — considere remarcar atividades externas',
    externalStageWarning: 'Etapa externa em dia de chuva forte',
    dayShort: ['Hoje', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'],
    todayLabel: 'Hoje',
    conditionClear: 'Tempo bom',
    conditionCloudy: 'Parcialmente nublado',
    conditionRainLight: 'Chuva leve',
    conditionRainHeavy: 'Chuva forte',
    conditionStorm: 'Tempestade',
    conditionSnow: 'Neve',
    conditionFog: 'Neblina',
    conditionUnknown: 'Indisponível',
    rdoAutoFilled: 'preenchido automático',
    rdoAutoFillFailed: 'Não consegui buscar o clima — preencha manualmente',
  },
  adminLabsGenerative: {
    title: 'Labs · Engenharia Generativa',
    subtitle: 'Descreva sua obra e a IA gera um esboço de planta, memorial, orçamento, cronograma e cotações.',
    sketchBadge: 'Esboço — requer revisão técnica',
    disclaimer: '💡 Este é um esboço gerado por IA. Não substitui projeto de arquiteto/engenheiro registrado no CREA/CAU.',
    promptPlaceholder: 'Descreva sua obra. Exemplo: Casa térrea 120m² 3 quartos em Sorocaba, padrão médio, 8 meses, terreno plano',
    generateBtn: 'Gerar esboço',
    generating: 'Gerando…',
    tabs: {
      planta: 'Planta',
      memorial: 'Memorial',
      orcamento: 'Orçamento',
      cronograma: 'Cronograma',
      cotacoes: 'Cotações',
    },
    steps: {
      planta: '✏️ Esboçando planta…',
      memorial: '📝 Escrevendo memorial…',
      orcamento: '💰 Calculando orçamento…',
      cronograma: '📅 Montando cronograma…',
      cotacoes: '📦 Listando cotações…',
    },
    orcamento: {
      total: 'Valor total estimado',
      area: 'Área',
      padrao: 'Padrão',
      tipologia: 'Tipologia',
      buckets: 'Composição do custo',
      categoria: 'Categoria',
      percentual: '%',
      valor: 'Valor estimado',
    },
    cronograma: {
      duration: 'Duração total',
      months: 'meses',
      activity: 'Atividade',
      month: 'Mês',
      days: 'Dias',
    },
    cotacoes: {
      item: 'Item',
      qtd: 'Qtd',
      unit: 'Unidade',
    },
    emptyTitle: 'Pronto para gerar',
    emptyHint: 'Descreva sua obra acima e clique em "Gerar esboço".',
    errorTitle: 'Erro ao gerar esboço',
    plantaUnavailable: 'Planta não gerada neste esboço.',
    v2: {
      tabBrief: 'Brief',
      tabLayout: 'Layout',
      tabEstimate: 'Estimativa & Memorial',
      briefHeader: 'O que o sistema entendeu do seu prompt',
      briefTipologia: 'Tipologia',
      briefArea: 'Área construída',
      briefPadrao: 'Padrão',
      briefRegiao: 'Região',
      briefPrazo: 'Prazo estimado',
      briefSistemas: 'Sistemas especiais',
      briefComodos: 'Cômodos identificados',
      briefDescricao: 'Descrição arquitetônica',
      briefPremissas: 'Premissas declaradas',
      briefLacunas: 'Lacunas (LLM teve que assumir)',
      briefTermos: 'Termos do prompt (serão citados no memorial)',
      briefMonths: 'meses',
      layoutHeader: 'Cômodos sugeridos (heurística declarada)',
      layoutAreaTotal: 'Área somada',
      layoutAreaDeclarada: 'Área declarada no Brief',
      layoutAreaWarning: 'Discrepância > 10% — revise área no Brief.',
      estimateHeader: 'Estimativa de valor (engine real)',
      estimateBandLabel: 'Banda 90% CI',
      estimateCubLabel: 'Comparação com CUB SINDUSCON',
      estimateCubPadrao: 'CUB padrão',
      estimateYourPrice: 'Seu R$/m²',
      estimateDiff: 'Diferença',
      estimateBuckets: 'Composição (vinda do engine de confiança)',
      estimateMemorial: 'Memorial descritivo',
      noneDash: '—',
      briefEditTipologiaLabel: 'Tipologia',
      briefEditPadraoLabel: 'Padrão',
      briefEditAreaLabel: 'Área (m²)',
      briefEditPrazoLabel: 'Prazo (meses)',
      briefEditUfLabel: 'UF',
      briefEditCidadeLabel: 'Cidade',
      briefEditSistemasLabel: 'Sistemas especiais',
      briefEditSistemasAddPlaceholder: 'Adicionar sistema…',
      briefEditSistemasAdd: 'Adicionar',
      recalcBtn: 'Recalcular estimativa',
      recalcLoading: 'Recalculando…',
      recalcDone: 'Estimativa atualizada',
      layoutSvgFooter: 'Esboço esquemático — não é planta arquitetônica',
    },
  },
  adminLabsGenerativeHistory: {
    pageTitle: 'Histórico Lab Generative',
    searchPlaceholder: 'Buscar por prompt…',
    columnPrompt: 'Prompt',
    columnPadrao: 'Padrão',
    columnEstimativa: 'Estimativa',
    columnStatus: 'Status',
    columnData: 'Data',
    columnTipologia: 'Tipologia',
    columnArea: 'Área',
    statusConverted: 'Convertido',
    statusPreview: 'Preview',
    emptyState: 'Nenhum draft V2 ainda. Gere um em /admin/labs/generative.',
    detailTitle: 'Detalhe do Draft',
    detailClose: 'Fechar',
    detailBrief: 'Brief extraído',
    detailEstimate: 'Estimativa',
    detailMemorial: 'Memorial',
    loadError: 'Não foi possível carregar o histórico.',
  },
  adminMetaTemplates: {
    title: 'Templates WhatsApp Meta',
    subtitle: 'Status de aprovação dos templates UTILITY no Meta Business Manager.',
    refreshBtn: 'Atualizar agora',
    refreshing: 'Atualizando…',
    refreshSuccess: (checked: number) =>
      `${checked} template${checked !== 1 ? 's' : ''} verificado${checked !== 1 ? 's' : ''}.`,
    refreshErrors: (count: number) =>
      `${count} erro${count !== 1 ? 's' : ''} durante a atualização — veja o console.`,
    empty: 'Nenhum template encontrado.',
    emptyHint: 'Clique em "Atualizar agora" para buscar os templates da Meta.',
    colName: 'Template',
    colStatus: 'Status',
    colCategory: 'Categoria',
    colLanguage: 'Idioma',
    colQuality: 'Qualidade',
    colLastChecked: 'Última verificação',
    statusLabels: {
      APPROVED: 'Aprovado',
      PENDING: 'Pendente',
      REJECTED: 'Rejeitado',
      DISABLED: 'Desativado',
      PAUSED: 'Pausado',
      unknown: 'Desconhecido',
    },
    qualityLabels: {
      GREEN: 'Verde',
      YELLOW: 'Amarelo',
      RED: 'Vermelho',
      UNKNOWN: 'Desconhecido',
    },
    rejectionLabel: 'Motivo:',
    loadError: 'Não foi possível carregar os templates.',
  },
  // Wave 51 · 2026-05-23 · Procurement Autopilot dummy-proof v2.
  procurementAutopilot: {
    title: 'Autopiloto de Compras',
    subtitle: 'Detecta lacunas, recomenda ações e — quando há regra automática ativa — fecha compras e emite ordens de compra automaticamente.',
    tabAlerts: (n: number) => `Alertas detectados (${n})`,
    tabRules: (n: number) => `Regras automáticas (${n})`,
    tabExecutions: (n: number) => `Execuções (${n})`,
    pauseIdleLabel: 'Nenhuma regra automática ativa',
    pauseActiveLabel: (n: number) => `Pausar autopilot (${n} ativ${n > 1 ? 'as' : 'a'})`,
    pausingLabel: 'Pausando…',
    pauseConfirmTitle: 'Pausar autopilot de compras?',
    pauseConfirmMessage: (n: number) => `Vai desativar todas as ${n} regra${n > 1 ? 's automáticas ativas' : ' automática ativa'}. Nenhuma compra automática será executada até você reativar manualmente.`,
    pauseConfirmCta: (n: number) => `Pausar ${n} regra${n > 1 ? 's' : ''}`,
    approveConfirmTitle: 'Confirmar revisão?',
    approveConfirmCta: 'Sim, confirmar',
    declineConfirmTitle: 'Confirmar recusa?',
    declineConfirmCta: 'Sim, descartar alerta',
    createRfqBtn: 'Criar cotação',
    creatingRfqBtn: 'Criando cotação…',
    revertPoBtn: 'Reverter compra',
    emptyRulesTitle: 'Nenhuma regra automática configurada',
    emptyRulesDesc: 'Crie a primeira regra pra liberar compras automáticas por categoria.',
    newRuleBtn: 'Nova regra automática',
    saveRuleBtn: 'Salvar regra',
    editRuleBtn: 'Editar regra automática',
  },

  // W123.12 · 2026-05-26 · Entrega (closeout conduzido)
  entrega: {
    title: 'Entrega da obra',
    activeSubtitle:
      'A obra está em andamento. Quando estiver pronta para entrega, inicie o processo conduzido — você vai checar pendências, assinar o termo, e o Manual do Proprietário é gerado automaticamente.',
    activeCardTitle: 'Pronto para entregar?',
    activeCardDesc:
      'Iniciar entrega congela RDOs, compras e pagamentos. Você ainda pode voltar atrás antes de marcar como entregue.',
    startCta: 'Iniciar entrega',
    startingCta: 'Iniciando…',
    startError: 'Não foi possível iniciar a entrega. Tente novamente.',
    handoffTitle: 'Entrega em andamento',
    handoffSubtitle:
      'Resolva as pendências abaixo antes de entregar a obra ao dono. Itens em vermelho bloqueiam a entrega.',
    completedTitle: 'Obra entregue',
    completedOn: (date) => `Entregue em ${date}`,
    completedNoDate: 'Status: entregue',
    manualTitle: 'Manual do Proprietário',
    manualDesc:
      'Manual completo com plantas, garantias, contatos e instruções de manutenção — pronto pra entregar ao dono.',
    manualDownload: 'Baixar Manual (PDF)',
    reopenTitle: 'Reabrir obra',
    reopenDesc:
      'Ação de administrador. Use somente se a obra foi marcada como entregue por engano ou se há pendências graves descobertas depois.',
    reopenCta: 'Reabrir obra (admin)',
    reopenConfirmTitle: 'Reabrir obra entregue?',
    reopenConfirmDesc:
      'A obra volta para "Em andamento". Todos os módulos voltam editáveis. Esta ação fica no histórico.',
    completeCta: 'Entregar ao dono',
    completingCta: 'Entregando…',
    completeError: 'Falha ao concluir entrega. Verifique se o checklist está totalmente verde.',
    confirmCompleteTitle: 'Entregar ao dono?',
    confirmCompleteDesc:
      'A obra será marcada como entregue. O Manual do Proprietário será gerado e ficará disponível para download. Esta ação é irreversível pelo PM.',
    allClear: 'Tudo certo. Pode entregar ao dono.',
    blockedMsg: 'Resolva os itens vermelhos para liberar a entrega.',
    item: {
      rnc: 'Não conformidades abertas',
      rncOk: 'Sem não conformidades abertas',
      rncOpen: (n) => `${n} não ${n === 1 ? 'conformidade aberta' : 'conformidades abertas'}`,
      fvs: 'Vistorias reprovadas',
      fvsOk: 'Nenhuma vistoria reprovada pendente',
      fvsFailed: (n) => `${n} vistoria${n === 1 ? '' : 's'} com não conformidade não verificada`,
      payments: 'Pagamentos em aberto',
      paymentsOk: 'Tudo pago',
      paymentsUnpaid: (amount) => `${amount} a pagar`,
      stock: 'Estoque restante',
      stockOk: 'Sem itens em estoque',
      stockRemaining: (n) => `${n} ${n === 1 ? 'item' : 'itens'} em estoque (informativo)`,
      docs: 'Documentos finais',
      docsOk: 'Contrato, ART, Alvará e Licença anexados',
      docsMissing: (list) => `Faltam: ${list}`,
      photos: 'Fotos finais',
      photosCount: (n) => `${n} ${n === 1 ? 'foto' : 'fotos'} (informativo)`,
      term: 'Termo de entrega',
      termSigned: (date) => `Assinado por proprietário em ${date}`,
      termUnsigned: 'Termo de entrega ainda não assinado pelo proprietário',
      collectSignature: 'Coletar assinatura',
      cancel: 'Cancelar',
      ownerNamePlaceholder: 'Nome do proprietário',
      confirmSign: 'Confirmar',
      signing: 'Assinando…',
      signError: 'Não foi possível salvar a assinatura. Tente novamente.',
      seeRnc: 'Ver não conformidades',
      seeFvs: 'Ver vistorias',
      goMoney: 'Ir para Dinheiro',
      attachDocs: 'Anexar documentos',
    },
  },
};
